import React, { useState, useEffect, useCallback } from "react";
import { Accordion, AccordionTab } from "primereact/accordion";
import StarIcon from "@mui/icons-material/Star";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { Tooltip, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import BrsrSectionCPopup from "../BrsrSectionCPopup";
import { saveSectionData, loadSectionData } from "../utils/answerManager";
import { fetchBrsrElectricityByFy } from "../utils/brsrElectricityCalculator";

const SectionC = ({ reportId }) => {
  const storageReportId = reportId || localStorage.getItem("brsr_report_id");
  const [essentialEnabled, setEssentialEnabled] = useState({}); // Track which principles have Essential enabled (default true)
  const [leadershipEnabled, setLeadershipEnabled] = useState({}); // Track which principles have Leadership enabled
  const [isBRSRCore, setIsBRSRCore] = useState(false); // Check if BRSR + Core is selected
  const [financialYears, setFinancialYears] = useState({ currentFY: "", previousFY: "" });
  // State for Scope 2 auto-fetch
  const [scope2Data, setScope2Data] = useState(null);
  const [scope2Loading, setScope2Loading] = useState(false);
  const [showSectionCPopup, setShowSectionCPopup] = useState(() => {
    // Initialize based on localStorage to prevent flash
    const popupSetting = localStorage.getItem("brsrSectionCPopup");
    return popupSetting !== "hide";
  }); // Popup state for Section C

  // State for Principle 1 Leadership questions
  const [principle1Leadership, setPrinciple1Leadership] = useState({
    // Question 1: Awareness programmes for value chain partners
    q1_valueChainAwareness: [{ programmes: "", topics: "", percentage: "" }],
    // Question 2: Conflict of interest processes
    q2_conflictProcesses: { hasProcesses: "", details: "" },
  });

  // State for Principle 2 Essential questions
  const [principle2Essential, setPrinciple2Essential] = useState({
    // Question 1: R&D and Capex investments
    q1_rdCapex: {
      rd: { current: "", previous: "", details: "" },
      capex: { current: "", previous: "", details: "" },
    },
    // Question 2: Sustainable sourcing
    q2_sustainableSourcing: { hasProcedures: "", percentage: "" },
    // Question 3: Processes for reclaiming products
    q3_reclaimProcesses: {
      plastics: "",
      ewaste: "",
      hazardous: "",
      other: "",
    },
    // Question 4: EPR applicability
    q4_epr: "",
  });

  // State for Principle 2 Leadership questions
  const [principle2Leadership, setPrinciple2Leadership] = useState({
    // Question 1: Life Cycle Assessment
    q1_lca: [{ nicCode: "", name: "", turnoverPercent: "", boundary: "", independentAgency: "", resultsCommunicated: "", webLink: "" }],
    // Question 2: Social/environmental concerns
    q2_concerns: [{ name: "", description: "", actionTaken: "" }],
    // Question 3: Recycled/reused input material
    q3_recycledMaterial: [{ material: "", current: "", previous: "" }],
    // Question 4: Products and packaging reclaimed
    q4_reclaimed: {
      plastics: { currentReused: "", currentRecycled: "", currentDisposed: "", previousReused: "", previousRecycled: "", previousDisposed: "" },
      ewaste: { currentReused: "", currentRecycled: "", currentDisposed: "", previousReused: "", previousRecycled: "", previousDisposed: "" },
      hazardous: { currentReused: "", currentRecycled: "", currentDisposed: "", previousReused: "", previousRecycled: "", previousDisposed: "" },
      other: { currentReused: "", currentRecycled: "", currentDisposed: "", previousReused: "", previousRecycled: "", previousDisposed: "" },
    },
    // Question 5: Reclaimed products percentage
    q5_reclaimedPercentage: [{ category: "", percentage: "" }],
  });

  // State for Principle 3 Essential questions
  const [principle3Essential, setPrinciple3Essential] = useState({
    // Question 1a: Employee wellbeing
    q1a_employeeWellbeing: {
      permanent: {
        male: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
        female: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
      },
      otherThanPermanent: {
        male: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
        female: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
      },
    },
    // Question 1b: Worker wellbeing
    q1b_workerWellbeing: {
      permanent: {
        male: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
        female: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
      },
      otherThanPermanent: {
        male: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
        female: { total: "", healthInsurance: "", accidentInsurance: "", maternity: "", paternity: "", dayCare: "" },
      },
    },
    // Question 1c (BRSR Core): Spending on wellbeing
    q1c_wellbeingSpending: { current: "", previous: "" },
    // Question 2: Retirement benefits
    q2_retirementBenefits: {
      pf: { currentEmployees: "", currentWorkers: "", currentDeposited: "", previousEmployees: "", previousWorkers: "", previousDeposited: "" },
      gratuity: { currentEmployees: "", currentWorkers: "", currentDeposited: "", previousEmployees: "", previousWorkers: "", previousDeposited: "" },
      esi: { currentEmployees: "", currentWorkers: "", currentDeposited: "", previousEmployees: "", previousWorkers: "", previousDeposited: "" },
      others: { currentEmployees: "", currentWorkers: "", currentDeposited: "", previousEmployees: "", previousWorkers: "", previousDeposited: "", specify: "" },
    },
    // Question 3: Accessibility
    q3_accessibility: "",
    // Question 4: Equal opportunity policy
    q4_equalOpportunity: "",
    // Question 5: Return to work and retention rates
    q5_returnRetention: {
      employees: { maleReturn: "", maleRetention: "", femaleReturn: "", femaleRetention: "" },
      workers: { maleReturn: "", maleRetention: "", femaleReturn: "", femaleRetention: "" },
    },
    // Question 6: Grievance mechanism
    q6_grievance: {
      permanentWorkers: { hasMechanism: "", details: "" },
      otherThanPermanentWorkers: { hasMechanism: "", details: "" },
      permanentEmployees: { hasMechanism: "", details: "" },
      otherThanPermanentEmployees: { hasMechanism: "", details: "" },
    },
    // Question 7: Membership in associations/unions (aggregate row = "Total Permanent Employees/Workers" with values, not a merged empty row)
    q7_membership: {
      currentFY: {
        permanentEmployees: {
          aggregate: { total: "", members: "" },
          male: { total: "", members: "" },
          female: { total: "", members: "" },
          others: { total: "", members: "" },
        },
        permanentWorkers: {
          aggregate: { total: "", members: "" },
          male: { total: "", members: "" },
          female: { total: "", members: "" },
          others: { total: "", members: "" },
        },
      },
      previousFY: {
        permanentEmployees: {
          aggregate: { total: "", members: "" },
          male: { total: "", members: "" },
          female: { total: "", members: "" },
          others: { total: "", members: "" },
        },
        permanentWorkers: {
          aggregate: { total: "", members: "" },
          male: { total: "", members: "" },
          female: { total: "", members: "" },
          others: { total: "", members: "" },
        },
      },
    },
    // Question 8: Training details
    q8_training: {
      currentFY: {
        employees: { male: { total: "", healthSafety: "", skillUpgrade: "" }, female: { total: "", healthSafety: "", skillUpgrade: "" } },
        workers: { male: { total: "", healthSafety: "", skillUpgrade: "" }, female: { total: "", healthSafety: "", skillUpgrade: "" } },
      },
      previousFY: {
        employees: { male: { total: "", healthSafety: "", skillUpgrade: "" }, female: { total: "", healthSafety: "", skillUpgrade: "" } },
        workers: { male: { total: "", healthSafety: "", skillUpgrade: "" }, female: { total: "", healthSafety: "", skillUpgrade: "" } },
      },
    },
    // Question 9: Performance reviews
    q9_performanceReviews: {
      currentFY: {
        employees: { male: { total: "", reviewed: "" }, female: { total: "", reviewed: "" } },
        workers: { male: { total: "", reviewed: "" }, female: { total: "", reviewed: "" } },
      },
      previousFY: {
        employees: { male: { total: "", reviewed: "" }, female: { total: "", reviewed: "" } },
        workers: { male: { total: "", reviewed: "" }, female: { total: "", reviewed: "" } },
      },
    },
    // Question 10: Health and safety management
    q10_healthSafety: {
      a_implemented: { hasSystem: "", coverage: "" },
      b_processes: "",
      c_workerReporting: "",
      c_workerReportingDetails: "",
      d_medicalAccess: "",
      d_medicalAccessDetails: "",
    },
    // Question 11 (BRSR Core): Safety incidents
    q11_safetyIncidents: {
      ltifr: { employeesCurrent: "", employeesPrevious: "", workersCurrent: "", workersPrevious: "" },
      recordableInjuries: { employeesCurrent: "", employeesPrevious: "", workersCurrent: "", workersPrevious: "" },
      fatalities: { employeesCurrent: "", employeesPrevious: "", workersCurrent: "", workersPrevious: "" },
      highConsequence: { employeesCurrent: "", employeesPrevious: "", workersCurrent: "", workersPrevious: "" },
    },
    // Question 12: Safe workplace measures
    q12_safeWorkplace: "",
    // Question 13: Complaints
    q13_complaints: {
      workingConditions: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      healthSafety: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    },
    // Question 14: Assessments
    q14_assessments: {
      healthSafety: "",
      workingConditions: "",
    },
    // Question 15: Corrective action
    q15_correctiveAction: "",
  });

  // State for Principle 3 Leadership questions
  const [principle3Leadership, setPrinciple3Leadership] = useState({
    // Question 1: Life insurance
    q1_lifeInsurance: {
      employees: "",
      workers: "",
    },
    // Question 2: Statutory dues
    q2_statutoryDues: "",
    // Question 3: Rehabilitation
    q3_rehabilitation: {
      employees: { currentAffected: "", currentRehabilitated: "", previousAffected: "", previousRehabilitated: "" },
      workers: { currentAffected: "", currentRehabilitated: "", previousAffected: "", previousRehabilitated: "" },
    },
    // Question 4: Transition assistance
    q4_transitionAssistance: "",
    // Question 5: Value chain assessment
    q5_valueChainAssessment: {
      healthSafety: "",
      workingConditions: "",
    },
    // Question 6: Value chain corrective action
    q6_valueChainCorrective: "",
  });

  // State for Principle 4 Essential questions
  const [principle4Essential, setPrinciple4Essential] = useState({
    // Question 1: Processes for identifying key stakeholder groups
    q1_identifyingStakeholders: "",
    // Question 2: Stakeholder groups and engagement frequency
    q2_stakeholderGroups: [{ stakeholderGroup: "", vulnerableMarginalised: "", channels: "", frequency: "", purposeScope: "" }],
  });

  // State for Principle 4 Leadership questions
  const [principle4Leadership, setPrinciple4Leadership] = useState({
    // Question 1: Processes for consultation between stakeholders and Board
    q1_consultationProcesses: "",
    // Question 2: Stakeholder consultation for environmental and social topics
    q2_stakeholderConsultation: {
      isUsed: "",
      details: "",
    },
    // Question 3: Engagement with vulnerable/marginalized groups
    q3_vulnerableEngagement: "",
  });

  // State for Principle 5 Essential questions
  const [principle5Essential, setPrinciple5Essential] = useState({
    // Question 1: Training on human rights
    q1_humanRightsTraining: {
      currentFY: {
        employees: {
          permanent: { total: "", covered: "" },
          otherThanPermanent: { total: "", covered: "" },
        },
        workers: {
          permanent: { total: "", covered: "" },
          otherThanPermanent: { total: "", covered: "" },
        },
      },
      previousFY: {
        employees: {
          permanent: { total: "", covered: "" },
          otherThanPermanent: { total: "", covered: "" },
        },
        workers: {
          permanent: { total: "", covered: "" },
          otherThanPermanent: { total: "", covered: "" },
        },
      },
    },
    // Question 2: Minimum wages
    q2_minimumWages: {
      employees: {
        currentFY: { total: "", belowMin: "", atMin: "" },
        previousFY: { total: "", belowMin: "", atMin: "" },
      },
      workers: {
        currentFY: { total: "", belowMin: "", atMin: "" },
        previousFY: { total: "", belowMin: "", atMin: "" },
      },
    },
    // Question 3a: Median remuneration
    q3a_medianRemuneration: {
      bod: { maleNumber: "", maleMedian: "", femaleNumber: "", femaleMedian: "" },
      kmp: { maleNumber: "", maleMedian: "", femaleNumber: "", femaleMedian: "" },
      employees: { maleNumber: "", maleMedian: "", femaleNumber: "", femaleMedian: "" },
      workers: { maleNumber: "", maleMedian: "", femaleNumber: "", femaleMedian: "" },
    },
    // Question 3b (BRSR Core): Gross wages paid to females as %
    q3b_grossWagesFemales: { current: "", previous: "" },
    // Question 4: Focal point for human rights
    q4_focalPoint: "",
    q4_focalPointDetails: "",
    // Question 5: Internal mechanisms for grievances
    q5_internalMechanisms: "",
    // Question 6: Number of complaints
    q6_complaints: {
      sexualHarassment: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      discrimination: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      childLabour: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      forcedLabour: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      wages: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
      otherHumanRights: { currentFiled: "", currentPending: "", currentRemarks: "", previousFiled: "", previousPending: "", previousRemarks: "" },
    },
    // Question 7 (BRSR Core): POSH complaints
    q7_poshComplaints: {
      totalComplaints: { current: "", previous: "" },
      percentageOfFemales: { current: "", previous: "" },
      upheld: { current: "", previous: "" },
    },
    // Question 8: Mechanisms to prevent adverse consequences
    q8_preventAdverse: "",
    // Question 9: Human rights in business agreements
    q9_businessAgreements: "",
    q9_businessAgreementsDetails: "",
    // Question 10: Assessments for the year
    q10_assessments: {
      childLabour: "",
      forcedLabour: "",
      sexualHarassment: "",
      discrimination: "",
      wages: "",
      others: "",
    },
    // Question 11: Corrective actions
    q11_correctiveActions: "",
  });

  // State for Principle 5 Leadership questions
  const [principle5Leadership, setPrinciple5Leadership] = useState({
    // Question 1: Business process modifications
    q1_businessProcessModifications: "",
    // Question 2: Human rights due-diligence scope
    q2_dueDiligenceScope: "",
    // Question 3: Accessibility for differently abled visitors
    q3_accessibilityVisitors: "",
    // Question 4: Assessment of value chain partners
    q4_valueChainAssessment: {
      sexualHarassment: "",
      discrimination: "",
      childLabour: "",
      forcedLabour: "",
      wages: "",
      others: "",
    },
    // Question 5: Corrective actions for assessments
    q5_correctiveActions: "",
  });

  // State for Principle 6 Essential questions
  const [principle6Essential, setPrinciple6Essential] = useState({
    // Question 1 (BRSR Core): Energy consumption
    q1_energyConsumption: {
      renewable: {
        electricity: { current: "", previous: "" },
        fuel: { current: "", previous: "" },
        other: { current: "", previous: "" },
      },
      nonRenewable: {
        electricity: { current: "", previous: "" },
        fuel: { current: "", previous: "" },
        other: { current: "", previous: "" },
      },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        perRupeeTurnoverPPP: { current: "", previous: "" },
        physicalOutput: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    // Question 2: PAT Scheme
    q2_patScheme: "",
    // Question 3 (BRSR Core): Water details
    q3_waterDetails: {
      withdrawal: {
        surfaceWater: { current: "", previous: "" },
        groundwater: { current: "", previous: "" },
        thirdParty: { current: "", previous: "" },
        seawater: { current: "", previous: "" },
        others: { current: "", previous: "" },
      },
      consumption: { current: "", previous: "" },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        perRupeeTurnoverPPP: { current: "", previous: "" },
        physicalOutput: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    // Question 4 (BRSR Core): Water discharge
    q4_waterDischarge: {
      surfaceWater: {
        noTreatment: { current: "", previous: "" },
        withTreatment: { current: "", previous: "", treatmentLevel: "" },
      },
      groundwater: {
        noTreatment: { current: "", previous: "" },
        withTreatment: { current: "", previous: "", treatmentLevel: "" },
      },
      seawater: {
        noTreatment: { current: "", previous: "" },
        withTreatment: { current: "", previous: "", treatmentLevel: "" },
      },
      thirdParties: {
        noTreatment: { current: "", previous: "" },
        withTreatment: { current: "", previous: "", treatmentLevel: "" },
      },
      others: {
        noTreatment: { current: "", previous: "" },
        withTreatment: { current: "", previous: "", treatmentLevel: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    // Question 5: Zero Liquid Discharge
    q5_zeroLiquidDischarge: "",
    // Question 6: Air emissions
    q6_airEmissions: [
      { parameter: "NOx", unit: "", current: "", previous: "" },
      { parameter: "SOx", unit: "", current: "", previous: "" },
      { parameter: "Particulate matter (PM)", unit: "", current: "", previous: "" },
      { parameter: "Persistent organic pollutants (POP)", unit: "", current: "", previous: "" },
      { parameter: "Volatile organic compounds (VOC)", unit: "", current: "", previous: "" },
      { parameter: "Hazardous air pollutants (HAP)", unit: "", current: "", previous: "" },
      { parameter: "Others - please specify", unit: "", current: "", previous: "", specify: "" },
    ],
    q6_airEmissionsExternalAssessment: { hasAssessment: "", agencyName: "" },
    // Question 7 (BRSR Core): GHG emissions Scope 1 & 2
    q7_ghgEmissions: {
      scope1: { current: "", previous: "", unit: "" },
      scope2: { current: "", previous: "", unit: "" },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        perRupeeTurnoverPPP: { current: "", previous: "" },
        physicalOutput: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    // Question 8: GHG reduction projects
    q8_ghgReductionProjects: "",
    // Question 9 (BRSR Core): Waste management
    q9_wasteManagement: {
      totalWaste: {
        plastic: { current: "", previous: "" },
        ewaste: { current: "", previous: "" },
        biomedical: { current: "", previous: "" },
        construction: { current: "", previous: "" },
        battery: { current: "", previous: "" },
        radioactive: { current: "", previous: "" },
        otherHazardous: { current: "", previous: "", specify: "" },
        otherNonHazardous: { current: "", previous: "", specify: "" },
      },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        perRupeeTurnoverPPP: { current: "", previous: "" },
        physicalOutput: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      recovery: {
        recycled: { current: "", previous: "" },
        reused: { current: "", previous: "" },
        otherRecovery: { current: "", previous: "" },
      },
      disposal: {
        incineration: { current: "", previous: "" },
        landfilling: { current: "", previous: "" },
        otherDisposal: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    // Question 10: Waste management practices
    q10_wasteManagementPractices: "",
    // Question 11: Ecologically sensitive areas
    q11_ecologicallySensitive: [{ location: "", typeOfOperations: "", compliance: "" }],
    // Question 12: Environmental impact assessments
    q12_eia: [{ projectName: "", eiaNotificationNo: "", date: "", independentAgency: "", publicDomain: "", webLink: "" }],
    // Question 13: Environmental compliance
    q13_environmentalCompliance: [{ law: "", nonComplianceDetails: "", finesPenalties: "", correctiveAction: "" }],
  });

  // State for Principle 6 Leadership questions
  const [principle6Leadership, setPrinciple6Leadership] = useState({
    // Question 1: Water stress areas
    q1_waterStress: {
      areaName: "",
      natureOfOperations: "",
      withdrawal: {
        surfaceWater: { current: "", previous: "" },
        groundwater: { current: "", previous: "" },
        thirdParty: { current: "", previous: "" },
        seawater: { current: "", previous: "" },
        others: { current: "", previous: "" },
      },
      consumption: { current: "", previous: "" },
      discharge: {
        surfaceWater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
        groundwater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
        seawater: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
        thirdParties: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
        others: { noTreatment: { current: "", previous: "" }, withTreatment: { current: "", previous: "" } },
      },
    },
    // Question 2: Scope 3 emissions
    q2_scope3Emissions: {
      totalScope3: { current: "", previous: "", unit: "" },
      intensity: {
        perRupeeTurnover: { current: "", previous: "" },
        optional: { current: "", previous: "" },
      },
      externalAssessment: { hasAssessment: "", agencyName: "" },
    },
    // Question 3: Biodiversity impact
    q3_biodiversityImpact: "",
    // Question 4: Resource efficiency initiatives
    q4_resourceEfficiency: [{ initiative: "", details: "", outcome: "" }],
    // Question 5: Business continuity plan
    q5_businessContinuity: "",
    // Question 6: Value chain environmental impact
    q6_valueChainImpact: "",
    // Question 7: Value chain partner assessment
    q7_valueChainAssessment: "",
  });

  // State for Principle 7 Essential questions
  const [principle7Essential, setPrinciple7Essential] = useState({
    // Question 1a: Number of affiliations
    q1a_numberOfAffiliations: "",
    // Question 1b: Top 10 trade and industry chambers/associations
    q1b_tradeChambers: Array.from({ length: 10 }, (_, i) => ({
      name: "",
      reach: "",
    })),
    // Question 2: Corrective action on anti-competitive conduct
    q2_correctiveAction: [{ authority: "", brief: "", correctiveAction: "" }],
  });

  // State for Principle 7 Leadership questions
  const [principle7Leadership, setPrinciple7Leadership] = useState({
    // Public policy positions advocated
    q1_publicPolicy: [{ policy: "", method: "", publicDomain: "", frequency: "", frequencyOther: "", webLink: "" }],
  });

  // State for Principle 8 Essential questions
  const [principle8Essential, setPrinciple8Essential] = useState({
    // Question 1: Social Impact Assessments
    q1_sia: [{ projectName: "", notificationNo: "", date: "", independentAgency: "", publicDomain: "", webLink: "" }],
    // Question 2: Rehabilitation and Resettlement projects
    q2_rrProjects: [{ projectName: "", state: "", district: "", pafNumber: "", pafPercentage: "", amountPaid: "" }],
    // Question 3: Mechanisms to receive and redress grievances
    q3_grievanceMechanisms: "",
    // Question 4 (BRSR Core): Input material sourced from suppliers
    q4_inputMaterial: {
      msme: { current: "", previous: "" },
      withinIndia: { current: "", previous: "" },
    },
    // Question 5 (BRSR Core): Job creation in smaller towns
    q5_jobCreation: {
      rural: { current: "", previous: "" },
      semiUrban: { current: "", previous: "" },
      urban: { current: "", previous: "" },
      metropolitan: { current: "", previous: "" },
    },
  });

  // State for Principle 8 Leadership questions
  const [principle8Leadership, setPrinciple8Leadership] = useState({
    // Question 1: Actions to mitigate negative social impacts
    q1_mitigateImpacts: [{ impact: "", correctiveAction: "" }],
    // Question 2: CSR projects in aspirational districts
    q2_csrAspirations: [{ state: "", district: "", amountSpent: "" }],
    // Question 3a: Preferential procurement policy
    q3a_preferentialPolicy: "",
    // Question 3b: Marginalized/vulnerable groups
    q3b_marginalized: "",
    // Question 3c: Percentage of procurement
    q3c_procurementPercentage: "",
    // Question 4: Benefits from IP based on traditional knowledge
    q4_ipBenefits: [{ ipName: "", owned: "", benefitShared: "", basisCalculation: "" }],
    // Question 5: Corrective actions on IP disputes
    q5_ipDisputes: [{ authority: "", caseDetails: "", correctiveAction: "" }],
    // Question 6: CSR beneficiaries
    q6_csrBeneficiaries: [{ csrProject: "", personsCount: "", vulnerablePercentage: "" }],
  });

  // State for Principle 9 Essential questions
  const [principle9Essential, setPrinciple9Essential] = useState({
    // Question 1: Mechanisms for consumer complaints
    q1_consumerComplaints: "",
    // Question 2: Turnover with product information
    q2_productInfoTurnover: {
      dataPrivacy: "",
      advertising: "",
      recyclingSafeDisposal: "",
      safeResponsibleUsage: "",
      environmentalSocialParameters: "",
    },
    // Question 3: Number of consumer complaints
    q3_complaintNumbers: {
      dataPrivacy: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      advertising: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      cyberSecurity: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      deliveryServices: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      restrictiveTraces: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      unfairTrade: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
      other: { current: { received: "", pending: "", remarks: "" }, previous: { received: "", pending: "", remarks: "" } },
    },
    // Question 4: Product recalls
    q4_productRecalls: [
      { type: "Voluntary recalls", number: "", reasons: "" },
      { type: "Forced recalls", number: "", reasons: "" },
    ],
    // Question 5: Cyber security framework
    q5_cyberSecurityFramework: "",
    // Question 6: Corrective actions
    q6_correctiveActions: "",
    // Question 7a: Data breaches instances
    q7a_dataBreachesInstances: "",
    // Question 7b (BRSR Core): Data breaches with PII
    q7b_dataBreachesPercentage: "",
    // Question 7c: Impact of data breaches
    q7c_dataBreachesImpact: "",
  });

  // State for Principle 9 Leadership questions
  const [principle9Leadership, setPrinciple9Leadership] = useState({
    // Question 1: Channels/platforms for product information
    q1_informationChannels: "",
    // Question 2: Consumer education steps
    q2_consumerEducation: "",
    // Question 3: Risk communication mechanisms
    q3_riskCommunication: "",
    // Question 4: Product information and consumer surveys
    q4_productInfoSurveys: "",
  });

  // State for Principle 1 Essential questions
  const [principle1Essential, setPrinciple1Essential] = useState({
    // Question 1: Training and awareness programmes
    q1_training: [
      { segment: "Board of Directors", programmes: "", topics: "", percentage: "" },
      { segment: "Key Managerial Personnel", programmes: "", topics: "", percentage: "" },
      { segment: "Employees other than BoD and KMPs", programmes: "", topics: "", percentage: "" },
      { segment: "Workers", programmes: "", topics: "", percentage: "" },
    ],
    // Question 2: Fines/penalties
    q2_fines: {
      monetary: [
        { type: "Penalty/ Fine", principle: "", agency: "", amount: "", brief: "", appeal: "" },
        { type: "Settlement", principle: "", agency: "", amount: "", brief: "", appeal: "" },
        { type: "Compounding fee", principle: "", agency: "", amount: "", brief: "", appeal: "" },
      ],
      nonMonetary: [
        { type: "Imprisonment", principle: "", agency: "", brief: "", appeal: "" },
        { type: "Punishment", principle: "", agency: "", brief: "", appeal: "" },
      ],
    },
    // Question 3: Appeal/Revision details
    q3_appeals: [{ caseDetails: "", agency: "" }],
    // Question 4: Anti-corruption policy
    q4_antiCorruption: "",
    // Question 5: Disciplinary action
    q5_disciplinary: {
      directors: { current: "", previous: "" },
      kmps: { current: "", previous: "" },
      employees: { current: "", previous: "" },
      workers: { current: "", previous: "" },
    },
    // Question 6: Conflict of interest complaints
    q6_conflict: {
      directors: { currentNumber: "", currentRemarks: "", previousNumber: "", previousRemarks: "" },
      kmps: { currentNumber: "", currentRemarks: "", previousNumber: "", previousRemarks: "" },
    },
    // Question 7: Corrective action
    q7_corrective: "",
    // Question 8 (BRSR Core): Accounts payables
    q8_accountsPayable: { current: "", previous: "" },
    // Question 9 (BRSR Core): Open-ness of business
    q9_openness: {
      concentrationPurchases: {
        a: { current: "", previous: "" }, // Purchases from trading houses as % of total purchases
        b: { current: "", previous: "" }, // Number of trading houses
        c: { current: "", previous: "" }, // Purchases from top 10 trading houses as %
      },
      concentrationSales: {
        a: { current: "", previous: "" }, // Sales to dealers/distributors as % of total sales
        b: { current: "", previous: "" }, // Number of dealers/distributors
        c: { current: "", previous: "" }, // Sales to top 10 dealers/distributors as %
      },
      shareRPTs: {
        a: { current: "", previous: "" }, // Purchases
        b: { current: "", previous: "" }, // Sales
        c: { current: "", previous: "" }, // Loans & advances
        d: { current: "", previous: "" }, // Investments
      },
    },
  });

  // NGRBC Principles dropdown options
  const ngrbcPrinciples = [
    {
      label: "Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.",
      value: "Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.",
    },
    {
      label: "Businesses should provide goods and services in a manner that is sustainable and safe",
      value: "Businesses should provide goods and services in a manner that is sustainable and safe",
    },
    {
      label: "Businesses should respect and promote the well-being of all employees, including those in their value chains",
      value: "Businesses should respect and promote the well-being of all employees, including those in their value chains",
    },
    {
      label: "Businesses should respect the interests of and be responsive to all its stakeholders",
      value: "Businesses should respect the interests of and be responsive to all its stakeholders",
    },
    {
      label: "Businesses should respect and promote human rights",
      value: "Businesses should respect and promote human rights",
    },
    {
      label: "Businesses should respect and make efforts to protect and restore the environment",
      value: "Businesses should respect and make efforts to protect and restore the environment",
    },
    {
      label: "Businesses, when engaging in influencing public and regulatory policy, should do so in a manner that is responsible and transparent",
      value: "Businesses, when engaging in influencing public and regulatory policy, should do so in a manner that is responsible and transparent",
    },
    {
      label: "Businesses should promote inclusive growth and equitable development",
      value: "Businesses should promote inclusive growth and equitable development",
    },
    {
      label: "Businesses should engage with and provide value to their consumers in a responsible manner",
      value: "Businesses should engage with and provide value to their consumers in a responsible manner",
    },
  ];

  // Yes/No options
  const yesNoOptions = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ];

  // Deep-merge helper: recursively merges `source` into `target`.
  // Plain objects are merged key-by-key; arrays and primitives from `source` replace `target`.
  // This ensures AI-partially-filled state (which only sets some nested fields) never wipes
  // sibling fields that were already present in `target` (the component's initial/previous state).
  const deepMerge = (target, source) => {
    if (
      source === null || source === undefined ||
      typeof source !== 'object' || Array.isArray(source)
    ) {
      return source; // primitives, null, and arrays replace outright
    }
    const result = Object.assign({}, target);
    for (const key of Object.keys(source)) {
      const srcVal = source[key];
      const tgtVal = target ? target[key] : undefined;
      if (
        srcVal !== null && srcVal !== undefined &&
        typeof srcVal === 'object' && !Array.isArray(srcVal) &&
        tgtVal !== null && tgtVal !== undefined &&
        typeof tgtVal === 'object' && !Array.isArray(tgtVal)
      ) {
        result[key] = deepMerge(tgtVal, srcVal); // recurse into nested objects
      } else {
        result[key] = srcVal; // replace primitive / array / null
      }
    }
    return result;
  };

  // Load saved Section C data on mount
  useEffect(() => {
    const saved = loadSectionData(storageReportId, "C");
    if (saved) {
      if (saved.essentialEnabled) setEssentialEnabled(saved.essentialEnabled);
      if (saved.leadershipEnabled) setLeadershipEnabled(saved.leadershipEnabled);
      // Deep-merge saved data over initial state so that any AI-partially-filled principle
      // object never loses sibling fields that weren't touched by the AI (shallow spread
      // would wipe nested siblings not present in the saved slice).
      if (saved.principle1Essential) setPrinciple1Essential((prev) => deepMerge(prev, saved.principle1Essential));
      if (saved.principle1Leadership) setPrinciple1Leadership((prev) => deepMerge(prev, saved.principle1Leadership));
      if (saved.principle2Essential) setPrinciple2Essential((prev) => deepMerge(prev, saved.principle2Essential));
      if (saved.principle2Leadership) setPrinciple2Leadership((prev) => deepMerge(prev, saved.principle2Leadership));
      if (saved.principle3Essential) setPrinciple3Essential((prev) => deepMerge(prev, saved.principle3Essential));
      if (saved.principle3Leadership) setPrinciple3Leadership((prev) => deepMerge(prev, saved.principle3Leadership));
      if (saved.principle4Essential) setPrinciple4Essential((prev) => deepMerge(prev, saved.principle4Essential));
      if (saved.principle4Leadership) setPrinciple4Leadership((prev) => deepMerge(prev, saved.principle4Leadership));
      if (saved.principle5Essential) setPrinciple5Essential((prev) => deepMerge(prev, saved.principle5Essential));
      if (saved.principle5Leadership) setPrinciple5Leadership((prev) => deepMerge(prev, saved.principle5Leadership));
      if (saved.principle6Essential) setPrinciple6Essential((prev) => deepMerge(prev, saved.principle6Essential));
      if (saved.principle6Leadership) setPrinciple6Leadership((prev) => deepMerge(prev, saved.principle6Leadership));
      if (saved.principle7Essential) setPrinciple7Essential((prev) => deepMerge(prev, saved.principle7Essential));
      if (saved.principle7Leadership) setPrinciple7Leadership((prev) => deepMerge(prev, saved.principle7Leadership));
      if (saved.principle8Essential) setPrinciple8Essential((prev) => deepMerge(prev, saved.principle8Essential));
      if (saved.principle8Leadership) setPrinciple8Leadership((prev) => deepMerge(prev, saved.principle8Leadership));
      if (saved.principle9Essential) setPrinciple9Essential((prev) => deepMerge(prev, saved.principle9Essential));
      if (saved.principle9Leadership) setPrinciple9Leadership((prev) => deepMerge(prev, saved.principle9Leadership));
      if (saved.financialYears) setFinancialYears(saved.financialYears);
    }
  }, [storageReportId]);

  // Auto-save Section C data with useCallback
  const saveAllSectionCData = useCallback(() => {
    const dataToSave = {
      essentialEnabled,
      leadershipEnabled,
      financialYears,
      principle1Essential,
      principle1Leadership,
      principle2Essential,
      principle2Leadership,
      principle3Essential,
      principle3Leadership,
      principle4Essential,
      principle4Leadership,
      principle5Essential,
      principle5Leadership,
      principle6Essential,
      principle6Leadership,
      principle7Essential,
      principle7Leadership,
      principle8Essential,
      principle8Leadership,
      principle9Essential,
      principle9Leadership,
    };
    saveSectionData(storageReportId, "C", dataToSave);
  }, [storageReportId, essentialEnabled, leadershipEnabled, financialYears, principle1Essential, principle1Leadership, principle2Essential, principle2Leadership, principle3Essential, principle3Leadership, principle4Essential, principle4Leadership, principle5Essential, principle5Leadership, principle6Essential, principle6Leadership, principle7Essential, principle7Leadership, principle8Essential, principle8Leadership, principle9Essential, principle9Leadership]);

  // Debounced auto-save effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveAllSectionCData();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [saveAllSectionCData]);

  // Fetch Financial Year and BRSR Core status from step1
  useEffect(() => {
    const step1 = localStorage.getItem("step1");
    if (step1) {
      try {
        const dataStep1 = JSON.parse(step1);
        if (dataStep1.financialYear) {
          const currentFY = dataStep1.financialYear;
          // Calculate previous FY (format: "YYYY-YY")
          const parts = currentFY.split("-");
          if (parts.length === 2) {
            const startYear = parseInt(parts[0]);
            const prevStartYear = startYear - 1;
            const prevEndYear = startYear;
            const previousFY = `${prevStartYear}-${String(prevEndYear).slice(-2)}`;
            setFinancialYears({ currentFY: currentFY, previousFY: previousFY });
          }
        }
        if (dataStep1.reportingOn === "BRSR + Core") {
          setIsBRSRCore(true);
        }
      } catch (error) {
        console.error("Error parsing step1:", error);
      }
    }
  }, []);

  // Fetch purchased-electricity report history for BRSR Principle 6 auto-population
  const fetchScope2DataForBRSR = useCallback(async () => {
    const parseFinancialYear = (fyValue) => {
      if (!fyValue || typeof fyValue !== "string") return null;
      const parts = fyValue.trim().split("-");
      if (parts.length !== 2) return null;
      const startYear = parseInt(parts[0], 10);
      if (Number.isNaN(startYear)) return null;
      return { startYear, endYear: startYear + 1 };
    };

    try {
      setScope2Loading(true);
      const username = localStorage.getItem("userName");
      const step1Raw = localStorage.getItem("step1");

      if (!step1Raw || !username) {
        console.warn("No step1 data or username found for electricity auto-fetch");
        setScope2Loading(false);
        return;
      }

      const step1Data = JSON.parse(step1Raw);
      const legalEntityRaw = step1Data.legalEntity;
      const financialYear = step1Data.financialYear;
      const parsedFY = parseFinancialYear(financialYear);

      const legalEntity = Array.isArray(legalEntityRaw)
        ? legalEntityRaw.filter((entity) => !!entity)
        : typeof legalEntityRaw === "string"
          ? legalEntityRaw
              .split(",")
              .map((entity) => entity.trim())
              .filter((entity) => !!entity)
          : [];

      if (!legalEntity.length || !parsedFY) {
        console.warn("Legal entity or financial year not configured");
        setScope2Loading(false);
        return;
      }

      const { current: currentFyData, previous: previousFyData } = await fetchBrsrElectricityByFy({
        username,
        legalEntities: legalEntity,
        fyStartYear: parsedFY.startYear,
      });

      setScope2Data({ source: "report-history" });

      // Convert MWh to GJ (1 MWh = 3.6 GJ)
      const MWH_TO_GJ = 3.6;
      const currentRenewableGJ = (currentFyData.renewableMwh * MWH_TO_GJ).toFixed(2);
      const currentNonRenewableGJ = (currentFyData.nonRenewableMwh * MWH_TO_GJ).toFixed(2);
      const previousRenewableGJ = (previousFyData.renewableMwh * MWH_TO_GJ).toFixed(2);
      const previousNonRenewableGJ = (previousFyData.nonRenewableMwh * MWH_TO_GJ).toFixed(2);

      setPrinciple6Essential((prev) => ({
        ...prev,
        q1_energyConsumption: {
          ...prev.q1_energyConsumption,
          renewable: {
            ...prev.q1_energyConsumption.renewable,
            electricity: {
              current: currentRenewableGJ,
              previous: previousRenewableGJ,
            },
          },
          nonRenewable: {
            ...prev.q1_energyConsumption.nonRenewable,
            electricity: {
              current: currentNonRenewableGJ,
              previous: previousNonRenewableGJ,
            },
          },
        },
      }));
    } catch (error) {
      console.error("Error fetching electricity data for BRSR:", error);
    } finally {
      setScope2Loading(false);
    }
  }, []);

  // Trigger Scope 2 fetch when BRSR Core is enabled
  useEffect(() => {
    if (isBRSRCore) {
      fetchScope2DataForBRSR();
    }
  }, [isBRSRCore, fetchScope2DataForBRSR]);

  // Note: Section C popup is now initialized in useState using a function initializer
  // This prevents the popup from flashing and then closing immediately

  // Helper functions for validation
  const validateNumbersOnly = (value) => {
    if (value === "") return true;
    return /^\d*\.?\d*$/.test(value);
  };

  const validateIntegersOnly = (value) => {
    if (value === "") return true;
    return /^\d+$/.test(value);
  };

  const validatePercentage = (value) => {
    if (value === "") return true;
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    if (num < 0 || num > 100) return false;
    const decimalParts = value.split(".");
    if (decimalParts.length > 1 && decimalParts[1].length > 2) return false;
    return true;
  };

  const validateNICCode = (value) => {
    if (value === "") return true;
    return value.length <= 5;
  };

  // Helper function to calculate percentage: (value / total) * 100
  const calculatePercentage = (value, total) => {
    if (!value || !total || parseFloat(total) === 0) return "";
    const percent = (parseFloat(value) / parseFloat(total)) * 100;
    return percent.toFixed(2);
  };

  // Helper function to calculate total from two values
  const calculateTotal = (value1, value2) => {
    const v1 = parseFloat(value1) || 0;
    const v2 = parseFloat(value2) || 0;
    return (v1 + v2).toString();
  };

  const principles = [
    {
      id: "principle1",
      label: "PRINCIPLE 1",
      description: "Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.",
    },
    {
      id: "principle2",
      label: "PRINCIPLE 2",
      description: "Businesses should provide goods and services in a manner that is sustainable and safe",
    },
    {
      id: "principle3",
      label: "PRINCIPLE 3",
      description: "Businesses should respect and promote the well-being of all employees, including those in their value chains",
    },
    {
      id: "principle4",
      label: "PRINCIPLE 4",
      description: "Businesses should respect the interests of and be responsive to all its stakeholders",
    },
    {
      id: "principle5",
      label: "PRINCIPLE 5",
      description: "Businesses should respect and promote human rights",
    },
    {
      id: "principle6",
      label: "PRINCIPLE 6",
      description: "Businesses should respect and make efforts to protect and restore the environment",
    },
    {
      id: "principle7",
      label: "PRINCIPLE 7",
      description: "Businesses, when engaging in influencing public and regulatory policy, should do so in a manner that is responsible and transparent",
    },
    {
      id: "principle8",
      label: "PRINCIPLE 8",
      description: "Businesses should promote inclusive growth and equitable development",
    },
    {
      id: "principle9",
      label: "PRINCIPLE 9",
      description: "Businesses should engage with and provide value to their consumers in a responsible manner",
    },
  ];

  // Initialize both Essential and Leadership as enabled by default for all principles
  useEffect(() => {
    const initialEssential = {};
    const initialLeadership = {};
    principles.forEach((principle) => {
      initialEssential[principle.id] = true;
      initialLeadership[principle.id] = true;
    });
    setEssentialEnabled(initialEssential);
    setLeadershipEnabled(initialLeadership);
  }, []);

  // Persist auto-calculated energy intensity (Total energy consumed / Revenue) into
  // state so the generated report shows it instead of "-". Mirrors the display-time
  // calc in renderPrinciple6Essential; only writes when a value is computable, never
  // overwrites a manual entry.
  useEffect(() => {
    const e = principle6Essential.q1_energyConsumption;
    const sum = (...vals) => vals.reduce((acc, v) => acc + (parseFloat(v) || 0), 0);

    // Round total to 2 decimals first, matching the display-time calc so the value
    // shown in the (disabled) input equals the value saved to the report.
    const totalCurrent = parseFloat(sum(e.renewable.electricity.current, e.renewable.fuel.current, e.renewable.other.current, e.nonRenewable.electricity.current, e.nonRenewable.fuel.current, e.nonRenewable.other.current).toFixed(2));
    const totalPrevious = parseFloat(sum(e.renewable.electricity.previous, e.renewable.fuel.previous, e.renewable.other.previous, e.nonRenewable.electricity.previous, e.nonRenewable.fuel.previous, e.nonRenewable.other.previous).toFixed(2));

    let step1 = {};
    try {
      step1 = JSON.parse(localStorage.getItem("step1") || "{}");
    } catch (err) {
      step1 = {};
    }
    const revenueCurrent = parseFloat(step1?.revenueCurrentFY);
    const revenuePrevious = parseFloat(step1?.revenuePreviousFY);

    const PPP_FACTORS = { 2022: 22.36, 2023: 22.89, 2024: 23.12, 2025: 23.5, 2026: 24.0 };
    const pppCurrent = PPP_FACTORS[financialYears.currentFY?.split("-")[0]] || 23.5;
    const pppPrevious = PPP_FACTORS[financialYears.previousFY?.split("-")[0]] || 23.5;

    const perRupeeCurrent = totalCurrent > 0 && revenueCurrent > 0 ? (totalCurrent / revenueCurrent).toFixed(6) : "";
    const perRupeePrevious = totalPrevious > 0 && revenuePrevious > 0 ? (totalPrevious / revenuePrevious).toFixed(6) : "";
    const pppTurnoverCurrent = totalCurrent > 0 && revenueCurrent > 0 && pppCurrent > 0 ? (totalCurrent / (revenueCurrent / pppCurrent)).toFixed(6) : "";
    const pppTurnoverPrevious = totalPrevious > 0 && revenuePrevious > 0 && pppPrevious > 0 ? (totalPrevious / (revenuePrevious / pppPrevious)).toFixed(6) : "";

    setPrinciple6Essential((prev) => {
      const it = prev.q1_energyConsumption.intensity;
      const nextPerRupee = { current: perRupeeCurrent || it.perRupeeTurnover.current, previous: perRupeePrevious || it.perRupeeTurnover.previous };
      const nextPPP = { current: pppTurnoverCurrent || it.perRupeeTurnoverPPP.current, previous: pppTurnoverPrevious || it.perRupeeTurnoverPPP.previous };
      if (nextPerRupee.current === it.perRupeeTurnover.current && nextPerRupee.previous === it.perRupeeTurnover.previous && nextPPP.current === it.perRupeeTurnoverPPP.current && nextPPP.previous === it.perRupeeTurnoverPPP.previous) {
        return prev;
      }
      return {
        ...prev,
        q1_energyConsumption: { ...prev.q1_energyConsumption, intensity: { ...it, perRupeeTurnover: nextPerRupee, perRupeeTurnoverPPP: nextPPP } },
      };
    });
  }, [principle6Essential.q1_energyConsumption.renewable, principle6Essential.q1_energyConsumption.nonRenewable, financialYears.currentFY, financialYears.previousFY]);

  const handleEssentialToggle = (principleId, event) => {
    const newValue = !essentialEnabled[principleId];
    setEssentialEnabled((prev) => ({
      ...prev,
      [principleId]: newValue,
    }));
  };

  const handleLeadershipToggle = (principleId, event) => {
    const newValue = !leadershipEnabled[principleId];
    setLeadershipEnabled((prev) => ({
      ...prev,
      [principleId]: newValue,
    }));
  };

  // Render Info Icon with Tooltip
  const renderInfoIcon = (infoText) => {
    if (!infoText) return null;
    return (
      <Tooltip title={<div style={{ whiteSpace: "pre-line", fontSize: "12px" }}>{infoText}</div>} arrow placement="right">
        <IconButton size="small" sx={{ padding: "4px", marginLeft: "8px" }}>
          <InfoIcon sx={{ fontSize: "18px", color: "#407775" }} />
        </IconButton>
      </Tooltip>
    );
  };

  // Helper function to render question header with icon on the right side
  const renderQuestionHeader = (questionNumber, questionText, isEssential = true, infoIconText = null) => {
    return (
      <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", marginBottom: "15px", gap: "8px" }}>
        <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 500, flex: 1 }}>
          {questionNumber}. {questionText}
        </h4>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          {infoIconText && renderInfoIcon(infoIconText)}
          {isEssential ? <img src="/assets/img/reports/mandatory.svg" alt="Essential" className="section-c-indicator-icon" /> : <img src="/assets/img/reports/leadership.svg" alt="Leadership" className="section-c-indicator-icon" />}
        </div>
      </div>
    );
  };

  // Render Leadership Section Divider
  const renderLeadershipDivider = () => {
    return (
      <div style={{ marginTop: "0", marginBottom: "30px", padding: " 15px 20px", backgroundColor: "#f0f7f7" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <img src="/assets/img/reports/leadership.svg" alt="Leadership" style={{ width: "24px", height: "24px" }} />
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#407775" }}>Leadership Questions</h3>
        </div>
      </div>
    );
  };

  // Render Principle 1 Essential Questions
  const renderPrinciple1Essential = () => {
    return (
      <div style={{ padding: "20px 0" }}>
        {/* Question 1: Training and awareness programmes */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Percentage coverage by training and awareness programmes on any of the Principles during the financial year:", true)}
          <div className="section-a-dynamic-table-container section-a-dynamic-table-container-responsive">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Segment</th>
                  <th className="section-a-dynamic-table-header">Total Number of training and awareness programmes held</th>
                  <th className="section-a-dynamic-table-header">Topics / principles covered under the training and its impact</th>
                  <th className="section-a-dynamic-table-header">%age of persons in respective category covered by the awareness programmes</th>
                </tr>
              </thead>
              <tbody>
                {principle1Essential.q1_training.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {row.segment}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.programmes}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            const updated = [...principle1Essential.q1_training];
                            updated[index].programmes = value;
                            setPrinciple1Essential((prev) => ({ ...prev, q1_training: updated }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.topics}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q1_training];
                          updated[index].topics = e.target.value;
                          setPrinciple1Essential((prev) => ({ ...prev, q1_training: updated }));
                        }}
                        className="section-a-input"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={row.percentage}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              const updated = [...principle1Essential.q1_training];
                              updated[index].percentage = value;
                              setPrinciple1Essential((prev) => ({ ...prev, q1_training: updated }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error will be shown via validation
                            }
                          }}
                          className={`section-a-input ${row.percentage && (parseFloat(row.percentage) < 0 || parseFloat(row.percentage) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {row.percentage && (parseFloat(row.percentage) < 0 || parseFloat(row.percentage) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {row.percentage && parseFloat(row.percentage) >= 0 && parseFloat(row.percentage) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 2: Fines/penalties */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Details of fines / penalties /punishment/ award/ compounding fees/ settlement amount paid in proceedings (by the entity or by directors / KMPs) with regulators/ law enforcement agencies/ judicial institutions, in the financial year:", true)}

          <div className="section-a-dynamic-table-container section-a-dynamic-table-container-responsive">
            <table className="section-a-dynamic-table">
              <tbody>
                {/* Monetary Section Header */}
                <tr>
                  <td colSpan="6" style={{ backgroundColor: "#f5f5f5", fontWeight: 600, padding: "12px 16px", fontSize: "15px" }}>
                    Monetary
                  </td>
                </tr>
                {/* Monetary Column Headers */}
                <tr>
                  <th className="section-a-dynamic-table-header">Type</th>
                  <th className="section-a-dynamic-table-header">NGRBC Principle</th>
                  <th className="section-a-dynamic-table-header">Name of the regulatory/enforcement agencies/judicial institutions</th>
                  <th className="section-a-dynamic-table-header">Amount (INR)</th>
                  <th className="section-a-dynamic-table-header">Brief of the Case</th>
                  <th className="section-a-dynamic-table-header">Has an appeal been preferred? (Yes/No)</th>
                </tr>
                {/* Monetary Rows */}
                {principle1Essential.q2_fines.monetary.map((row, index) => (
                  <tr key={`monetary-${index}`} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {row.type}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={row.principle}
                        options={ngrbcPrinciples}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q2_fines.monetary];
                          updated[index].principle = e.value || "";
                          setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, monetary: updated } }));
                        }}
                        placeholder="Select Principle"
                        className="section-a-dropdown"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.agency}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q2_fines.monetary];
                          updated[index].agency = e.target.value;
                          setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, monetary: updated } }));
                        }}
                        className="section-a-input"
                        rows={2}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            const updated = [...principle1Essential.q2_fines.monetary];
                            updated[index].amount = value;
                            setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, monetary: updated } }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter amount"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.brief}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q2_fines.monetary];
                          updated[index].brief = e.target.value;
                          setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, monetary: updated } }));
                        }}
                        className="section-a-input"
                        rows={2}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <RadioButton
                            inputId={`appeal-yes-monetary-${index}`}
                            value="Yes"
                            checked={row.appeal === "Yes"}
                            onChange={(e) => {
                              const updated = [...principle1Essential.q2_fines.monetary];
                              updated[index].appeal = "Yes";
                              setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, monetary: updated } }));
                            }}
                          />
                          <label htmlFor={`appeal-yes-monetary-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            Yes
                          </label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <RadioButton
                            inputId={`appeal-no-monetary-${index}`}
                            value="No"
                            checked={row.appeal === "No"}
                            onChange={(e) => {
                              const updated = [...principle1Essential.q2_fines.monetary];
                              updated[index].appeal = "No";
                              setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, monetary: updated } }));
                            }}
                          />
                          <label htmlFor={`appeal-no-monetary-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            No
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Non-Monetary Section Header */}
                <tr>
                  <td colSpan="6" style={{ backgroundColor: "#f5f5f5", fontWeight: 600, padding: "12px 16px", fontSize: "15px" }}>
                    Non-Monetary
                  </td>
                </tr>
                {/* Non-Monetary Column Headers */}
                <tr>
                  <th className="section-a-dynamic-table-header">Type</th>
                  <th className="section-a-dynamic-table-header">NGRBC Principle</th>
                  <th className="section-a-dynamic-table-header">Name of the regulatory/enforcement agencies/judicial institutions</th>
                  <th className="section-a-dynamic-table-header">Amount (INR)</th>
                  <th className="section-a-dynamic-table-header">Brief of the Case</th>
                  <th className="section-a-dynamic-table-header">Has an appeal been preferred? (Yes/No)</th>
                </tr>
                {/* Non-Monetary Rows */}
                {principle1Essential.q2_fines.nonMonetary.map((row, index) => (
                  <tr key={`nonmonetary-${index}`} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {row.type}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={row.principle}
                        options={ngrbcPrinciples}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q2_fines.nonMonetary];
                          updated[index].principle = e.value || "";
                          setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, nonMonetary: updated } }));
                        }}
                        placeholder="Select Principle"
                        className="section-a-dropdown"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.agency}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q2_fines.nonMonetary];
                          updated[index].agency = e.target.value;
                          setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, nonMonetary: updated } }));
                        }}
                        className="section-a-input"
                        rows={2}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">{/* Empty cell for Amount column in Non-Monetary section */}</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.brief}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q2_fines.nonMonetary];
                          updated[index].brief = e.target.value;
                          setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, nonMonetary: updated } }));
                        }}
                        className="section-a-input"
                        rows={2}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <RadioButton
                            inputId={`appeal-yes-nonmonetary-${index}`}
                            value="Yes"
                            checked={row.appeal === "Yes"}
                            onChange={(e) => {
                              const updated = [...principle1Essential.q2_fines.nonMonetary];
                              updated[index].appeal = "Yes";
                              setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, nonMonetary: updated } }));
                            }}
                          />
                          <label htmlFor={`appeal-yes-nonmonetary-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            Yes
                          </label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <RadioButton
                            inputId={`appeal-no-nonmonetary-${index}`}
                            value="No"
                            checked={row.appeal === "No"}
                            onChange={(e) => {
                              const updated = [...principle1Essential.q2_fines.nonMonetary];
                              updated[index].appeal = "No";
                              setPrinciple1Essential((prev) => ({ ...prev, q2_fines: { ...prev.q2_fines, nonMonetary: updated } }));
                            }}
                          />
                          <label htmlFor={`appeal-no-nonmonetary-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            No
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 3: Appeal/Revision details */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Of the instances disclosed in Question 2 above, details of the Appeal/ Revision preferred in cases where monetary or non-monetary action has been appealed.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Case Details</th>
                  <th className="section-a-dynamic-table-header">Name of the regulatory/enforcement agencies/judicial institutions</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle1Essential.q3_appeals.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.caseDetails}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q3_appeals];
                          updated[index].caseDetails = e.target.value;
                          setPrinciple1Essential((prev) => ({ ...prev, q3_appeals: updated }));
                        }}
                        className="section-a-input"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.agency}
                        onChange={(e) => {
                          const updated = [...principle1Essential.q3_appeals];
                          updated[index].agency = e.target.value;
                          setPrinciple1Essential((prev) => ({ ...prev, q3_appeals: updated }));
                        }}
                        className="section-a-input"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {principle1Essential.q3_appeals.length > 1 && (
                        <Tooltip title="Delete" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete Icon"
                            className="delete-icon actions-icon"
                            onClick={() => {
                              const updated = principle1Essential.q3_appeals.filter((_, i) => i !== index);
                              setPrinciple1Essential((prev) => ({ ...prev, q3_appeals: updated }));
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple1Essential((prev) => ({
                    ...prev,
                    q3_appeals: [...prev.q3_appeals, { caseDetails: "", agency: "" }],
                  }));
                }}
                style={{ cursor: "pointer" }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 4: Anti-corruption policy */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Does the entity have an anti-corruption or anti-bribery policy? If yes, provide details in brief and if available, provide a web-link to the policy.", true)}
          <InputTextarea
            value={principle1Essential.q4_antiCorruption}
            onChange={(e) => {
              setPrinciple1Essential((prev) => ({ ...prev, q4_antiCorruption: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details and web-link if available"
          />
        </div>

        {/* Question 5: Disciplinary action */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Number of Directors/KMPs/employees/workers against whom disciplinary action was taken by any law enforcement agency for the charges of bribery/ corruption:", true)}
          <div className="section-a-dynamic-table-container section-a-dynamic-table-container-responsive">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Directors
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q5_disciplinary.directors.current}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q5_disciplinary: { ...prev.q5_disciplinary, directors: { ...prev.q5_disciplinary.directors, current: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q5_disciplinary.directors.previous}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q5_disciplinary: { ...prev.q5_disciplinary, directors: { ...prev.q5_disciplinary.directors, previous: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    KMPs
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q5_disciplinary.kmps.current}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q5_disciplinary: { ...prev.q5_disciplinary, kmps: { ...prev.q5_disciplinary.kmps, current: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q5_disciplinary.kmps.previous}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q5_disciplinary: { ...prev.q5_disciplinary, kmps: { ...prev.q5_disciplinary.kmps, previous: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Employees
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q5_disciplinary.employees.current}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q5_disciplinary: { ...prev.q5_disciplinary, employees: { ...prev.q5_disciplinary.employees, current: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q5_disciplinary.employees.previous}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q5_disciplinary: { ...prev.q5_disciplinary, employees: { ...prev.q5_disciplinary.employees, previous: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Workers
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q5_disciplinary.workers.current}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q5_disciplinary: { ...prev.q5_disciplinary, workers: { ...prev.q5_disciplinary.workers, current: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q5_disciplinary.workers.previous}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q5_disciplinary: { ...prev.q5_disciplinary, workers: { ...prev.q5_disciplinary.workers, previous: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 6: Conflict of interest complaints */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("6", "Details of complaints with regard to conflict of interest:", true)}
          <div className="section-a-dynamic-table-container section-a-dynamic-table-container-responsive">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2"></th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    FY {financialYears.currentFY}
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    FY {financialYears.previousFY}
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Number</th>
                  <th className="section-a-dynamic-table-header">Remarks</th>
                  <th className="section-a-dynamic-table-header">Number</th>
                  <th className="section-a-dynamic-table-header">Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Number of complaints received in relation to issues of Conflict of Interest of the Directors
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q6_conflict.directors.currentNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q6_conflict: {
                              ...prev.q6_conflict,
                              directors: { ...prev.q6_conflict.directors, currentNumber: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputTextarea
                      value={principle1Essential.q6_conflict.directors.currentRemarks}
                      onChange={(e) => {
                        setPrinciple1Essential((prev) => ({
                          ...prev,
                          q6_conflict: {
                            ...prev.q6_conflict,
                            directors: { ...prev.q6_conflict.directors, currentRemarks: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      rows={2}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q6_conflict.directors.previousNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q6_conflict: {
                              ...prev.q6_conflict,
                              directors: { ...prev.q6_conflict.directors, previousNumber: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputTextarea
                      value={principle1Essential.q6_conflict.directors.previousRemarks}
                      onChange={(e) => {
                        setPrinciple1Essential((prev) => ({
                          ...prev,
                          q6_conflict: {
                            ...prev.q6_conflict,
                            directors: { ...prev.q6_conflict.directors, previousRemarks: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      rows={2}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Number of complaints received in relation to issues of Conflict of Interest of the KMPs
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q6_conflict.kmps.currentNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q6_conflict: {
                              ...prev.q6_conflict,
                              kmps: { ...prev.q6_conflict.kmps, currentNumber: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputTextarea
                      value={principle1Essential.q6_conflict.kmps.currentRemarks}
                      onChange={(e) => {
                        setPrinciple1Essential((prev) => ({
                          ...prev,
                          q6_conflict: {
                            ...prev.q6_conflict,
                            kmps: { ...prev.q6_conflict.kmps, currentRemarks: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      rows={2}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle1Essential.q6_conflict.kmps.previousNumber}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple1Essential((prev) => ({
                            ...prev,
                            q6_conflict: {
                              ...prev.q6_conflict,
                              kmps: { ...prev.q6_conflict.kmps, previousNumber: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputTextarea
                      value={principle1Essential.q6_conflict.kmps.previousRemarks}
                      onChange={(e) => {
                        setPrinciple1Essential((prev) => ({
                          ...prev,
                          q6_conflict: {
                            ...prev.q6_conflict,
                            kmps: { ...prev.q6_conflict.kmps, previousRemarks: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      rows={2}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 7: Corrective action */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("7", "Provide details of any corrective action taken or underway on issues related to fines / penalties / action taken by regulators/ law enforcement agencies/ judicial institutions, on cases of corruption and conflicts of interest.", true)}
          <InputTextarea
            value={principle1Essential.q7_corrective}
            onChange={(e) => {
              setPrinciple1Essential((prev) => ({ ...prev, q7_corrective: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 8 (BRSR Core): Accounts payables */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "8",
              "Number of days of accounts payables ((Accounts payable *365) / Cost of goods/services procured) in the following format:",
              true,
              "The BFSI sector shall include relevant items under 'Other Liabilities' Schedule as reported in their financial statements.\n\n" +
                "'Cost of Goods/Services Procured' shall be reported as per guidance for total purchases under Attribute 7 (Enabling Inclusive Development).\n\n" +
                "'Total purchases' shall include all types of procurement such as raw material, spares, services, capex procurement items etc. as stated in Annexure I - Format of BRSR Core.\n\n" +
                "The relevant items under Trade Payables as reported in the financial statement shall be included against Accounts Payable",
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header"></th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      Number of days of accounts payables
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle1Essential.q8_accountsPayable.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple1Essential((prev) => ({
                              ...prev,
                              q8_accountsPayable: { ...prev.q8_accountsPayable, current: value },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle1Essential.q8_accountsPayable.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple1Essential((prev) => ({
                              ...prev,
                              q8_accountsPayable: { ...prev.q8_accountsPayable, previous: value },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Question 9 (BRSR Core): Open-ness of business */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader("9", "Open-ness of business", true)}
            <p style={{ marginBottom: "15px", fontSize: "14px", color: "#666" }}>Provide details of concentration of purchases and sales with trading houses, dealers, and related parties along-with loans and advances & investments, with related parties, in the following format:</p>

            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">Parameter</th>
                    <th className="section-a-dynamic-table-header">Metrics</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Concentration of Purchases */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" rowSpan="3" style={{ fontWeight: 500, verticalAlign: "top", paddingTop: "15px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Concentration of Purchases
                        {renderInfoIcon(
                          "Concentration of purchases:\n" +
                            "'Purchases' for this parameter shall be reported as per the guidance under Attribute 7 (Enabling Inclusive Development).\n" +
                            "'Total purchases' shall include all types of procurement such as raw material, spares, services, capex procurement items etc. as stated in Annexure I - Format of BRSR Core.\n\n" +
                            "A 'trading house' is a specialized legal entity primarily engaged in the business of export, import, and/or domestic trade of goods and services, facilitating such import, export and/or domestic trade and providing related services to support these transactions.",
                        )}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (a) Purchases from trading houses as % of total purchases
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.concentrationPurchases.a.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  concentrationPurchases: {
                                    ...prev.q9_openness.concentrationPurchases,
                                    a: { ...prev.q9_openness.concentrationPurchases.a, current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.concentrationPurchases.a.current && (parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.current) < 0 || parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.concentrationPurchases.a.current && (parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.current) < 0 || parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.concentrationPurchases.a.current && parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.current) >= 0 && parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.concentrationPurchases.a.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  concentrationPurchases: {
                                    ...prev.q9_openness.concentrationPurchases,
                                    a: { ...prev.q9_openness.concentrationPurchases.a, previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.concentrationPurchases.a.previous && (parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.previous) < 0 || parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.concentrationPurchases.a.previous && (parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.previous) < 0 || parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.concentrationPurchases.a.previous && parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.previous) >= 0 && parseFloat(principle1Essential.q9_openness.concentrationPurchases.a.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (b) Number of trading houses where purchases are made from
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle1Essential.q9_openness.concentrationPurchases.b.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple1Essential((prev) => ({
                              ...prev,
                              q9_openness: {
                                ...prev.q9_openness,
                                concentrationPurchases: {
                                  ...prev.q9_openness.concentrationPurchases,
                                  b: { ...prev.q9_openness.concentrationPurchases.b, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle1Essential.q9_openness.concentrationPurchases.b.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple1Essential((prev) => ({
                              ...prev,
                              q9_openness: {
                                ...prev.q9_openness,
                                concentrationPurchases: {
                                  ...prev.q9_openness.concentrationPurchases,
                                  b: { ...prev.q9_openness.concentrationPurchases.b, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (c) Purchases from top 10 trading houses as % of total purchases from trading houses
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.concentrationPurchases.c.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  concentrationPurchases: {
                                    ...prev.q9_openness.concentrationPurchases,
                                    c: { ...prev.q9_openness.concentrationPurchases.c, current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.concentrationPurchases.c.current && (parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.current) < 0 || parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.concentrationPurchases.c.current && (parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.current) < 0 || parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.concentrationPurchases.c.current && parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.current) >= 0 && parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.concentrationPurchases.c.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  concentrationPurchases: {
                                    ...prev.q9_openness.concentrationPurchases,
                                    c: { ...prev.q9_openness.concentrationPurchases.c, previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.concentrationPurchases.c.previous && (parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.previous) < 0 || parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.concentrationPurchases.c.previous && (parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.previous) < 0 || parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.concentrationPurchases.c.previous && parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.previous) >= 0 && parseFloat(principle1Essential.q9_openness.concentrationPurchases.c.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>

                  {/* Concentration of Sales */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" rowSpan="3" style={{ fontWeight: 500, verticalAlign: "top", paddingTop: "15px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Concentration of Sales
                        {renderInfoIcon(
                          "Concentration of sales:\n" +
                            "Sales shall mean sale of good and services. In case of the BFSI sector, total sales shall mean total revenue. Accordingly, reporting under the subparameters shall be total revenue for the sub parameter as a % to the total revenue in case of the BFSI sector.\n\n" +
                            "'Dealer' or 'distributor' means any person who whether for commission, remuneration or otherwise transfer or facilitates such transfer of the right to use any goods or services for any purpose (whether or not for a specified period) for cash, deferred payment or other valuable consideration.",
                        )}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (a) Sales to dealers / distributors as % of total sales
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.concentrationSales.a.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  concentrationSales: {
                                    ...prev.q9_openness.concentrationSales,
                                    a: { ...prev.q9_openness.concentrationSales.a, current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.concentrationSales.a.current && (parseFloat(principle1Essential.q9_openness.concentrationSales.a.current) < 0 || parseFloat(principle1Essential.q9_openness.concentrationSales.a.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.concentrationSales.a.current && (parseFloat(principle1Essential.q9_openness.concentrationSales.a.current) < 0 || parseFloat(principle1Essential.q9_openness.concentrationSales.a.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.concentrationSales.a.current && parseFloat(principle1Essential.q9_openness.concentrationSales.a.current) >= 0 && parseFloat(principle1Essential.q9_openness.concentrationSales.a.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.concentrationSales.a.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  concentrationSales: {
                                    ...prev.q9_openness.concentrationSales,
                                    a: { ...prev.q9_openness.concentrationSales.a, previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.concentrationSales.a.previous && (parseFloat(principle1Essential.q9_openness.concentrationSales.a.previous) < 0 || parseFloat(principle1Essential.q9_openness.concentrationSales.a.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.concentrationSales.a.previous && (parseFloat(principle1Essential.q9_openness.concentrationSales.a.previous) < 0 || parseFloat(principle1Essential.q9_openness.concentrationSales.a.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.concentrationSales.a.previous && parseFloat(principle1Essential.q9_openness.concentrationSales.a.previous) >= 0 && parseFloat(principle1Essential.q9_openness.concentrationSales.a.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (b) Number of dealers / distributors to whom sales are made
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle1Essential.q9_openness.concentrationSales.b.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple1Essential((prev) => ({
                              ...prev,
                              q9_openness: {
                                ...prev.q9_openness,
                                concentrationSales: {
                                  ...prev.q9_openness.concentrationSales,
                                  b: { ...prev.q9_openness.concentrationSales.b, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle1Essential.q9_openness.concentrationSales.b.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple1Essential((prev) => ({
                              ...prev,
                              q9_openness: {
                                ...prev.q9_openness,
                                concentrationSales: {
                                  ...prev.q9_openness.concentrationSales,
                                  b: { ...prev.q9_openness.concentrationSales.b, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (c) Sales to top 10 dealers / distributors as % of total sales to dealers / distributors
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.concentrationSales.c.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  concentrationSales: {
                                    ...prev.q9_openness.concentrationSales,
                                    c: { ...prev.q9_openness.concentrationSales.c, current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.concentrationSales.c.current && (parseFloat(principle1Essential.q9_openness.concentrationSales.c.current) < 0 || parseFloat(principle1Essential.q9_openness.concentrationSales.c.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.concentrationSales.c.current && (parseFloat(principle1Essential.q9_openness.concentrationSales.c.current) < 0 || parseFloat(principle1Essential.q9_openness.concentrationSales.c.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.concentrationSales.c.current && parseFloat(principle1Essential.q9_openness.concentrationSales.c.current) >= 0 && parseFloat(principle1Essential.q9_openness.concentrationSales.c.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.concentrationSales.c.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  concentrationSales: {
                                    ...prev.q9_openness.concentrationSales,
                                    c: { ...prev.q9_openness.concentrationSales.c, previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.concentrationSales.c.previous && (parseFloat(principle1Essential.q9_openness.concentrationSales.c.previous) < 0 || parseFloat(principle1Essential.q9_openness.concentrationSales.c.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.concentrationSales.c.previous && (parseFloat(principle1Essential.q9_openness.concentrationSales.c.previous) < 0 || parseFloat(principle1Essential.q9_openness.concentrationSales.c.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.concentrationSales.c.previous && parseFloat(principle1Essential.q9_openness.concentrationSales.c.previous) >= 0 && parseFloat(principle1Essential.q9_openness.concentrationSales.c.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>

                  {/* Share of RPTs */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" rowSpan="4" style={{ fontWeight: 500, verticalAlign: "top", paddingTop: "15px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Share of related party transactions (RPTs) in:
                        {renderInfoIcon(
                          "Share of related party transactions (RPTs) in:\n" +
                            "(a) Purchases (Purchases with related parties / Total Purchases)\n" +
                            "(b) Sales (Sales to related parties / Total Sales)\n" +
                            "(c) Loans & advances (Loans & advances given to related parties / Total loans & advances)\n" +
                            "(d) Investments (Investments in related parties / Total Investments made)\n\n" +
                            "The BRSR Core reporting shall use the definition of 'related party' and 'related party transaction' as defined under Regulation 2 (1) (zb) and Regulation (1) (zc) of the LODR Regulations respectively.\n\n" +
                            "Sales and Purchases will have the same meaning as in this Attribute above.\n" +
                            "It is clarified that investments with related parties (BRSR Core) shall be clarified to mean Investments in related parties.\n" +
                            "Loans and Advances and Investments should be taken as per relevant schedules in the audited Balance sheet.",
                        )}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (a) Purchases (Purchases with related parties / Total Purchases)
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.shareRPTs.a.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  shareRPTs: {
                                    ...prev.q9_openness.shareRPTs,
                                    a: { ...prev.q9_openness.shareRPTs.a, current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.shareRPTs.a.current && (parseFloat(principle1Essential.q9_openness.shareRPTs.a.current) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.a.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.shareRPTs.a.current && (parseFloat(principle1Essential.q9_openness.shareRPTs.a.current) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.a.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.shareRPTs.a.current && parseFloat(principle1Essential.q9_openness.shareRPTs.a.current) >= 0 && parseFloat(principle1Essential.q9_openness.shareRPTs.a.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.shareRPTs.a.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  shareRPTs: {
                                    ...prev.q9_openness.shareRPTs,
                                    a: { ...prev.q9_openness.shareRPTs.a, previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.shareRPTs.a.previous && (parseFloat(principle1Essential.q9_openness.shareRPTs.a.previous) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.a.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.shareRPTs.a.previous && (parseFloat(principle1Essential.q9_openness.shareRPTs.a.previous) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.a.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.shareRPTs.a.previous && parseFloat(principle1Essential.q9_openness.shareRPTs.a.previous) >= 0 && parseFloat(principle1Essential.q9_openness.shareRPTs.a.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (b) Sales (Sales to related parties / Total Sales)
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.shareRPTs.b.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  shareRPTs: {
                                    ...prev.q9_openness.shareRPTs,
                                    b: { ...prev.q9_openness.shareRPTs.b, current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.shareRPTs.b.current && (parseFloat(principle1Essential.q9_openness.shareRPTs.b.current) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.b.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.shareRPTs.b.current && (parseFloat(principle1Essential.q9_openness.shareRPTs.b.current) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.b.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.shareRPTs.b.current && parseFloat(principle1Essential.q9_openness.shareRPTs.b.current) >= 0 && parseFloat(principle1Essential.q9_openness.shareRPTs.b.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.shareRPTs.b.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  shareRPTs: {
                                    ...prev.q9_openness.shareRPTs,
                                    b: { ...prev.q9_openness.shareRPTs.b, previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.shareRPTs.b.previous && (parseFloat(principle1Essential.q9_openness.shareRPTs.b.previous) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.b.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.shareRPTs.b.previous && (parseFloat(principle1Essential.q9_openness.shareRPTs.b.previous) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.b.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.shareRPTs.b.previous && parseFloat(principle1Essential.q9_openness.shareRPTs.b.previous) >= 0 && parseFloat(principle1Essential.q9_openness.shareRPTs.b.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (c) Loans & advances (Loans & advances given to related parties / Total loans & advances)
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.shareRPTs.c.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  shareRPTs: {
                                    ...prev.q9_openness.shareRPTs,
                                    c: { ...prev.q9_openness.shareRPTs.c, current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.shareRPTs.c.current && (parseFloat(principle1Essential.q9_openness.shareRPTs.c.current) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.c.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.shareRPTs.c.current && (parseFloat(principle1Essential.q9_openness.shareRPTs.c.current) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.c.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.shareRPTs.c.current && parseFloat(principle1Essential.q9_openness.shareRPTs.c.current) >= 0 && parseFloat(principle1Essential.q9_openness.shareRPTs.c.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.shareRPTs.c.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  shareRPTs: {
                                    ...prev.q9_openness.shareRPTs,
                                    c: { ...prev.q9_openness.shareRPTs.c, previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.shareRPTs.c.previous && (parseFloat(principle1Essential.q9_openness.shareRPTs.c.previous) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.c.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.shareRPTs.c.previous && (parseFloat(principle1Essential.q9_openness.shareRPTs.c.previous) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.c.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.shareRPTs.c.previous && parseFloat(principle1Essential.q9_openness.shareRPTs.c.previous) >= 0 && parseFloat(principle1Essential.q9_openness.shareRPTs.c.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      (d) Investments (Investments in related parties / Total Investments made)
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.shareRPTs.d.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  shareRPTs: {
                                    ...prev.q9_openness.shareRPTs,
                                    d: { ...prev.q9_openness.shareRPTs.d, current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.shareRPTs.d.current && (parseFloat(principle1Essential.q9_openness.shareRPTs.d.current) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.d.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.shareRPTs.d.current && (parseFloat(principle1Essential.q9_openness.shareRPTs.d.current) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.d.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.shareRPTs.d.current && parseFloat(principle1Essential.q9_openness.shareRPTs.d.current) >= 0 && parseFloat(principle1Essential.q9_openness.shareRPTs.d.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle1Essential.q9_openness.shareRPTs.d.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple1Essential((prev) => ({
                                ...prev,
                                q9_openness: {
                                  ...prev.q9_openness,
                                  shareRPTs: {
                                    ...prev.q9_openness.shareRPTs,
                                    d: { ...prev.q9_openness.shareRPTs.d, previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${principle1Essential.q9_openness.shareRPTs.d.previous && (parseFloat(principle1Essential.q9_openness.shareRPTs.d.previous) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.d.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle1Essential.q9_openness.shareRPTs.d.previous && (parseFloat(principle1Essential.q9_openness.shareRPTs.d.previous) < 0 || parseFloat(principle1Essential.q9_openness.shareRPTs.d.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle1Essential.q9_openness.shareRPTs.d.previous && parseFloat(principle1Essential.q9_openness.shareRPTs.d.previous) >= 0 && parseFloat(principle1Essential.q9_openness.shareRPTs.d.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Principle 1 Leadership Questions
  const renderPrinciple1Leadership = () => {
    return (
      <div style={{ padding: "20px 0" }}>
        {/* Question 1: Awareness programmes for value chain partners */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Awareness programmes conducted for value chain partners on any of the Principles during the financial year:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Total no of awareness programmes held</th>
                  <th className="section-a-dynamic-table-header">Topics/principles covered under the training</th>
                  <th className="section-a-dynamic-table-header">%age of value chain partners covered (by value of business done with such partners) under the awareness programmes</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle1Leadership.q1_valueChainAwareness.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.programmes}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            const updated = [...principle1Leadership.q1_valueChainAwareness];
                            updated[index].programmes = value;
                            setPrinciple1Leadership((prev) => ({ ...prev, q1_valueChainAwareness: updated }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.topics}
                        onChange={(e) => {
                          const updated = [...principle1Leadership.q1_valueChainAwareness];
                          updated[index].topics = e.target.value;
                          setPrinciple1Leadership((prev) => ({ ...prev, q1_valueChainAwareness: updated }));
                        }}
                        className="section-a-input"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={row.percentage}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              const updated = [...principle1Leadership.q1_valueChainAwareness];
                              updated[index].percentage = value;
                              setPrinciple1Leadership((prev) => ({ ...prev, q1_valueChainAwareness: updated }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error will be shown via validation
                            }
                          }}
                          className={`section-a-input ${row.percentage && (parseFloat(row.percentage) < 0 || parseFloat(row.percentage) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {row.percentage && (parseFloat(row.percentage) < 0 || parseFloat(row.percentage) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {row.percentage && parseFloat(row.percentage) >= 0 && parseFloat(row.percentage) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {principle1Leadership.q1_valueChainAwareness.length > 1 && (
                        <Tooltip title="Delete" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete Icon"
                            className="delete-icon actions-icon"
                            onClick={() => {
                              const updated = principle1Leadership.q1_valueChainAwareness.filter((_, i) => i !== index);
                              setPrinciple1Leadership((prev) => ({ ...prev, q1_valueChainAwareness: updated }));
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple1Leadership((prev) => ({
                    ...prev,
                    q1_valueChainAwareness: [...prev.q1_valueChainAwareness, { programmes: "", topics: "", percentage: "" }],
                  }));
                }}
                style={{ cursor: "pointer" }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 2: Conflict of interest processes */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Does the entity have processes in place to avoid/ manage conflict of interests involving members of the Board? (Yes/No) If Yes, provide details of the same.", false)}
          <div style={{ marginBottom: "15px" }}>
            <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "15px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="conflict-processes-yes"
                  value="Yes"
                  checked={principle1Leadership.q2_conflictProcesses.hasProcesses === "Yes"}
                  onChange={(e) => {
                    setPrinciple1Leadership((prev) => ({
                      ...prev,
                      q2_conflictProcesses: { ...prev.q2_conflictProcesses, hasProcesses: "Yes" },
                    }));
                  }}
                />
                <label htmlFor="conflict-processes-yes" style={{ cursor: "pointer", margin: 0 }}>
                  Yes
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="conflict-processes-no"
                  value="No"
                  checked={principle1Leadership.q2_conflictProcesses.hasProcesses === "No"}
                  onChange={(e) => {
                    setPrinciple1Leadership((prev) => ({
                      ...prev,
                      q2_conflictProcesses: { ...prev.q2_conflictProcesses, hasProcesses: "No", details: "" },
                    }));
                  }}
                />
                <label htmlFor="conflict-processes-no" style={{ cursor: "pointer", margin: 0 }}>
                  No
                </label>
              </div>
            </div>
          </div>
          {principle1Leadership.q2_conflictProcesses.hasProcesses === "Yes" && (
            <InputTextarea
              value={principle1Leadership.q2_conflictProcesses.details}
              onChange={(e) => {
                setPrinciple1Leadership((prev) => ({
                  ...prev,
                  q2_conflictProcesses: { ...prev.q2_conflictProcesses, details: e.target.value },
                }));
              }}
              className="section-a-input"
              rows={4}
              style={{ width: "100%", resize: "vertical" }}
              placeholder="Enter details"
            />
          )}
        </div>
      </div>
    );
  };

  // Render Principle 2 Essential Questions
  const renderPrinciple2Essential = () => {
    return (
      <div style={{ padding: "20px 0" }}>
        {/* Question 1: R&D and Capex investments */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Percentage of R&D and capital expenditure (capex) investments in specific technologies to improve the environmental and social impacts of product and processes to total R&D and capex investments made by the entity, respectively.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.currentFY || "____"}</th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.previousFY || "____"}</th>
                  <th className="section-a-dynamic-table-header">Details of improvements in environmental and social impacts</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    R&D
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div>
                      <InputText
                        value={principle2Essential.q1_rdCapex.rd.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            setPrinciple2Essential((prev) => ({
                              ...prev,
                              q1_rdCapex: { ...prev.q1_rdCapex, rd: { ...prev.q1_rdCapex.rd, current: value } },
                            }));
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                            // Error shown via validation
                          }
                        }}
                        className={`section-a-input ${principle2Essential.q1_rdCapex.rd.current && (parseFloat(principle2Essential.q1_rdCapex.rd.current) < 0 || parseFloat(principle2Essential.q1_rdCapex.rd.current) > 100) ? "section-a-input-error" : ""}`}
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle2Essential.q1_rdCapex.rd.current && (parseFloat(principle2Essential.q1_rdCapex.rd.current) < 0 || parseFloat(principle2Essential.q1_rdCapex.rd.current) > 100) && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Value needs to be between 0 and 100
                        </div>
                      )}
                      {principle2Essential.q1_rdCapex.rd.current && parseFloat(principle2Essential.q1_rdCapex.rd.current) >= 0 && parseFloat(principle2Essential.q1_rdCapex.rd.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div>
                      <InputText
                        value={principle2Essential.q1_rdCapex.rd.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            setPrinciple2Essential((prev) => ({
                              ...prev,
                              q1_rdCapex: { ...prev.q1_rdCapex, rd: { ...prev.q1_rdCapex.rd, previous: value } },
                            }));
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                            // Error shown via validation
                          }
                        }}
                        className={`section-a-input ${principle2Essential.q1_rdCapex.rd.previous && (parseFloat(principle2Essential.q1_rdCapex.rd.previous) < 0 || parseFloat(principle2Essential.q1_rdCapex.rd.previous) > 100) ? "section-a-input-error" : ""}`}
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle2Essential.q1_rdCapex.rd.previous && (parseFloat(principle2Essential.q1_rdCapex.rd.previous) < 0 || parseFloat(principle2Essential.q1_rdCapex.rd.previous) > 100) && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Value needs to be between 0 and 100
                        </div>
                      )}
                      {principle2Essential.q1_rdCapex.rd.previous && parseFloat(principle2Essential.q1_rdCapex.rd.previous) >= 0 && parseFloat(principle2Essential.q1_rdCapex.rd.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputTextarea
                      value={principle2Essential.q1_rdCapex.rd.details}
                      onChange={(e) => {
                        setPrinciple2Essential((prev) => ({
                          ...prev,
                          q1_rdCapex: { ...prev.q1_rdCapex, rd: { ...prev.q1_rdCapex.rd, details: e.target.value } },
                        }));
                      }}
                      className="section-a-input"
                      rows={3}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Capex
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div>
                      <InputText
                        value={principle2Essential.q1_rdCapex.capex.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            setPrinciple2Essential((prev) => ({
                              ...prev,
                              q1_rdCapex: { ...prev.q1_rdCapex, capex: { ...prev.q1_rdCapex.capex, current: value } },
                            }));
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                            // Error shown via validation
                          }
                        }}
                        className={`section-a-input ${principle2Essential.q1_rdCapex.capex.current && (parseFloat(principle2Essential.q1_rdCapex.capex.current) < 0 || parseFloat(principle2Essential.q1_rdCapex.capex.current) > 100) ? "section-a-input-error" : ""}`}
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle2Essential.q1_rdCapex.capex.current && (parseFloat(principle2Essential.q1_rdCapex.capex.current) < 0 || parseFloat(principle2Essential.q1_rdCapex.capex.current) > 100) && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Value needs to be between 0 and 100
                        </div>
                      )}
                      {principle2Essential.q1_rdCapex.capex.current && parseFloat(principle2Essential.q1_rdCapex.capex.current) >= 0 && parseFloat(principle2Essential.q1_rdCapex.capex.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div>
                      <InputText
                        value={principle2Essential.q1_rdCapex.capex.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            setPrinciple2Essential((prev) => ({
                              ...prev,
                              q1_rdCapex: { ...prev.q1_rdCapex, capex: { ...prev.q1_rdCapex.capex, previous: value } },
                            }));
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                            // Error shown via validation
                          }
                        }}
                        className={`section-a-input ${principle2Essential.q1_rdCapex.capex.previous && (parseFloat(principle2Essential.q1_rdCapex.capex.previous) < 0 || parseFloat(principle2Essential.q1_rdCapex.capex.previous) > 100) ? "section-a-input-error" : ""}`}
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle2Essential.q1_rdCapex.capex.previous && (parseFloat(principle2Essential.q1_rdCapex.capex.previous) < 0 || parseFloat(principle2Essential.q1_rdCapex.capex.previous) > 100) && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Value needs to be between 0 and 100
                        </div>
                      )}
                      {principle2Essential.q1_rdCapex.capex.previous && parseFloat(principle2Essential.q1_rdCapex.capex.previous) >= 0 && parseFloat(principle2Essential.q1_rdCapex.capex.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputTextarea
                      value={principle2Essential.q1_rdCapex.capex.details}
                      onChange={(e) => {
                        setPrinciple2Essential((prev) => ({
                          ...prev,
                          q1_rdCapex: { ...prev.q1_rdCapex, capex: { ...prev.q1_rdCapex.capex, details: e.target.value } },
                        }));
                      }}
                      className="section-a-input"
                      rows={3}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 2: Sustainable sourcing */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2a", "Does the entity have procedures in place for sustainable sourcing? (Yes/No)", true)}
          <div style={{ marginBottom: "15px" }}>
            <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="sustainable-sourcing-yes"
                  value="Yes"
                  checked={principle2Essential.q2_sustainableSourcing.hasProcedures === "Yes"}
                  onChange={(e) => {
                    setPrinciple2Essential((prev) => ({
                      ...prev,
                      q2_sustainableSourcing: { ...prev.q2_sustainableSourcing, hasProcedures: "Yes" },
                    }));
                  }}
                />
                <label htmlFor="sustainable-sourcing-yes" style={{ cursor: "pointer", margin: 0 }}>
                  Yes
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="sustainable-sourcing-no"
                  value="No"
                  checked={principle2Essential.q2_sustainableSourcing.hasProcedures === "No"}
                  onChange={(e) => {
                    setPrinciple2Essential((prev) => ({
                      ...prev,
                      q2_sustainableSourcing: { ...prev.q2_sustainableSourcing, hasProcedures: "No", percentage: "" },
                    }));
                  }}
                />
                <label htmlFor="sustainable-sourcing-no" style={{ cursor: "pointer", margin: 0 }}>
                  No
                </label>
              </div>
            </div>
          </div>
          {principle2Essential.q2_sustainableSourcing.hasProcedures === "Yes" && (
            <div>
              {renderQuestionHeader("2b", "If yes, what percentage of inputs were sourced sustainably?", true)}
              <div>
                <InputText
                  value={principle2Essential.q2_sustainableSourcing.percentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (validatePercentage(value)) {
                      setPrinciple2Essential((prev) => ({
                        ...prev,
                        q2_sustainableSourcing: { ...prev.q2_sustainableSourcing, percentage: value },
                      }));
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                      // Error shown via validation
                    }
                  }}
                  className={`section-a-input ${principle2Essential.q2_sustainableSourcing.percentage && (parseFloat(principle2Essential.q2_sustainableSourcing.percentage) < 0 || parseFloat(principle2Essential.q2_sustainableSourcing.percentage) > 100) ? "section-a-input-error" : ""}`}
                  placeholder="0-100"
                  type="text"
                  inputMode="decimal"
                  style={{ width: "100%", maxWidth: "300px" }}
                />
                {principle2Essential.q2_sustainableSourcing.percentage && (parseFloat(principle2Essential.q2_sustainableSourcing.percentage) < 0 || parseFloat(principle2Essential.q2_sustainableSourcing.percentage) > 100) && (
                  <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                    Value needs to be between 0 and 100
                  </div>
                )}
                {principle2Essential.q2_sustainableSourcing.percentage && parseFloat(principle2Essential.q2_sustainableSourcing.percentage) >= 0 && parseFloat(principle2Essential.q2_sustainableSourcing.percentage) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
              </div>
            </div>
          )}
        </div>

        {/* Question 3: Processes for reclaiming products */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for (a) Plastics (including packaging), (b) E-waste, (c) Hazardous waste and (d) other waste.", true)}
          <div style={{ marginBottom: "15px" }}>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: 500 }}>(a) Plastics (including packaging)</label>
              <InputTextarea
                value={principle2Essential.q3_reclaimProcesses.plastics}
                onChange={(e) => {
                  setPrinciple2Essential((prev) => ({
                    ...prev,
                    q3_reclaimProcesses: { ...prev.q3_reclaimProcesses, plastics: e.target.value },
                  }));
                }}
                className="section-a-input"
                rows={3}
                style={{ width: "100%", resize: "vertical" }}
                placeholder="Enter details"
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: 500 }}>(b) E-waste</label>
              <InputTextarea
                value={principle2Essential.q3_reclaimProcesses.ewaste}
                onChange={(e) => {
                  setPrinciple2Essential((prev) => ({
                    ...prev,
                    q3_reclaimProcesses: { ...prev.q3_reclaimProcesses, ewaste: e.target.value },
                  }));
                }}
                className="section-a-input"
                rows={3}
                style={{ width: "100%", resize: "vertical" }}
                placeholder="Enter details"
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: 500 }}>(c) Hazardous waste</label>
              <InputTextarea
                value={principle2Essential.q3_reclaimProcesses.hazardous}
                onChange={(e) => {
                  setPrinciple2Essential((prev) => ({
                    ...prev,
                    q3_reclaimProcesses: { ...prev.q3_reclaimProcesses, hazardous: e.target.value },
                  }));
                }}
                className="section-a-input"
                rows={3}
                style={{ width: "100%", resize: "vertical" }}
                placeholder="Enter details"
              />
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: 500 }}>(d) Other waste</label>
              <InputTextarea
                value={principle2Essential.q3_reclaimProcesses.other}
                onChange={(e) => {
                  setPrinciple2Essential((prev) => ({
                    ...prev,
                    q3_reclaimProcesses: { ...prev.q3_reclaimProcesses, other: e.target.value },
                  }));
                }}
                className="section-a-input"
                rows={3}
                style={{ width: "100%", resize: "vertical" }}
                placeholder="Enter details"
              />
            </div>
          </div>
        </div>

        {/* Question 4: EPR applicability */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Whether Extended Producer Responsibility (EPR) is applicable to the entity's activities (Yes / No). If yes, whether the waste collection plan is in line with the Extended Producer Responsibility (EPR) plan submitted to Pollution Control Boards? If not, provide steps taken to address the same.", true)}
          <InputTextarea
            value={principle2Essential.q4_epr}
            onChange={(e) => {
              setPrinciple2Essential((prev) => ({ ...prev, q4_epr: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>
      </div>
    );
  };

  // Render Principle 2 Leadership Questions
  const renderPrinciple2Leadership = () => {
    return (
      <div style={{ padding: "20px 0" }}>
        {/* Question 1: Life Cycle Assessment */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Has the entity conducted Life Cycle Perspective / Assessments (LCA) for any of its products (for manufacturing industry) or for its services (for service industry)? If yes, provide details in the following format?", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">NIC Code</th>
                  <th className="section-a-dynamic-table-header">Name of Product/Service</th>
                  <th className="section-a-dynamic-table-header">% of total turnover contributed</th>
                  <th className="section-a-dynamic-table-header">Boundary for which the Life Cycle Perspective / Assessment was conducted</th>
                  <th className="section-a-dynamic-table-header">Whether conducted by independent external agency (Yes/No)</th>
                  <th className="section-a-dynamic-table-header">Results communicated in public domain (Yes/No). If yes, provide the web-link.</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle2Leadership.q1_lca.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.nicCode}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNICCode(value) && validateIntegersOnly(value)) {
                            const updated = [...principle2Leadership.q1_lca];
                            updated[index].nicCode = value;
                            setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                          }
                        }}
                        className={`section-a-input ${row.nicCode && row.nicCode.length > 5 ? "section-a-input-error" : ""}`}
                        placeholder="Enter NIC Code"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        maxLength={5}
                      />
                      {row.nicCode && row.nicCode.length > 5 && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Maximum: 5 Characters
                        </div>
                      )}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.name}
                        onChange={(e) => {
                          const updated = [...principle2Leadership.q1_lca];
                          updated[index].name = e.target.value;
                          setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter name"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={row.turnoverPercent}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              const updated = [...principle2Leadership.q1_lca];
                              updated[index].turnoverPercent = value;
                              setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${row.turnoverPercent && (parseFloat(row.turnoverPercent) < 0 || parseFloat(row.turnoverPercent) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {row.turnoverPercent && (parseFloat(row.turnoverPercent) < 0 || parseFloat(row.turnoverPercent) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {row.turnoverPercent && parseFloat(row.turnoverPercent) >= 0 && parseFloat(row.turnoverPercent) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.boundary}
                        onChange={(e) => {
                          const updated = [...principle2Leadership.q1_lca];
                          updated[index].boundary = e.target.value;
                          setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <RadioButton
                            inputId={`lca-independent-yes-${index}`}
                            value="Yes"
                            checked={row.independentAgency === "Yes"}
                            onChange={(e) => {
                              const updated = [...principle2Leadership.q1_lca];
                              updated[index].independentAgency = "Yes";
                              setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                            }}
                          />
                          <label htmlFor={`lca-independent-yes-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            Yes
                          </label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <RadioButton
                            inputId={`lca-independent-no-${index}`}
                            value="No"
                            checked={row.independentAgency === "No"}
                            onChange={(e) => {
                              const updated = [...principle2Leadership.q1_lca];
                              updated[index].independentAgency = "No";
                              setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                            }}
                          />
                          <label htmlFor={`lca-independent-no-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            No
                          </label>
                        </div>
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ marginBottom: "10px" }}>
                        <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <RadioButton
                              inputId={`lca-communicated-yes-${index}`}
                              value="Yes"
                              checked={row.resultsCommunicated === "Yes"}
                              onChange={(e) => {
                                const updated = [...principle2Leadership.q1_lca];
                                updated[index].resultsCommunicated = "Yes";
                                setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                              }}
                            />
                            <label htmlFor={`lca-communicated-yes-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                              Yes
                            </label>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <RadioButton
                              inputId={`lca-communicated-no-${index}`}
                              value="No"
                              checked={row.resultsCommunicated === "No"}
                              onChange={(e) => {
                                const updated = [...principle2Leadership.q1_lca];
                                updated[index].resultsCommunicated = "No";
                                updated[index].webLink = "";
                                setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                              }}
                            />
                            <label htmlFor={`lca-communicated-no-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                              No
                            </label>
                          </div>
                        </div>
                        {row.resultsCommunicated === "Yes" && (
                          <InputText
                            value={row.webLink}
                            onChange={(e) => {
                              const updated = [...principle2Leadership.q1_lca];
                              updated[index].webLink = e.target.value;
                              setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                            }}
                            className="section-a-input"
                            placeholder="Enter web-link"
                            style={{ width: "100%" }}
                          />
                        )}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {principle2Leadership.q1_lca.length > 1 && (
                        <Tooltip title="Delete" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete Icon"
                            className="delete-icon actions-icon"
                            onClick={() => {
                              const updated = principle2Leadership.q1_lca.filter((_, i) => i !== index);
                              setPrinciple2Leadership((prev) => ({ ...prev, q1_lca: updated }));
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple2Leadership((prev) => ({
                    ...prev,
                    q1_lca: [...prev.q1_lca, { nicCode: "", name: "", turnoverPercent: "", boundary: "", independentAgency: "", resultsCommunicated: "", webLink: "" }],
                  }));
                }}
                style={{ cursor: "pointer" }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 2: Social/environmental concerns */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "If there are any significant social or environmental concerns and/or risks arising from production or disposal of your products / services, as identified in the Life Cycle Perspective / Assessments (LCA) or through any other means, briefly describe the same along-with action taken to mitigate the same.", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Name of Product / Service</th>
                  <th className="section-a-dynamic-table-header">Description of the risk/ concern</th>
                  <th className="section-a-dynamic-table-header">Action Taken</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle2Leadership.q2_concerns.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.name}
                        onChange={(e) => {
                          const updated = [...principle2Leadership.q2_concerns];
                          updated[index].name = e.target.value;
                          setPrinciple2Leadership((prev) => ({ ...prev, q2_concerns: updated }));
                        }}
                        className="section-a-input"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.description}
                        onChange={(e) => {
                          const updated = [...principle2Leadership.q2_concerns];
                          updated[index].description = e.target.value;
                          setPrinciple2Leadership((prev) => ({ ...prev, q2_concerns: updated }));
                        }}
                        className="section-a-input"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.actionTaken}
                        onChange={(e) => {
                          const updated = [...principle2Leadership.q2_concerns];
                          updated[index].actionTaken = e.target.value;
                          setPrinciple2Leadership((prev) => ({ ...prev, q2_concerns: updated }));
                        }}
                        className="section-a-input"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {principle2Leadership.q2_concerns.length > 1 && (
                        <Tooltip title="Delete" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete Icon"
                            className="delete-icon actions-icon"
                            onClick={() => {
                              const updated = principle2Leadership.q2_concerns.filter((_, i) => i !== index);
                              setPrinciple2Leadership((prev) => ({ ...prev, q2_concerns: updated }));
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple2Leadership((prev) => ({
                    ...prev,
                    q2_concerns: [...prev.q2_concerns, { name: "", description: "", actionTaken: "" }],
                  }));
                }}
                style={{ cursor: "pointer" }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 3: Recycled/reused input material */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Percentage of recycled or reused input material to total material (by value) used in production (for manufacturing industry) or providing services (for service industry).", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2">
                    Indicate input material
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    Recycled or re-used input material to total material
                  </th>
                  <th className="section-a-dynamic-table-header" rowSpan="2" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                </tr>
              </thead>
              <tbody>
                {principle2Leadership.q3_recycledMaterial.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.material}
                        onChange={(e) => {
                          const updated = [...principle2Leadership.q3_recycledMaterial];
                          updated[index].material = e.target.value;
                          setPrinciple2Leadership((prev) => ({ ...prev, q3_recycledMaterial: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={row.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              const updated = [...principle2Leadership.q3_recycledMaterial];
                              updated[index].current = value;
                              setPrinciple2Leadership((prev) => ({ ...prev, q3_recycledMaterial: updated }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${row.current && (parseFloat(row.current) < 0 || parseFloat(row.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {row.current && (parseFloat(row.current) < 0 || parseFloat(row.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {row.current && parseFloat(row.current) >= 0 && parseFloat(row.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={row.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              const updated = [...principle2Leadership.q3_recycledMaterial];
                              updated[index].previous = value;
                              setPrinciple2Leadership((prev) => ({ ...prev, q3_recycledMaterial: updated }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${row.previous && (parseFloat(row.previous) < 0 || parseFloat(row.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {row.previous && (parseFloat(row.previous) < 0 || parseFloat(row.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {row.previous && parseFloat(row.previous) >= 0 && parseFloat(row.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {principle2Leadership.q3_recycledMaterial.length > 1 && (
                        <Tooltip title="Delete" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete Icon"
                            className="delete-icon actions-icon"
                            onClick={() => {
                              const updated = principle2Leadership.q3_recycledMaterial.filter((_, i) => i !== index);
                              setPrinciple2Leadership((prev) => ({ ...prev, q3_recycledMaterial: updated }));
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple2Leadership((prev) => ({
                    ...prev,
                    q3_recycledMaterial: [...prev.q3_recycledMaterial, { material: "", current: "", previous: "" }],
                  }));
                }}
                style={{ cursor: "pointer" }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 4: Products and packaging reclaimed */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Of the products and packaging reclaimed at end of life of products, amount (in metric tonnes) reused, recycled, and safely disposed, as per the following format:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2"></th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.currentFY}
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.previousFY}
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Re-Used</th>
                  <th className="section-a-dynamic-table-header">Recycled</th>
                  <th className="section-a-dynamic-table-header">Safely Disposed</th>
                  <th className="section-a-dynamic-table-header">Re-Used</th>
                  <th className="section-a-dynamic-table-header">Recycled</th>
                  <th className="section-a-dynamic-table-header">Safely Disposed</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Plastics (including packaging)
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.plastics.currentReused}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, plastics: { ...prev.q4_reclaimed.plastics, currentReused: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.plastics.currentRecycled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, plastics: { ...prev.q4_reclaimed.plastics, currentRecycled: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.plastics.currentDisposed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, plastics: { ...prev.q4_reclaimed.plastics, currentDisposed: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.plastics.previousReused}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, plastics: { ...prev.q4_reclaimed.plastics, previousReused: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.plastics.previousRecycled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, plastics: { ...prev.q4_reclaimed.plastics, previousRecycled: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.plastics.previousDisposed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, plastics: { ...prev.q4_reclaimed.plastics, previousDisposed: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    E-waste
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.ewaste.currentReused}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, ewaste: { ...prev.q4_reclaimed.ewaste, currentReused: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.ewaste.currentRecycled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, ewaste: { ...prev.q4_reclaimed.ewaste, currentRecycled: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.ewaste.currentDisposed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, ewaste: { ...prev.q4_reclaimed.ewaste, currentDisposed: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.ewaste.previousReused}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, ewaste: { ...prev.q4_reclaimed.ewaste, previousReused: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.ewaste.previousRecycled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, ewaste: { ...prev.q4_reclaimed.ewaste, previousRecycled: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.ewaste.previousDisposed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, ewaste: { ...prev.q4_reclaimed.ewaste, previousDisposed: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Hazardous waste
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.hazardous.currentReused}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, hazardous: { ...prev.q4_reclaimed.hazardous, currentReused: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.hazardous.currentRecycled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, hazardous: { ...prev.q4_reclaimed.hazardous, currentRecycled: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.hazardous.currentDisposed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, hazardous: { ...prev.q4_reclaimed.hazardous, currentDisposed: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.hazardous.previousReused}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, hazardous: { ...prev.q4_reclaimed.hazardous, previousReused: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.hazardous.previousRecycled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, hazardous: { ...prev.q4_reclaimed.hazardous, previousRecycled: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.hazardous.previousDisposed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, hazardous: { ...prev.q4_reclaimed.hazardous, previousDisposed: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Other waste
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.other.currentReused}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, other: { ...prev.q4_reclaimed.other, currentReused: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.other.currentRecycled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, other: { ...prev.q4_reclaimed.other, currentRecycled: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.other.currentDisposed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, other: { ...prev.q4_reclaimed.other, currentDisposed: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.other.previousReused}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, other: { ...prev.q4_reclaimed.other, previousReused: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.other.previousRecycled}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, other: { ...prev.q4_reclaimed.other, previousRecycled: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle2Leadership.q4_reclaimed.other.previousDisposed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple2Leadership((prev) => ({
                            ...prev,
                            q4_reclaimed: { ...prev.q4_reclaimed, other: { ...prev.q4_reclaimed.other, previousDisposed: value } },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 5: Reclaimed products percentage */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Reclaimed products and their packaging materials (as percentage of products sold) for each product category.", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Indicate product category</th>
                  <th className="section-a-dynamic-table-header">Reclaimed products and their packaging materials as % of total products sold in respective category</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle2Leadership.q5_reclaimedPercentage.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.category}
                        onChange={(e) => {
                          const updated = [...principle2Leadership.q5_reclaimedPercentage];
                          updated[index].category = e.target.value;
                          setPrinciple2Leadership((prev) => ({ ...prev, q5_reclaimedPercentage: updated }));
                        }}
                        className="section-a-input"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={row.percentage}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              const updated = [...principle2Leadership.q5_reclaimedPercentage];
                              updated[index].percentage = value;
                              setPrinciple2Leadership((prev) => ({ ...prev, q5_reclaimedPercentage: updated }));
                            }
                          }}
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
                              // Error shown via validation
                            }
                          }}
                          className={`section-a-input ${row.percentage && (parseFloat(row.percentage) < 0 || parseFloat(row.percentage) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {row.percentage && (parseFloat(row.percentage) < 0 || parseFloat(row.percentage) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {row.percentage && parseFloat(row.percentage) >= 0 && parseFloat(row.percentage) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {principle2Leadership.q5_reclaimedPercentage.length > 1 && (
                        <Tooltip title="Delete" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete Icon"
                            className="delete-icon actions-icon"
                            onClick={() => {
                              const updated = principle2Leadership.q5_reclaimedPercentage.filter((_, i) => i !== index);
                              setPrinciple2Leadership((prev) => ({ ...prev, q5_reclaimedPercentage: updated }));
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple2Leadership((prev) => ({
                    ...prev,
                    q5_reclaimedPercentage: [...prev.q5_reclaimedPercentage, { category: "", percentage: "" }],
                  }));
                }}
                style={{ cursor: "pointer" }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Principle 3 Essential Questions
  const renderPrinciple3Essential = () => {
    // Helper to render wellbeing table row
    const renderWellbeingRow = (category, gender, genderLabel, data, updateFn, questionKey, categoryType) => {
      const total = data.total;
      const healthInsurance = data.healthInsurance;
      const accidentInsurance = data.accidentInsurance;
      const maternity = data.maternity;
      const paternity = data.paternity;
      const dayCare = data.dayCare;

      const healthPct = calculatePercentage(healthInsurance, total);
      const accidentPct = calculatePercentage(accidentInsurance, total);
      const maternityPct = calculatePercentage(maternity, total);
      const paternityPct = calculatePercentage(paternity, total);
      const dayCarePct = calculatePercentage(dayCare, total);

      return (
        <tr className="section-a-dynamic-table-row">
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
            {genderLabel}
          </td>
          <td className="section-a-dynamic-table-cell">
            <InputText
              value={total}
              onChange={(e) => {
                const value = e.target.value;
                if (validateIntegersOnly(value)) {
                  updateFn((prev) => ({
                    ...prev,
                    [questionKey]: {
                      ...prev[questionKey],
                      [categoryType]: {
                        ...prev[questionKey][categoryType],
                        [gender]: { ...prev[questionKey][categoryType][gender], total: value },
                      },
                    },
                  }));
                }
              }}
              className="section-a-input"
              placeholder="Enter number"
              type="text"
              inputMode="numeric"
              style={{ width: "100%" }}
            />
          </td>
          <td className="section-a-dynamic-table-cell">
            <InputText
              value={healthInsurance}
              onChange={(e) => {
                const value = e.target.value;
                if (validateIntegersOnly(value)) {
                  updateFn((prev) => ({
                    ...prev,
                    [questionKey]: {
                      ...prev[questionKey],
                      [categoryType]: {
                        ...prev[questionKey][categoryType],
                        [gender]: { ...prev[questionKey][categoryType][gender], healthInsurance: value },
                      },
                    },
                  }));
                }
              }}
              className="section-a-input"
              placeholder="Enter number"
              type="text"
              inputMode="numeric"
              style={{ width: "100%" }}
            />
          </td>
          <td className="section-a-dynamic-table-cell">{healthPct && <span>{healthPct}%</span>}</td>
          <td className="section-a-dynamic-table-cell">
            <InputText
              value={accidentInsurance}
              onChange={(e) => {
                const value = e.target.value;
                if (validateIntegersOnly(value)) {
                  updateFn((prev) => ({
                    ...prev,
                    [questionKey]: {
                      ...prev[questionKey],
                      [categoryType]: {
                        ...prev[questionKey][categoryType],
                        [gender]: { ...prev[questionKey][categoryType][gender], accidentInsurance: value },
                      },
                    },
                  }));
                }
              }}
              className="section-a-input"
              placeholder="Enter number"
              type="text"
              inputMode="numeric"
              style={{ width: "100%" }}
            />
          </td>
          <td className="section-a-dynamic-table-cell">{accidentPct && <span>{accidentPct}%</span>}</td>
          <td className="section-a-dynamic-table-cell">
            <InputText
              value={maternity}
              onChange={(e) => {
                const value = e.target.value;
                if (validateIntegersOnly(value)) {
                  updateFn((prev) => ({
                    ...prev,
                    [questionKey]: {
                      ...prev[questionKey],
                      [categoryType]: {
                        ...prev[questionKey][categoryType],
                        [gender]: { ...prev[questionKey][categoryType][gender], maternity: value },
                      },
                    },
                  }));
                }
              }}
              className="section-a-input"
              placeholder="Enter number"
              type="text"
              inputMode="numeric"
              style={{ width: "100%" }}
            />
          </td>
          <td className="section-a-dynamic-table-cell">{maternityPct && <span>{maternityPct}%</span>}</td>
          <td className="section-a-dynamic-table-cell">
            <InputText
              value={paternity}
              onChange={(e) => {
                const value = e.target.value;
                if (validateIntegersOnly(value)) {
                  updateFn((prev) => ({
                    ...prev,
                    [questionKey]: {
                      ...prev[questionKey],
                      [categoryType]: {
                        ...prev[questionKey][categoryType],
                        [gender]: { ...prev[questionKey][categoryType][gender], paternity: value },
                      },
                    },
                  }));
                }
              }}
              className="section-a-input"
              placeholder="Enter number"
              type="text"
              inputMode="numeric"
              style={{ width: "100%" }}
            />
          </td>
          <td className="section-a-dynamic-table-cell">{paternityPct && <span>{paternityPct}%</span>}</td>
          <td className="section-a-dynamic-table-cell">
            <InputText
              value={dayCare}
              onChange={(e) => {
                const value = e.target.value;
                if (validateIntegersOnly(value)) {
                  updateFn((prev) => ({
                    ...prev,
                    [questionKey]: {
                      ...prev[questionKey],
                      [categoryType]: {
                        ...prev[questionKey][categoryType],
                        [gender]: { ...prev[questionKey][categoryType][gender], dayCare: value },
                      },
                    },
                  }));
                }
              }}
              className="section-a-input"
              placeholder="Enter number"
              type="text"
              inputMode="numeric"
              style={{ width: "100%" }}
            />
          </td>
          <td className="section-a-dynamic-table-cell">{dayCarePct && <span>{dayCarePct}%</span>}</td>
        </tr>
      );
    };

    // Helper to render total row for wellbeing table
    const renderWellbeingTotalRow = (data, categoryKey) => {
      const maleData = data.male;
      const femaleData = data.female;

      const totalA = calculateTotal(maleData.total, femaleData.total);
      const totalHealth = calculateTotal(maleData.healthInsurance, femaleData.healthInsurance);
      const totalAccident = calculateTotal(maleData.accidentInsurance, femaleData.accidentInsurance);
      const totalMaternity = calculateTotal(maleData.maternity, femaleData.maternity);
      const totalPaternity = calculateTotal(maleData.paternity, femaleData.paternity);
      const totalDayCare = calculateTotal(maleData.dayCare, femaleData.dayCare);

      const healthPct = calculatePercentage(totalHealth, totalA);
      const accidentPct = calculatePercentage(totalAccident, totalA);
      const maternityPct = calculatePercentage(totalMaternity, totalA);
      const paternityPct = calculatePercentage(totalPaternity, totalA);
      const dayCarePct = calculatePercentage(totalDayCare, totalA);

      return (
        <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
          <td className="section-a-dynamic-table-cell">Total</td>
          <td className="section-a-dynamic-table-cell">{totalA || ""}</td>
          <td className="section-a-dynamic-table-cell">{totalHealth || ""}</td>
          <td className="section-a-dynamic-table-cell">{healthPct && `${healthPct}%`}</td>
          <td className="section-a-dynamic-table-cell">{totalAccident || ""}</td>
          <td className="section-a-dynamic-table-cell">{accidentPct && `${accidentPct}%`}</td>
          <td className="section-a-dynamic-table-cell">{totalMaternity || ""}</td>
          <td className="section-a-dynamic-table-cell">{maternityPct && `${maternityPct}%`}</td>
          <td className="section-a-dynamic-table-cell">{totalPaternity || ""}</td>
          <td className="section-a-dynamic-table-cell">{paternityPct && `${paternityPct}%`}</td>
          <td className="section-a-dynamic-table-cell">{totalDayCare || ""}</td>
          <td className="section-a-dynamic-table-cell">{dayCarePct && `${dayCarePct}%`}</td>
        </tr>
      );
    };

    /** BRSR P3 Essential 1a/1b: three-row header — benefit names span two columns; Number/% on row 3 */
    const renderP3WellbeingThead = (coveredLabel) => (
      <thead>
        <tr>
          <th className="section-a-dynamic-table-header" rowSpan={3}>
            Category
          </th>
          <th className="section-a-dynamic-table-header" rowSpan={3}>
            Total (A)
          </th>
          <th className="section-a-dynamic-table-header" colSpan={10}>
            {coveredLabel}
          </th>
        </tr>
        <tr>
          <th className="section-a-dynamic-table-header" colSpan={2}>
            Health insurance
          </th>
          <th className="section-a-dynamic-table-header" colSpan={2}>
            Accident insurance
          </th>
          <th className="section-a-dynamic-table-header" colSpan={2}>
            Maternity benefits
          </th>
          <th className="section-a-dynamic-table-header" colSpan={2}>
            Paternity Benefits
          </th>
          <th className="section-a-dynamic-table-header" colSpan={2}>
            Day Care facilities
          </th>
        </tr>
        <tr>
          <th className="section-a-dynamic-table-header">Number (B)</th>
          <th className="section-a-dynamic-table-header">% (B / A)</th>
          <th className="section-a-dynamic-table-header">Number (C)</th>
          <th className="section-a-dynamic-table-header">% (C / A)</th>
          <th className="section-a-dynamic-table-header">Number (D)</th>
          <th className="section-a-dynamic-table-header">% (D / A)</th>
          <th className="section-a-dynamic-table-header">Number (E)</th>
          <th className="section-a-dynamic-table-header">% (E / A)</th>
          <th className="section-a-dynamic-table-header">Number (F)</th>
          <th className="section-a-dynamic-table-header">% (F / A)</th>
        </tr>
      </thead>
    );

    return (
      <div style={{ padding: "20px 0" }}>
        {/* Question 1a: Employee wellbeing */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1a", "Details of measures for the well-being of employees:", true)}
          <div className="section-a-dynamic-table-container section-c-p3-wellbeing-table">
            <table className="section-a-dynamic-table">
              {renderP3WellbeingThead("% of employees covered by")}
              <tbody>
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f0f0f0" }}>
                  <td className="section-a-dynamic-table-cell" colSpan="12">
                    Permanent employees
                  </td>
                </tr>
                {renderWellbeingRow("permanent", "male", "Male", principle3Essential.q1a_employeeWellbeing.permanent.male, setPrinciple3Essential, "q1a_employeeWellbeing", "permanent")}
                {renderWellbeingRow("permanent", "female", "Female", principle3Essential.q1a_employeeWellbeing.permanent.female, setPrinciple3Essential, "q1a_employeeWellbeing", "permanent")}
                {renderWellbeingTotalRow(principle3Essential.q1a_employeeWellbeing.permanent, "permanent")}
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f0f0f0" }}>
                  <td className="section-a-dynamic-table-cell" colSpan="12">
                    Other than Permanent employees
                  </td>
                </tr>
                {renderWellbeingRow("otherThanPermanent", "male", "Male", principle3Essential.q1a_employeeWellbeing.otherThanPermanent.male, setPrinciple3Essential, "q1a_employeeWellbeing", "otherThanPermanent")}
                {renderWellbeingRow("otherThanPermanent", "female", "Female", principle3Essential.q1a_employeeWellbeing.otherThanPermanent.female, setPrinciple3Essential, "q1a_employeeWellbeing", "otherThanPermanent")}
                {renderWellbeingTotalRow(principle3Essential.q1a_employeeWellbeing.otherThanPermanent, "otherThanPermanent")}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 1b: Worker wellbeing */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1b", "Details of measures for the well-being of workers:", true)}
          <div className="section-a-dynamic-table-container section-c-p3-wellbeing-table">
            <table className="section-a-dynamic-table">
              {renderP3WellbeingThead("% of workers covered by")}
              <tbody>
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f0f0f0" }}>
                  <td className="section-a-dynamic-table-cell" colSpan="12">
                    Permanent workers
                  </td>
                </tr>
                {renderWellbeingRow("permanent", "male", "Male", principle3Essential.q1b_workerWellbeing.permanent.male, setPrinciple3Essential, "q1b_workerWellbeing", "permanent")}
                {renderWellbeingRow("permanent", "female", "Female", principle3Essential.q1b_workerWellbeing.permanent.female, setPrinciple3Essential, "q1b_workerWellbeing", "permanent")}
                {renderWellbeingTotalRow(principle3Essential.q1b_workerWellbeing.permanent, "permanent")}
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f0f0f0" }}>
                  <td className="section-a-dynamic-table-cell" colSpan="12">
                    Other than Permanent workers
                  </td>
                </tr>
                {renderWellbeingRow("otherThanPermanent", "male", "Male", principle3Essential.q1b_workerWellbeing.otherThanPermanent.male, setPrinciple3Essential, "q1b_workerWellbeing", "otherThanPermanent")}
                {renderWellbeingRow("otherThanPermanent", "female", "Female", principle3Essential.q1b_workerWellbeing.otherThanPermanent.female, setPrinciple3Essential, "q1b_workerWellbeing", "otherThanPermanent")}
                {renderWellbeingTotalRow(principle3Essential.q1b_workerWellbeing.otherThanPermanent, "otherThanPermanent")}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 1c (BRSR Core): Spending on wellbeing */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "1c",
              "Spending on measures towards well-being of employees and workers (including permanent and other than permanent) in the following format –",
              true,
              "As per BRSR Core, the KPI should include the 5 initiatives (i.e., health insurance, accident insurance, maternity benefits, paternity benefits, day care facilities) covered under question 1a and 1b under Principle 3 and additionally health & safety measures including access to mental health. To check sources including – Insurance Policies & Premium Paid Details, Infant Care Policy, Amount billed/invoices towards providing such facilities. Revenue shall mean 'Total Revenue from Operations – From Audited P&L Statement' as stated in Annexure I - Format of BRSR Core and should not include 'Other Income'. In case of BFSI, Total Revenue shall mean: 'Interest Earned' and 'Other Income' except Profit / (loss) on sale of building and other assets (net).",
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header"></th>
                    <th className="section-a-dynamic-table-header">
                      FY {financialYears.currentFY}
                      <br />
                    </th>
                    <th className="section-a-dynamic-table-header">
                      FY {financialYears.previousFY}
                      <br />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      Cost incurred on well-being measures as a % of total revenue of the company
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q1c_wellbeingSpending.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q1c_wellbeingSpending: { ...prev.q1c_wellbeingSpending, current: value },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q1c_wellbeingSpending.current && (parseFloat(principle3Essential.q1c_wellbeingSpending.current) < 0 || parseFloat(principle3Essential.q1c_wellbeingSpending.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q1c_wellbeingSpending.current && (parseFloat(principle3Essential.q1c_wellbeingSpending.current) < 0 || parseFloat(principle3Essential.q1c_wellbeingSpending.current) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q1c_wellbeingSpending.current && parseFloat(principle3Essential.q1c_wellbeingSpending.current) >= 0 && parseFloat(principle3Essential.q1c_wellbeingSpending.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q1c_wellbeingSpending.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q1c_wellbeingSpending: { ...prev.q1c_wellbeingSpending, previous: value },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q1c_wellbeingSpending.previous && (parseFloat(principle3Essential.q1c_wellbeingSpending.previous) < 0 || parseFloat(principle3Essential.q1c_wellbeingSpending.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q1c_wellbeingSpending.previous && (parseFloat(principle3Essential.q1c_wellbeingSpending.previous) < 0 || parseFloat(principle3Essential.q1c_wellbeingSpending.previous) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q1c_wellbeingSpending.previous && parseFloat(principle3Essential.q1c_wellbeingSpending.previous) >= 0 && parseFloat(principle3Essential.q1c_wellbeingSpending.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Question 2: Retirement benefits */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Details of retirement benefits, for Current FY and Previous Financial Year.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2">
                    Benefits
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.currentFY}
                    <br />
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.previousFY}
                    <br />
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">No. of employees covered as a % of total employees</th>
                  <th className="section-a-dynamic-table-header">No. of workers covered as a % of total workers</th>
                  <th className="section-a-dynamic-table-header">Deducted and deposited with the authority (Y/N/N.A.)</th>
                  <th className="section-a-dynamic-table-header">No. of employees covered as a % of total employees</th>
                  <th className="section-a-dynamic-table-header">No. of workers covered as a % of total workers</th>
                  <th className="section-a-dynamic-table-header">Deducted and deposited with the authority (Y/N/N.A.)</th>
                </tr>
              </thead>
              <tbody>
                {["pf", "gratuity", "esi", "others"].map((benefit) => (
                  <tr key={benefit} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {benefit === "pf" ? "PF" : benefit === "gratuity" ? "Gratuity" : benefit === "esi" ? "ESI" : "Others – please specify"}
                      {benefit === "others" && (
                        <InputText
                          value={principle3Essential.q2_retirementBenefits.others.specify}
                          onChange={(e) => {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q2_retirementBenefits: {
                                ...prev.q2_retirementBenefits,
                                others: { ...prev.q2_retirementBenefits.others, specify: e.target.value },
                              },
                            }));
                          }}
                          className="section-a-input"
                          placeholder="Specify"
                          style={{ width: "100%", marginTop: "5px" }}
                        />
                      )}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q2_retirementBenefits[benefit].currentEmployees}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q2_retirementBenefits: {
                                  ...prev.q2_retirementBenefits,
                                  [benefit]: { ...prev.q2_retirementBenefits[benefit], currentEmployees: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q2_retirementBenefits[benefit].currentEmployees && (parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentEmployees) < 0 || parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentEmployees) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q2_retirementBenefits[benefit].currentEmployees && (parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentEmployees) < 0 || parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentEmployees) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q2_retirementBenefits[benefit].currentEmployees && parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentEmployees) >= 0 && parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentEmployees) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q2_retirementBenefits[benefit].currentWorkers}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q2_retirementBenefits: {
                                  ...prev.q2_retirementBenefits,
                                  [benefit]: { ...prev.q2_retirementBenefits[benefit], currentWorkers: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q2_retirementBenefits[benefit].currentWorkers && (parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentWorkers) < 0 || parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentWorkers) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q2_retirementBenefits[benefit].currentWorkers && (parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentWorkers) < 0 || parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentWorkers) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q2_retirementBenefits[benefit].currentWorkers && parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentWorkers) >= 0 && parseFloat(principle3Essential.q2_retirementBenefits[benefit].currentWorkers) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={principle3Essential.q2_retirementBenefits[benefit].currentDeposited}
                        onChange={(e) => {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q2_retirementBenefits: {
                              ...prev.q2_retirementBenefits,
                              [benefit]: { ...prev.q2_retirementBenefits[benefit], currentDeposited: e.value },
                            },
                          }));
                        }}
                        options={[
                          { label: "Y", value: "Y" },
                          { label: "N", value: "N" },
                          { label: "N.A.", value: "N.A." },
                        ]}
                        placeholder="Select"
                        className="section-a-input"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q2_retirementBenefits[benefit].previousEmployees}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q2_retirementBenefits: {
                                  ...prev.q2_retirementBenefits,
                                  [benefit]: { ...prev.q2_retirementBenefits[benefit], previousEmployees: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q2_retirementBenefits[benefit].previousEmployees && (parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousEmployees) < 0 || parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousEmployees) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q2_retirementBenefits[benefit].previousEmployees && (parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousEmployees) < 0 || parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousEmployees) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q2_retirementBenefits[benefit].previousEmployees && parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousEmployees) >= 0 && parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousEmployees) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q2_retirementBenefits[benefit].previousWorkers}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q2_retirementBenefits: {
                                  ...prev.q2_retirementBenefits,
                                  [benefit]: { ...prev.q2_retirementBenefits[benefit], previousWorkers: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q2_retirementBenefits[benefit].previousWorkers && (parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousWorkers) < 0 || parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousWorkers) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q2_retirementBenefits[benefit].previousWorkers && (parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousWorkers) < 0 || parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousWorkers) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q2_retirementBenefits[benefit].previousWorkers && parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousWorkers) >= 0 && parseFloat(principle3Essential.q2_retirementBenefits[benefit].previousWorkers) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={principle3Essential.q2_retirementBenefits[benefit].previousDeposited}
                        onChange={(e) => {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q2_retirementBenefits: {
                              ...prev.q2_retirementBenefits,
                              [benefit]: { ...prev.q2_retirementBenefits[benefit], previousDeposited: e.value },
                            },
                          }));
                        }}
                        options={[
                          { label: "Y", value: "Y" },
                          { label: "N", value: "N" },
                          { label: "N.A.", value: "N.A." },
                        ]}
                        placeholder="Select"
                        className="section-a-input"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 3: Accessibility */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Accessibility of workplaces - Are the premises / offices of the entity accessible to differently abled employees and workers, as per the requirements of the Rights of Persons with Disabilities Act, 2016? If not, whether any steps are being taken by the entity in this regard.", true)}
          <InputTextarea
            value={principle3Essential.q3_accessibility}
            onChange={(e) => {
              setPrinciple3Essential((prev) => ({ ...prev, q3_accessibility: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 4: Equal opportunity policy */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Does the entity have an equal opportunity policy as per the Rights of Persons with Disabilities Act, 2016? If so, provide a web-link to the policy.", true)}
          <InputTextarea
            value={principle3Essential.q4_equalOpportunity}
            onChange={(e) => {
              setPrinciple3Essential((prev) => ({ ...prev, q4_equalOpportunity: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details and web-link"
          />
        </div>

        {/* Question 5: Return to work and retention rates */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Return to work and Retention rates of permanent employees and workers that took parental leave.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2">
                    Gender
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    Permanent employees
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    Permanent workers
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Return to work rate</th>
                  <th className="section-a-dynamic-table-header">Retention rate</th>
                  <th className="section-a-dynamic-table-header">Return to work rate</th>
                  <th className="section-a-dynamic-table-header">Retention rate</th>
                </tr>
              </thead>
              <tbody>
                {["male", "female"].map((gender) => (
                  <tr key={gender} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {gender === "male" ? "Male" : "Female"}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q5_returnRetention.employees[`${gender}Return`]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q5_returnRetention: {
                                  ...prev.q5_returnRetention,
                                  employees: { ...prev.q5_returnRetention.employees, [`${gender}Return`]: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q5_returnRetention.employees[`${gender}Return`] && (parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Return`]) < 0 || parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Return`]) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q5_returnRetention.employees[`${gender}Return`] && (parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Return`]) < 0 || parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Return`]) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q5_returnRetention.employees[`${gender}Return`] && parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Return`]) >= 0 && parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Return`]) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q5_returnRetention.employees[`${gender}Retention`]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q5_returnRetention: {
                                  ...prev.q5_returnRetention,
                                  employees: { ...prev.q5_returnRetention.employees, [`${gender}Retention`]: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q5_returnRetention.employees[`${gender}Retention`] && (parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Retention`]) < 0 || parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Retention`]) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q5_returnRetention.employees[`${gender}Retention`] && (parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Retention`]) < 0 || parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Retention`]) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q5_returnRetention.employees[`${gender}Retention`] && parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Retention`]) >= 0 && parseFloat(principle3Essential.q5_returnRetention.employees[`${gender}Retention`]) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q5_returnRetention.workers[`${gender}Return`]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q5_returnRetention: {
                                  ...prev.q5_returnRetention,
                                  workers: { ...prev.q5_returnRetention.workers, [`${gender}Return`]: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q5_returnRetention.workers[`${gender}Return`] && (parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Return`]) < 0 || parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Return`]) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q5_returnRetention.workers[`${gender}Return`] && (parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Return`]) < 0 || parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Return`]) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q5_returnRetention.workers[`${gender}Return`] && parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Return`]) >= 0 && parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Return`]) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div>
                        <InputText
                          value={principle3Essential.q5_returnRetention.workers[`${gender}Retention`]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q5_returnRetention: {
                                  ...prev.q5_returnRetention,
                                  workers: { ...prev.q5_returnRetention.workers, [`${gender}Retention`]: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle3Essential.q5_returnRetention.workers[`${gender}Retention`] && (parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Retention`]) < 0 || parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Retention`]) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle3Essential.q5_returnRetention.workers[`${gender}Retention`] && (parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Retention`]) < 0 || parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Retention`]) > 100) && (
                          <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                            Value needs to be between 0 and 100
                          </div>
                        )}
                        {principle3Essential.q5_returnRetention.workers[`${gender}Retention`] && parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Retention`]) >= 0 && parseFloat(principle3Essential.q5_returnRetention.workers[`${gender}Retention`]) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                  <td className="section-a-dynamic-table-cell">Total</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const total = calculateTotal(principle3Essential.q5_returnRetention.employees.maleReturn, principle3Essential.q5_returnRetention.employees.femaleReturn);
                      return total && parseFloat(total) > 0 ? `${(parseFloat(total) / 2).toFixed(2)}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const total = calculateTotal(principle3Essential.q5_returnRetention.employees.maleRetention, principle3Essential.q5_returnRetention.employees.femaleRetention);
                      return total && parseFloat(total) > 0 ? `${(parseFloat(total) / 2).toFixed(2)}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const total = calculateTotal(principle3Essential.q5_returnRetention.workers.maleReturn, principle3Essential.q5_returnRetention.workers.femaleReturn);
                      return total && parseFloat(total) > 0 ? `${(parseFloat(total) / 2).toFixed(2)}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const total = calculateTotal(principle3Essential.q5_returnRetention.workers.maleRetention, principle3Essential.q5_returnRetention.workers.femaleRetention);
                      return total && parseFloat(total) > 0 ? `${(parseFloat(total) / 2).toFixed(2)}%` : "";
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 6: Grievance mechanism */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("6", "Is there a mechanism available to receive and redress grievances for the following categories of employees and worker? If yes, give details of the mechanism in brief.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">Yes/No (If Yes, then give details of the mechanism in brief)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "permanentWorkers", label: "Permanent Workers" },
                  { key: "otherThanPermanentWorkers", label: "Other than Permanent Workers" },
                  { key: "permanentEmployees", label: "Permanent Employees" },
                  { key: "otherThanPermanentEmployees", label: "Other than Permanent Employees" },
                ].map((category) => (
                  <tr key={category.key} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {category.label}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ marginBottom: "10px" }}>
                        <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <RadioButton
                              inputId={`grievance-${category.key}-yes`}
                              value="Yes"
                              checked={principle3Essential.q6_grievance[category.key].hasMechanism === "Yes"}
                              onChange={(e) => {
                                setPrinciple3Essential((prev) => ({
                                  ...prev,
                                  q6_grievance: {
                                    ...prev.q6_grievance,
                                    [category.key]: { ...prev.q6_grievance[category.key], hasMechanism: "Yes" },
                                  },
                                }));
                              }}
                            />
                            <label htmlFor={`grievance-${category.key}-yes`} style={{ cursor: "pointer", margin: 0 }}>
                              Yes
                            </label>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <RadioButton
                              inputId={`grievance-${category.key}-no`}
                              value="No"
                              checked={principle3Essential.q6_grievance[category.key].hasMechanism === "No"}
                              onChange={(e) => {
                                setPrinciple3Essential((prev) => ({
                                  ...prev,
                                  q6_grievance: {
                                    ...prev.q6_grievance,
                                    [category.key]: { hasMechanism: "No", details: "" },
                                  },
                                }));
                              }}
                            />
                            <label htmlFor={`grievance-${category.key}-no`} style={{ cursor: "pointer", margin: 0 }}>
                              No
                            </label>
                          </div>
                        </div>
                        {principle3Essential.q6_grievance[category.key].hasMechanism === "Yes" && (
                          <InputTextarea
                            value={principle3Essential.q6_grievance[category.key].details}
                            onChange={(e) => {
                              setPrinciple3Essential((prev) => ({
                                ...prev,
                                q6_grievance: {
                                  ...prev.q6_grievance,
                                  [category.key]: { ...prev.q6_grievance[category.key], details: e.target.value },
                                },
                              }));
                            }}
                            className="section-a-input"
                            rows={3}
                            style={{ width: "100%", resize: "vertical" }}
                            placeholder="Enter details"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 7: Membership in associations/unions */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("7", "Membership of employees and worker in association(s) or Unions recognised by the listed entity:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2">
                    Category
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.currentFY}
                    <br />
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.previousFY}
                    <br />
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Total employees / workers in respective category (A)</th>
                  <th className="section-a-dynamic-table-header">No. of employees / workers in respective category, who are part of association(s) or Union (B)</th>
                  <th className="section-a-dynamic-table-header">% (B / A)</th>
                  <th className="section-a-dynamic-table-header">Total employees / workers in respective category (C)</th>
                  <th className="section-a-dynamic-table-header">No. of employees / workers in respective category, who are part of association(s) or Union (D)</th>
                  <th className="section-a-dynamic-table-header">% (D / C)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600 }}>
                  <td className="section-a-dynamic-table-cell">Total Permanent Employees</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Essential.q7_membership.currentFY.permanentEmployees?.aggregate?.total ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q7_membership: {
                              ...prev.q7_membership,
                              currentFY: {
                                ...prev.q7_membership.currentFY,
                                permanentEmployees: {
                                  ...prev.q7_membership.currentFY.permanentEmployees,
                                  aggregate: { ...(prev.q7_membership.currentFY.permanentEmployees?.aggregate || {}), total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Essential.q7_membership.currentFY.permanentEmployees?.aggregate?.members ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q7_membership: {
                              ...prev.q7_membership,
                              currentFY: {
                                ...prev.q7_membership.currentFY,
                                permanentEmployees: {
                                  ...prev.q7_membership.currentFY.permanentEmployees,
                                  aggregate: { ...(prev.q7_membership.currentFY.permanentEmployees?.aggregate || {}), members: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const agg = principle3Essential.q7_membership.currentFY.permanentEmployees?.aggregate || {};
                      const pct = calculatePercentage(agg.members, agg.total);
                      if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                        return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                      }
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Essential.q7_membership.previousFY.permanentEmployees?.aggregate?.total ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q7_membership: {
                              ...prev.q7_membership,
                              previousFY: {
                                ...prev.q7_membership.previousFY,
                                permanentEmployees: {
                                  ...prev.q7_membership.previousFY.permanentEmployees,
                                  aggregate: { ...(prev.q7_membership.previousFY.permanentEmployees?.aggregate || {}), total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Essential.q7_membership.previousFY.permanentEmployees?.aggregate?.members ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q7_membership: {
                              ...prev.q7_membership,
                              previousFY: {
                                ...prev.q7_membership.previousFY,
                                permanentEmployees: {
                                  ...prev.q7_membership.previousFY.permanentEmployees,
                                  aggregate: { ...(prev.q7_membership.previousFY.permanentEmployees?.aggregate || {}), members: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const agg = principle3Essential.q7_membership.previousFY.permanentEmployees?.aggregate || {};
                      const pct = calculatePercentage(agg.members, agg.total);
                      if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                        return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                      }
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                </tr>
                {["male", "female", "others"].map((gender) => (
                  <tr key={`employees-${gender}`} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, paddingLeft: "24px" }}>
                      {gender === "male" ? "Male" : gender === "female" ? "Female" : "Others"}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q7_membership.currentFY.permanentEmployees?.[gender]?.total ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q7_membership: {
                                ...prev.q7_membership,
                                currentFY: {
                                  ...prev.q7_membership.currentFY,
                                  permanentEmployees: {
                                    ...prev.q7_membership.currentFY.permanentEmployees,
                                    [gender]: { ...(prev.q7_membership.currentFY.permanentEmployees?.[gender] || {}), total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q7_membership.currentFY.permanentEmployees?.[gender]?.members ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q7_membership: {
                                ...prev.q7_membership,
                                currentFY: {
                                  ...prev.q7_membership.currentFY,
                                  permanentEmployees: {
                                    ...prev.q7_membership.currentFY.permanentEmployees,
                                    [gender]: { ...(prev.q7_membership.currentFY.permanentEmployees?.[gender] || {}), members: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const row = principle3Essential.q7_membership.currentFY.permanentEmployees?.[gender] || {};
                        const pct = calculatePercentage(row.members, row.total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q7_membership.previousFY.permanentEmployees?.[gender]?.total ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q7_membership: {
                                ...prev.q7_membership,
                                previousFY: {
                                  ...prev.q7_membership.previousFY,
                                  permanentEmployees: {
                                    ...prev.q7_membership.previousFY.permanentEmployees,
                                    [gender]: { ...(prev.q7_membership.previousFY.permanentEmployees?.[gender] || {}), total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q7_membership.previousFY.permanentEmployees?.[gender]?.members ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q7_membership: {
                                ...prev.q7_membership,
                                previousFY: {
                                  ...prev.q7_membership.previousFY,
                                  permanentEmployees: {
                                    ...prev.q7_membership.previousFY.permanentEmployees,
                                    [gender]: { ...(prev.q7_membership.previousFY.permanentEmployees?.[gender] || {}), members: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const row = principle3Essential.q7_membership.previousFY.permanentEmployees?.[gender] || {};
                        const pct = calculatePercentage(row.members, row.total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                  </tr>
                ))}
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600 }}>
                  <td className="section-a-dynamic-table-cell">Total Permanent Workers</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Essential.q7_membership.currentFY.permanentWorkers?.aggregate?.total ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q7_membership: {
                              ...prev.q7_membership,
                              currentFY: {
                                ...prev.q7_membership.currentFY,
                                permanentWorkers: {
                                  ...prev.q7_membership.currentFY.permanentWorkers,
                                  aggregate: { ...(prev.q7_membership.currentFY.permanentWorkers?.aggregate || {}), total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Essential.q7_membership.currentFY.permanentWorkers?.aggregate?.members ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q7_membership: {
                              ...prev.q7_membership,
                              currentFY: {
                                ...prev.q7_membership.currentFY,
                                permanentWorkers: {
                                  ...prev.q7_membership.currentFY.permanentWorkers,
                                  aggregate: { ...(prev.q7_membership.currentFY.permanentWorkers?.aggregate || {}), members: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const agg = principle3Essential.q7_membership.currentFY.permanentWorkers?.aggregate || {};
                      const pct = calculatePercentage(agg.members, agg.total);
                      if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                        return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                      }
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Essential.q7_membership.previousFY.permanentWorkers?.aggregate?.total ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q7_membership: {
                              ...prev.q7_membership,
                              previousFY: {
                                ...prev.q7_membership.previousFY,
                                permanentWorkers: {
                                  ...prev.q7_membership.previousFY.permanentWorkers,
                                  aggregate: { ...(prev.q7_membership.previousFY.permanentWorkers?.aggregate || {}), total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Essential.q7_membership.previousFY.permanentWorkers?.aggregate?.members ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q7_membership: {
                              ...prev.q7_membership,
                              previousFY: {
                                ...prev.q7_membership.previousFY,
                                permanentWorkers: {
                                  ...prev.q7_membership.previousFY.permanentWorkers,
                                  aggregate: { ...(prev.q7_membership.previousFY.permanentWorkers?.aggregate || {}), members: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const agg = principle3Essential.q7_membership.previousFY.permanentWorkers?.aggregate || {};
                      const pct = calculatePercentage(agg.members, agg.total);
                      if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                        return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                      }
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                </tr>
                {["male", "female", "others"].map((gender) => (
                  <tr key={`workers-${gender}`} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, paddingLeft: "24px" }}>
                      {gender === "male" ? "Male" : gender === "female" ? "Female" : "Others"}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q7_membership.currentFY.permanentWorkers?.[gender]?.total ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q7_membership: {
                                ...prev.q7_membership,
                                currentFY: {
                                  ...prev.q7_membership.currentFY,
                                  permanentWorkers: {
                                    ...prev.q7_membership.currentFY.permanentWorkers,
                                    [gender]: { ...(prev.q7_membership.currentFY.permanentWorkers?.[gender] || {}), total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q7_membership.currentFY.permanentWorkers?.[gender]?.members ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q7_membership: {
                                ...prev.q7_membership,
                                currentFY: {
                                  ...prev.q7_membership.currentFY,
                                  permanentWorkers: {
                                    ...prev.q7_membership.currentFY.permanentWorkers,
                                    [gender]: { ...(prev.q7_membership.currentFY.permanentWorkers?.[gender] || {}), members: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const row = principle3Essential.q7_membership.currentFY.permanentWorkers?.[gender] || {};
                        const pct = calculatePercentage(row.members, row.total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q7_membership.previousFY.permanentWorkers?.[gender]?.total ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q7_membership: {
                                ...prev.q7_membership,
                                previousFY: {
                                  ...prev.q7_membership.previousFY,
                                  permanentWorkers: {
                                    ...prev.q7_membership.previousFY.permanentWorkers,
                                    [gender]: { ...(prev.q7_membership.previousFY.permanentWorkers?.[gender] || {}), total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q7_membership.previousFY.permanentWorkers?.[gender]?.members ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q7_membership: {
                                ...prev.q7_membership,
                                previousFY: {
                                  ...prev.q7_membership.previousFY,
                                  permanentWorkers: {
                                    ...prev.q7_membership.previousFY.permanentWorkers,
                                    [gender]: { ...(prev.q7_membership.previousFY.permanentWorkers?.[gender] || {}), members: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const row = principle3Essential.q7_membership.previousFY.permanentWorkers?.[gender] || {};
                        const pct = calculatePercentage(row.members, row.total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 8: Training details */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("8", "Details of training given to employees and workers:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan={3}>
                    Category
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan={5}>
                    FY {financialYears.currentFY}
                    <br />
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan={5}>
                    FY {financialYears.previousFY}
                    <br />
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan={2}>
                    Total (A)
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan={2}>
                    On Health and safety measures
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan={2}>
                    On Skill upgradation
                  </th>
                  <th className="section-a-dynamic-table-header" rowSpan={2}>
                    Total (D)
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan={2}>
                    On Health and safety measures
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan={2}>
                    On Skill upgradation
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">No. (B)</th>
                  <th className="section-a-dynamic-table-header">% (B / A)</th>
                  <th className="section-a-dynamic-table-header">No. (C)</th>
                  <th className="section-a-dynamic-table-header">% (C / A)</th>
                  <th className="section-a-dynamic-table-header">No. (E)</th>
                  <th className="section-a-dynamic-table-header">% (E / D)</th>
                  <th className="section-a-dynamic-table-header">No. (F)</th>
                  <th className="section-a-dynamic-table-header">% (F / D)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f0f0f0" }}>
                  <td className="section-a-dynamic-table-cell" colSpan="11">
                    Employees
                  </td>
                </tr>
                {["male", "female"].map((gender) => (
                  <tr key={`training-employees-${gender}`} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, paddingLeft: "24px" }}>
                      {gender === "male" ? "Male" : "Female"}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.currentFY.employees[gender].total}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                currentFY: {
                                  ...prev.q8_training.currentFY,
                                  employees: {
                                    ...prev.q8_training.currentFY.employees,
                                    [gender]: { ...prev.q8_training.currentFY.employees[gender], total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.currentFY.employees[gender].healthSafety}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                currentFY: {
                                  ...prev.q8_training.currentFY,
                                  employees: {
                                    ...prev.q8_training.currentFY.employees,
                                    [gender]: { ...prev.q8_training.currentFY.employees[gender], healthSafety: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q8_training.currentFY.employees[gender].healthSafety, principle3Essential.q8_training.currentFY.employees[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.currentFY.employees[gender].skillUpgrade}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                currentFY: {
                                  ...prev.q8_training.currentFY,
                                  employees: {
                                    ...prev.q8_training.currentFY.employees,
                                    [gender]: { ...prev.q8_training.currentFY.employees[gender], skillUpgrade: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q8_training.currentFY.employees[gender].skillUpgrade, principle3Essential.q8_training.currentFY.employees[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.previousFY.employees[gender].total}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                previousFY: {
                                  ...prev.q8_training.previousFY,
                                  employees: {
                                    ...prev.q8_training.previousFY.employees,
                                    [gender]: { ...prev.q8_training.previousFY.employees[gender], total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.previousFY.employees[gender].healthSafety}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                previousFY: {
                                  ...prev.q8_training.previousFY,
                                  employees: {
                                    ...prev.q8_training.previousFY.employees,
                                    [gender]: { ...prev.q8_training.previousFY.employees[gender], healthSafety: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q8_training.previousFY.employees[gender].healthSafety, principle3Essential.q8_training.previousFY.employees[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.previousFY.employees[gender].skillUpgrade}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                previousFY: {
                                  ...prev.q8_training.previousFY,
                                  employees: {
                                    ...prev.q8_training.previousFY.employees,
                                    [gender]: { ...prev.q8_training.previousFY.employees[gender], skillUpgrade: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q8_training.previousFY.employees[gender].skillUpgrade, principle3Essential.q8_training.previousFY.employees[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                  </tr>
                ))}
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                  <td className="section-a-dynamic-table-cell">Total</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.currentFY.employees.male.total, principle3Essential.q8_training.currentFY.employees.female.total)}</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.currentFY.employees.male.healthSafety, principle3Essential.q8_training.currentFY.employees.female.healthSafety)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalA = calculateTotal(principle3Essential.q8_training.currentFY.employees.male.total, principle3Essential.q8_training.currentFY.employees.female.total);
                      const totalB = calculateTotal(principle3Essential.q8_training.currentFY.employees.male.healthSafety, principle3Essential.q8_training.currentFY.employees.female.healthSafety);
                      const pct = calculatePercentage(totalB, totalA);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.currentFY.employees.male.skillUpgrade, principle3Essential.q8_training.currentFY.employees.female.skillUpgrade)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalA = calculateTotal(principle3Essential.q8_training.currentFY.employees.male.total, principle3Essential.q8_training.currentFY.employees.female.total);
                      const totalC = calculateTotal(principle3Essential.q8_training.currentFY.employees.male.skillUpgrade, principle3Essential.q8_training.currentFY.employees.female.skillUpgrade);
                      const pct = calculatePercentage(totalC, totalA);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.previousFY.employees.male.total, principle3Essential.q8_training.previousFY.employees.female.total)}</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.previousFY.employees.male.healthSafety, principle3Essential.q8_training.previousFY.employees.female.healthSafety)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalD = calculateTotal(principle3Essential.q8_training.previousFY.employees.male.total, principle3Essential.q8_training.previousFY.employees.female.total);
                      const totalE = calculateTotal(principle3Essential.q8_training.previousFY.employees.male.healthSafety, principle3Essential.q8_training.previousFY.employees.female.healthSafety);
                      const pct = calculatePercentage(totalE, totalD);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.previousFY.employees.male.skillUpgrade, principle3Essential.q8_training.previousFY.employees.female.skillUpgrade)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalD = calculateTotal(principle3Essential.q8_training.previousFY.employees.male.total, principle3Essential.q8_training.previousFY.employees.female.total);
                      const totalF = calculateTotal(principle3Essential.q8_training.previousFY.employees.male.skillUpgrade, principle3Essential.q8_training.previousFY.employees.female.skillUpgrade);
                      const pct = calculatePercentage(totalF, totalD);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f0f0f0" }}>
                  <td className="section-a-dynamic-table-cell" colSpan="11">
                    Workers
                  </td>
                </tr>
                {["male", "female"].map((gender) => (
                  <tr key={`training-workers-${gender}`} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, paddingLeft: "24px" }}>
                      {gender === "male" ? "Male" : "Female"}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.currentFY.workers[gender].total}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                currentFY: {
                                  ...prev.q8_training.currentFY,
                                  workers: {
                                    ...prev.q8_training.currentFY.workers,
                                    [gender]: { ...prev.q8_training.currentFY.workers[gender], total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.currentFY.workers[gender].healthSafety}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                currentFY: {
                                  ...prev.q8_training.currentFY,
                                  workers: {
                                    ...prev.q8_training.currentFY.workers,
                                    [gender]: { ...prev.q8_training.currentFY.workers[gender], healthSafety: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q8_training.currentFY.workers[gender].healthSafety, principle3Essential.q8_training.currentFY.workers[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.currentFY.workers[gender].skillUpgrade}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                currentFY: {
                                  ...prev.q8_training.currentFY,
                                  workers: {
                                    ...prev.q8_training.currentFY.workers,
                                    [gender]: { ...prev.q8_training.currentFY.workers[gender], skillUpgrade: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q8_training.currentFY.workers[gender].skillUpgrade, principle3Essential.q8_training.currentFY.workers[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.previousFY.workers[gender].total}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                previousFY: {
                                  ...prev.q8_training.previousFY,
                                  workers: {
                                    ...prev.q8_training.previousFY.workers,
                                    [gender]: { ...prev.q8_training.previousFY.workers[gender], total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.previousFY.workers[gender].healthSafety}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                previousFY: {
                                  ...prev.q8_training.previousFY,
                                  workers: {
                                    ...prev.q8_training.previousFY.workers,
                                    [gender]: { ...prev.q8_training.previousFY.workers[gender], healthSafety: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q8_training.previousFY.workers[gender].healthSafety, principle3Essential.q8_training.previousFY.workers[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q8_training.previousFY.workers[gender].skillUpgrade}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q8_training: {
                                ...prev.q8_training,
                                previousFY: {
                                  ...prev.q8_training.previousFY,
                                  workers: {
                                    ...prev.q8_training.previousFY.workers,
                                    [gender]: { ...prev.q8_training.previousFY.workers[gender], skillUpgrade: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q8_training.previousFY.workers[gender].skillUpgrade, principle3Essential.q8_training.previousFY.workers[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                  </tr>
                ))}
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                  <td className="section-a-dynamic-table-cell">Total</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.currentFY.workers.male.total, principle3Essential.q8_training.currentFY.workers.female.total)}</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.currentFY.workers.male.healthSafety, principle3Essential.q8_training.currentFY.workers.female.healthSafety)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalA = calculateTotal(principle3Essential.q8_training.currentFY.workers.male.total, principle3Essential.q8_training.currentFY.workers.female.total);
                      const totalB = calculateTotal(principle3Essential.q8_training.currentFY.workers.male.healthSafety, principle3Essential.q8_training.currentFY.workers.female.healthSafety);
                      const pct = calculatePercentage(totalB, totalA);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.currentFY.workers.male.skillUpgrade, principle3Essential.q8_training.currentFY.workers.female.skillUpgrade)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalA = calculateTotal(principle3Essential.q8_training.currentFY.workers.male.total, principle3Essential.q8_training.currentFY.workers.female.total);
                      const totalC = calculateTotal(principle3Essential.q8_training.currentFY.workers.male.skillUpgrade, principle3Essential.q8_training.currentFY.workers.female.skillUpgrade);
                      const pct = calculatePercentage(totalC, totalA);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.previousFY.workers.male.total, principle3Essential.q8_training.previousFY.workers.female.total)}</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.previousFY.workers.male.healthSafety, principle3Essential.q8_training.previousFY.workers.female.healthSafety)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalD = calculateTotal(principle3Essential.q8_training.previousFY.workers.male.total, principle3Essential.q8_training.previousFY.workers.female.total);
                      const totalE = calculateTotal(principle3Essential.q8_training.previousFY.workers.male.healthSafety, principle3Essential.q8_training.previousFY.workers.female.healthSafety);
                      const pct = calculatePercentage(totalE, totalD);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q8_training.previousFY.workers.male.skillUpgrade, principle3Essential.q8_training.previousFY.workers.female.skillUpgrade)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalD = calculateTotal(principle3Essential.q8_training.previousFY.workers.male.total, principle3Essential.q8_training.previousFY.workers.female.total);
                      const totalF = calculateTotal(principle3Essential.q8_training.previousFY.workers.male.skillUpgrade, principle3Essential.q8_training.previousFY.workers.female.skillUpgrade);
                      const pct = calculatePercentage(totalF, totalD);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 9: Performance reviews - Similar structure to Q8 but simpler */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("9", "Details of performance and career development reviews of employees and worker:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2">
                    Category
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.currentFY}
                    <br />
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.previousFY}
                    <br />
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Total (A)</th>
                  <th className="section-a-dynamic-table-header">No. (B)</th>
                  <th className="section-a-dynamic-table-header">% (B / A)</th>
                  <th className="section-a-dynamic-table-header">Total (C)</th>
                  <th className="section-a-dynamic-table-header">No. (D)</th>
                  <th className="section-a-dynamic-table-header">% (D / C)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f0f0f0" }}>
                  <td className="section-a-dynamic-table-cell" colSpan="7">
                    Employees
                  </td>
                </tr>
                {["male", "female"].map((gender) => (
                  <tr key={`performance-employees-${gender}`} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, paddingLeft: "24px" }}>
                      {gender === "male" ? "Male" : "Female"}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q9_performanceReviews.currentFY.employees[gender].total}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q9_performanceReviews: {
                                ...prev.q9_performanceReviews,
                                currentFY: {
                                  ...prev.q9_performanceReviews.currentFY,
                                  employees: {
                                    ...prev.q9_performanceReviews.currentFY.employees,
                                    [gender]: { ...prev.q9_performanceReviews.currentFY.employees[gender], total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q9_performanceReviews.currentFY.employees[gender].reviewed}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q9_performanceReviews: {
                                ...prev.q9_performanceReviews,
                                currentFY: {
                                  ...prev.q9_performanceReviews.currentFY,
                                  employees: {
                                    ...prev.q9_performanceReviews.currentFY.employees,
                                    [gender]: { ...prev.q9_performanceReviews.currentFY.employees[gender], reviewed: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q9_performanceReviews.currentFY.employees[gender].reviewed, principle3Essential.q9_performanceReviews.currentFY.employees[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q9_performanceReviews.previousFY.employees[gender].total}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q9_performanceReviews: {
                                ...prev.q9_performanceReviews,
                                previousFY: {
                                  ...prev.q9_performanceReviews.previousFY,
                                  employees: {
                                    ...prev.q9_performanceReviews.previousFY.employees,
                                    [gender]: { ...prev.q9_performanceReviews.previousFY.employees[gender], total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q9_performanceReviews.previousFY.employees[gender].reviewed}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q9_performanceReviews: {
                                ...prev.q9_performanceReviews,
                                previousFY: {
                                  ...prev.q9_performanceReviews.previousFY,
                                  employees: {
                                    ...prev.q9_performanceReviews.previousFY.employees,
                                    [gender]: { ...prev.q9_performanceReviews.previousFY.employees[gender], reviewed: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q9_performanceReviews.previousFY.employees[gender].reviewed, principle3Essential.q9_performanceReviews.previousFY.employees[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                  </tr>
                ))}
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                  <td className="section-a-dynamic-table-cell">Total</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q9_performanceReviews.currentFY.employees.male.total, principle3Essential.q9_performanceReviews.currentFY.employees.female.total)}</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q9_performanceReviews.currentFY.employees.male.reviewed, principle3Essential.q9_performanceReviews.currentFY.employees.female.reviewed)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalA = calculateTotal(principle3Essential.q9_performanceReviews.currentFY.employees.male.total, principle3Essential.q9_performanceReviews.currentFY.employees.female.total);
                      const totalB = calculateTotal(principle3Essential.q9_performanceReviews.currentFY.employees.male.reviewed, principle3Essential.q9_performanceReviews.currentFY.employees.female.reviewed);
                      const pct = calculatePercentage(totalB, totalA);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q9_performanceReviews.previousFY.employees.male.total, principle3Essential.q9_performanceReviews.previousFY.employees.female.total)}</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q9_performanceReviews.previousFY.employees.male.reviewed, principle3Essential.q9_performanceReviews.previousFY.employees.female.reviewed)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalC = calculateTotal(principle3Essential.q9_performanceReviews.previousFY.employees.male.total, principle3Essential.q9_performanceReviews.previousFY.employees.female.total);
                      const totalD = calculateTotal(principle3Essential.q9_performanceReviews.previousFY.employees.male.reviewed, principle3Essential.q9_performanceReviews.previousFY.employees.female.reviewed);
                      const pct = calculatePercentage(totalD, totalC);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f0f0f0" }}>
                  <td className="section-a-dynamic-table-cell" colSpan="7">
                    Workers
                  </td>
                </tr>
                {["male", "female"].map((gender) => (
                  <tr key={`performance-workers-${gender}`} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, paddingLeft: "24px" }}>
                      {gender === "male" ? "Male" : "Female"}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q9_performanceReviews.currentFY.workers[gender].total}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q9_performanceReviews: {
                                ...prev.q9_performanceReviews,
                                currentFY: {
                                  ...prev.q9_performanceReviews.currentFY,
                                  workers: {
                                    ...prev.q9_performanceReviews.currentFY.workers,
                                    [gender]: { ...prev.q9_performanceReviews.currentFY.workers[gender], total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q9_performanceReviews.currentFY.workers[gender].reviewed}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q9_performanceReviews: {
                                ...prev.q9_performanceReviews,
                                currentFY: {
                                  ...prev.q9_performanceReviews.currentFY,
                                  workers: {
                                    ...prev.q9_performanceReviews.currentFY.workers,
                                    [gender]: { ...prev.q9_performanceReviews.currentFY.workers[gender], reviewed: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q9_performanceReviews.currentFY.workers[gender].reviewed, principle3Essential.q9_performanceReviews.currentFY.workers[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q9_performanceReviews.previousFY.workers[gender].total}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q9_performanceReviews: {
                                ...prev.q9_performanceReviews,
                                previousFY: {
                                  ...prev.q9_performanceReviews.previousFY,
                                  workers: {
                                    ...prev.q9_performanceReviews.previousFY.workers,
                                    [gender]: { ...prev.q9_performanceReviews.previousFY.workers[gender], total: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q9_performanceReviews.previousFY.workers[gender].reviewed}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q9_performanceReviews: {
                                ...prev.q9_performanceReviews,
                                previousFY: {
                                  ...prev.q9_performanceReviews.previousFY,
                                  workers: {
                                    ...prev.q9_performanceReviews.previousFY.workers,
                                    [gender]: { ...prev.q9_performanceReviews.previousFY.workers[gender], reviewed: value },
                                  },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {(() => {
                        const pct = calculatePercentage(principle3Essential.q9_performanceReviews.previousFY.workers[gender].reviewed, principle3Essential.q9_performanceReviews.previousFY.workers[gender].total);
                        if (pct && (parseFloat(pct) < 0 || parseFloat(pct) > 100)) {
                          return <span style={{ color: "#d32f2f", fontSize: "12px" }}>Value needs to be between 0 and 100</span>;
                        }
                        return pct ? `${pct}%` : "";
                      })()}
                    </td>
                  </tr>
                ))}
                <tr className="section-a-dynamic-table-row" style={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                  <td className="section-a-dynamic-table-cell">Total</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q9_performanceReviews.currentFY.workers.male.total, principle3Essential.q9_performanceReviews.currentFY.workers.female.total)}</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q9_performanceReviews.currentFY.workers.male.reviewed, principle3Essential.q9_performanceReviews.currentFY.workers.female.reviewed)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalA = calculateTotal(principle3Essential.q9_performanceReviews.currentFY.workers.male.total, principle3Essential.q9_performanceReviews.currentFY.workers.female.total);
                      const totalB = calculateTotal(principle3Essential.q9_performanceReviews.currentFY.workers.male.reviewed, principle3Essential.q9_performanceReviews.currentFY.workers.female.reviewed);
                      const pct = calculatePercentage(totalB, totalA);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q9_performanceReviews.previousFY.workers.male.total, principle3Essential.q9_performanceReviews.previousFY.workers.female.total)}</td>
                  <td className="section-a-dynamic-table-cell">{calculateTotal(principle3Essential.q9_performanceReviews.previousFY.workers.male.reviewed, principle3Essential.q9_performanceReviews.previousFY.workers.female.reviewed)}</td>
                  <td className="section-a-dynamic-table-cell">
                    {(() => {
                      const totalC = calculateTotal(principle3Essential.q9_performanceReviews.previousFY.workers.male.total, principle3Essential.q9_performanceReviews.previousFY.workers.female.total);
                      const totalD = calculateTotal(principle3Essential.q9_performanceReviews.previousFY.workers.male.reviewed, principle3Essential.q9_performanceReviews.previousFY.workers.female.reviewed);
                      const pct = calculatePercentage(totalD, totalC);
                      return pct ? `${pct}%` : "";
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 10: Health and safety management system */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("10", "Health and safety management system:", true)}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
              <StarIcon style={{ fontSize: "18px", color: "#FF6F61", flexShrink: 0 }} />
              <h5 style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>10a. Whether an occupational health and safety management system has been implemented by the entity? (Yes/ No). If yes, the coverage such system?</h5>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <RadioButton
                    inputId="health-safety-yes"
                    value="Yes"
                    checked={principle3Essential.q10_healthSafety.a_implemented.hasSystem === "Yes"}
                    onChange={(e) => {
                      setPrinciple3Essential((prev) => ({
                        ...prev,
                        q10_healthSafety: {
                          ...prev.q10_healthSafety,
                          a_implemented: { ...prev.q10_healthSafety.a_implemented, hasSystem: "Yes" },
                        },
                      }));
                    }}
                  />
                  <label htmlFor="health-safety-yes" style={{ cursor: "pointer", margin: 0 }}>
                    Yes
                  </label>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <RadioButton
                    inputId="health-safety-no"
                    value="No"
                    checked={principle3Essential.q10_healthSafety.a_implemented.hasSystem === "No"}
                    onChange={(e) => {
                      setPrinciple3Essential((prev) => ({
                        ...prev,
                        q10_healthSafety: {
                          ...prev.q10_healthSafety,
                          a_implemented: { hasSystem: "No", coverage: "" },
                        },
                      }));
                    }}
                  />
                  <label htmlFor="health-safety-no" style={{ cursor: "pointer", margin: 0 }}>
                    No
                  </label>
                </div>
              </div>
              {principle3Essential.q10_healthSafety.a_implemented.hasSystem === "Yes" && (
                <InputTextarea
                  value={principle3Essential.q10_healthSafety.a_implemented.coverage}
                  onChange={(e) => {
                    setPrinciple3Essential((prev) => ({
                      ...prev,
                      q10_healthSafety: {
                        ...prev.q10_healthSafety,
                        a_implemented: { ...prev.q10_healthSafety.a_implemented, coverage: e.target.value },
                      },
                    }));
                  }}
                  className="section-a-input"
                  rows={3}
                  style={{ width: "100%", resize: "vertical" }}
                  placeholder="Enter coverage details"
                />
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
              <StarIcon style={{ fontSize: "18px", color: "#FF6F61", flexShrink: 0 }} />
              <h5 style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>10b. What are the processes used to identify work-related hazards and assess risks on a routine and non-routine basis by the entity?</h5>
            </div>
            <InputTextarea
              value={principle3Essential.q10_healthSafety.b_processes}
              onChange={(e) => {
                setPrinciple3Essential((prev) => ({
                  ...prev,
                  q10_healthSafety: { ...prev.q10_healthSafety, b_processes: e.target.value },
                }));
              }}
              className="section-a-input"
              rows={3}
              style={{ width: "100%", resize: "vertical" }}
              placeholder="Enter details"
            />
            <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "20px 0 10px 0" }}>
              <StarIcon style={{ fontSize: "18px", color: "#FF6F61", flexShrink: 0 }} />
              <h5 style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>10c. Whether you have processes for workers to report the work related hazards and to remove themselves from such risks. (Y/N)</h5>
            </div>
            <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="worker-reporting-yes"
                  value="Yes"
                  checked={principle3Essential.q10_healthSafety.c_workerReporting === "Yes" || principle3Essential.q10_healthSafety.c_workerReporting === "Y"}
                  onChange={() => {
                    setPrinciple3Essential((prev) => ({
                      ...prev,
                      q10_healthSafety: { ...prev.q10_healthSafety, c_workerReporting: "Yes" },
                    }));
                  }}
                />
                <label htmlFor="worker-reporting-yes" style={{ cursor: "pointer", margin: 0 }}>
                  Yes
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="worker-reporting-no"
                  value="No"
                  checked={principle3Essential.q10_healthSafety.c_workerReporting === "No" || principle3Essential.q10_healthSafety.c_workerReporting === "N"}
                  onChange={() => {
                    setPrinciple3Essential((prev) => ({
                      ...prev,
                      q10_healthSafety: {
                        ...prev.q10_healthSafety,
                        c_workerReporting: "No",
                        c_workerReportingDetails: "",
                      },
                    }));
                  }}
                />
                <label htmlFor="worker-reporting-no" style={{ cursor: "pointer", margin: 0 }}>
                  No
                </label>
              </div>
            </div>
            {(principle3Essential.q10_healthSafety.c_workerReporting === "Yes" || principle3Essential.q10_healthSafety.c_workerReporting === "Y") && (
              <InputTextarea
                value={principle3Essential.q10_healthSafety.c_workerReportingDetails}
                onChange={(e) => {
                  setPrinciple3Essential((prev) => ({
                    ...prev,
                    q10_healthSafety: { ...prev.q10_healthSafety, c_workerReportingDetails: e.target.value },
                  }));
                }}
                className="section-a-input"
                rows={3}
                style={{ width: "100%", resize: "vertical", marginTop: "10px" }}
                placeholder="Provide additional details"
              />
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "20px 0 10px 0" }}>
              <StarIcon style={{ fontSize: "18px", color: "#FF6F61", flexShrink: 0 }} />
              <h5 style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>10d. Do the employees/ worker of the entity have access to non-occupational medical and healthcare services? (Yes/ No)</h5>
            </div>
            <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="medical-access-yes"
                  value="Yes"
                  checked={principle3Essential.q10_healthSafety.d_medicalAccess === "Yes"}
                  onChange={() => {
                    setPrinciple3Essential((prev) => ({
                      ...prev,
                      q10_healthSafety: { ...prev.q10_healthSafety, d_medicalAccess: "Yes" },
                    }));
                  }}
                />
                <label htmlFor="medical-access-yes" style={{ cursor: "pointer", margin: 0 }}>
                  Yes
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="medical-access-no"
                  value="No"
                  checked={principle3Essential.q10_healthSafety.d_medicalAccess === "No"}
                  onChange={() => {
                    setPrinciple3Essential((prev) => ({
                      ...prev,
                      q10_healthSafety: {
                        ...prev.q10_healthSafety,
                        d_medicalAccess: "No",
                        d_medicalAccessDetails: "",
                      },
                    }));
                  }}
                />
                <label htmlFor="medical-access-no" style={{ cursor: "pointer", margin: 0 }}>
                  No
                </label>
              </div>
            </div>
            {principle3Essential.q10_healthSafety.d_medicalAccess === "Yes" && (
              <InputTextarea
                value={principle3Essential.q10_healthSafety.d_medicalAccessDetails}
                onChange={(e) => {
                  setPrinciple3Essential((prev) => ({
                    ...prev,
                    q10_healthSafety: { ...prev.q10_healthSafety, d_medicalAccessDetails: e.target.value },
                  }));
                }}
                className="section-a-input"
                rows={3}
                style={{ width: "100%", resize: "vertical", marginTop: "10px" }}
                placeholder="Provide additional details"
              />
            )}
          </div>
        </div>

        {/* Question 11 (BRSR Core): Safety incidents */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "11",
              "Details of safety related incidents, in the following format:",
              true,
              "Number of Permanent Disabilities: To check on the basis of claims. Lost Time Injury Frequency Rate (LTIFR) (per one million-person hours worked): 1. Total number of lost time injuries 2. Total No. of working hours 3. LTIFR = (Total number of lost time injuries *10,00,000) / Total No. of working hours. No. of fatalities: To check on the basis of claims as reported to the Factory Inspector",
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">Safety Incident / Number</th>
                    <th className="section-a-dynamic-table-header">Category*</th>
                    <th className="section-a-dynamic-table-header">
                      FY {financialYears.currentFY}
                      <br />
                    </th>
                    <th className="section-a-dynamic-table-header">
                      FY {financialYears.previousFY}
                      <br />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: "ltifr", label: "Lost Time Injury Frequency Rate (LTIFR) (per one million-person hours worked)" },
                    { key: "recordableInjuries", label: "Total recordable work-related injuries" },
                    { key: "fatalities", label: "No. of fatalities" },
                    { key: "highConsequence", label: "High consequence work-related injury or ill-health (excluding fatalities)" },
                  ].map((incident) => (
                    <React.Fragment key={incident.key}>
                      <tr className="section-a-dynamic-table-row">
                        <td className="section-a-dynamic-table-cell" rowSpan="2" style={{ fontWeight: 500, verticalAlign: "middle" }}>
                          {incident.label}
                        </td>
                        <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                          Employees
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle3Essential.q11_safetyIncidents[incident.key].employeesCurrent}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple3Essential((prev) => ({
                                  ...prev,
                                  q11_safetyIncidents: {
                                    ...prev.q11_safetyIncidents,
                                    [incident.key]: {
                                      ...prev.q11_safetyIncidents[incident.key],
                                      employeesCurrent: value,
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle3Essential.q11_safetyIncidents[incident.key].employeesPrevious}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple3Essential((prev) => ({
                                  ...prev,
                                  q11_safetyIncidents: {
                                    ...prev.q11_safetyIncidents,
                                    [incident.key]: {
                                      ...prev.q11_safetyIncidents[incident.key],
                                      employeesPrevious: value,
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                      </tr>
                      <tr className="section-a-dynamic-table-row">
                        <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                          Workers
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle3Essential.q11_safetyIncidents[incident.key].workersCurrent}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple3Essential((prev) => ({
                                  ...prev,
                                  q11_safetyIncidents: {
                                    ...prev.q11_safetyIncidents,
                                    [incident.key]: {
                                      ...prev.q11_safetyIncidents[incident.key],
                                      workersCurrent: value,
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle3Essential.q11_safetyIncidents[incident.key].workersPrevious}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple3Essential((prev) => ({
                                  ...prev,
                                  q11_safetyIncidents: {
                                    ...prev.q11_safetyIncidents,
                                    [incident.key]: {
                                      ...prev.q11_safetyIncidents[incident.key],
                                      workersPrevious: value,
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: "12px", color: "#666", marginTop: "10px" }}>*Including in the contract workforce</p>
            </div>
          </div>
        )}

        {/* Question 12: Safe workplace measures */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("12", "Describe the measures taken by the entity to ensure a safe and healthy work place.", true)}
          <InputTextarea
            value={principle3Essential.q12_safeWorkplace}
            onChange={(e) => {
              setPrinciple3Essential((prev) => ({ ...prev, q12_safeWorkplace: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 13: Complaints */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("13", "Number of Complaints on the following made by employees and workers:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.currentFY}
                    <br />
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.previousFY}
                    <br />
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">Filed during the Year</th>
                  <th className="section-a-dynamic-table-header">Pending resolution at the end of year</th>
                  <th className="section-a-dynamic-table-header">Remarks</th>
                  <th className="section-a-dynamic-table-header">Filed during the Year</th>
                  <th className="section-a-dynamic-table-header">Pending resolution at the end of year</th>
                  <th className="section-a-dynamic-table-header">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "workingConditions", label: "Working Conditions" },
                  { key: "healthSafety", label: "Health & Safety" },
                ].map((complaint) => (
                  <tr key={complaint.key} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {complaint.label}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q13_complaints[complaint.key].currentFiled}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q13_complaints: {
                                ...prev.q13_complaints,
                                [complaint.key]: { ...prev.q13_complaints[complaint.key], currentFiled: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q13_complaints[complaint.key].currentPending}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q13_complaints: {
                                ...prev.q13_complaints,
                                [complaint.key]: { ...prev.q13_complaints[complaint.key], currentPending: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={principle3Essential.q13_complaints[complaint.key].currentRemarks}
                        onChange={(e) => {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q13_complaints: {
                              ...prev.q13_complaints,
                              [complaint.key]: { ...prev.q13_complaints[complaint.key], currentRemarks: e.target.value },
                            },
                          }));
                        }}
                        className="section-a-input"
                        rows={2}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q13_complaints[complaint.key].previousFiled}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q13_complaints: {
                                ...prev.q13_complaints,
                                [complaint.key]: { ...prev.q13_complaints[complaint.key], previousFiled: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle3Essential.q13_complaints[complaint.key].previousPending}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q13_complaints: {
                                ...prev.q13_complaints,
                                [complaint.key]: { ...prev.q13_complaints[complaint.key], previousPending: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={principle3Essential.q13_complaints[complaint.key].previousRemarks}
                        onChange={(e) => {
                          setPrinciple3Essential((prev) => ({
                            ...prev,
                            q13_complaints: {
                              ...prev.q13_complaints,
                              [complaint.key]: { ...prev.q13_complaints[complaint.key], previousRemarks: e.target.value },
                            },
                          }));
                        }}
                        className="section-a-input"
                        rows={2}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 14: Assessments */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("14", "Assessments for the year:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">% of your plants and offices that were assessed (by entity or statutory authorities or third parties)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Health and safety practices
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div>
                      <InputText
                        value={principle3Essential.q14_assessments.healthSafety}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q14_assessments: { ...prev.q14_assessments, healthSafety: value },
                            }));
                          }
                        }}
                        className={`section-a-input ${principle3Essential.q14_assessments.healthSafety && (parseFloat(principle3Essential.q14_assessments.healthSafety) < 0 || parseFloat(principle3Essential.q14_assessments.healthSafety) > 100) ? "section-a-input-error" : ""}`}
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle3Essential.q14_assessments.healthSafety && (parseFloat(principle3Essential.q14_assessments.healthSafety) < 0 || parseFloat(principle3Essential.q14_assessments.healthSafety) > 100) && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Value needs to be between 0 and 100
                        </div>
                      )}
                      {principle3Essential.q14_assessments.healthSafety && parseFloat(principle3Essential.q14_assessments.healthSafety) >= 0 && parseFloat(principle3Essential.q14_assessments.healthSafety) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                    </div>
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Working Conditions
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div>
                      <InputText
                        value={principle3Essential.q14_assessments.workingConditions}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            setPrinciple3Essential((prev) => ({
                              ...prev,
                              q14_assessments: { ...prev.q14_assessments, workingConditions: value },
                            }));
                          }
                        }}
                        className={`section-a-input ${principle3Essential.q14_assessments.workingConditions && (parseFloat(principle3Essential.q14_assessments.workingConditions) < 0 || parseFloat(principle3Essential.q14_assessments.workingConditions) > 100) ? "section-a-input-error" : ""}`}
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle3Essential.q14_assessments.workingConditions && (parseFloat(principle3Essential.q14_assessments.workingConditions) < 0 || parseFloat(principle3Essential.q14_assessments.workingConditions) > 100) && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Value needs to be between 0 and 100
                        </div>
                      )}
                      {principle3Essential.q14_assessments.workingConditions && parseFloat(principle3Essential.q14_assessments.workingConditions) >= 0 && parseFloat(principle3Essential.q14_assessments.workingConditions) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 15: Corrective action */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("15", "Provide details of any corrective action taken or underway to address safety-related incidents (if any) and on significant risks / concerns arising from assessments of health & safety practices and working conditions.", true)}
          <InputTextarea
            value={principle3Essential.q15_correctiveAction}
            onChange={(e) => {
              setPrinciple3Essential((prev) => ({ ...prev, q15_correctiveAction: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>
      </div>
    );
  };

  // Render Principle 3 Leadership Questions
  const renderPrinciple3Leadership = () => {
    return (
      <div style={{ padding: "20px 0" }}>
        {/* Question 1: Life insurance — (A)/(B) with Yes/No radios (not a table) */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Does the entity extend any life insurance or any compensatory package in the event of death of (A) Employees (Y/N) (B) Workers (Y/N).", false)}
          <div style={{ marginTop: "12px" }}>
            <div style={{ marginBottom: "18px" }}>
              <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#2d3f50" }}>(A) Employees</div>
              <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <RadioButton
                    inputId="life-insurance-employees-yes"
                    value="Yes"
                    checked={principle3Leadership.q1_lifeInsurance.employees === "Yes"}
                    onChange={() => {
                      setPrinciple3Leadership((prev) => ({
                        ...prev,
                        q1_lifeInsurance: { ...prev.q1_lifeInsurance, employees: "Yes" },
                      }));
                    }}
                  />
                  <label htmlFor="life-insurance-employees-yes" style={{ cursor: "pointer", margin: 0 }}>
                    Yes
                  </label>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <RadioButton
                    inputId="life-insurance-employees-no"
                    value="No"
                    checked={principle3Leadership.q1_lifeInsurance.employees === "No"}
                    onChange={() => {
                      setPrinciple3Leadership((prev) => ({
                        ...prev,
                        q1_lifeInsurance: { ...prev.q1_lifeInsurance, employees: "No" },
                      }));
                    }}
                  />
                  <label htmlFor="life-insurance-employees-no" style={{ cursor: "pointer", margin: 0 }}>
                    No
                  </label>
                </div>
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "8px", color: "#2d3f50" }}>(B) Workers</div>
              <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <RadioButton
                    inputId="life-insurance-workers-yes"
                    value="Yes"
                    checked={principle3Leadership.q1_lifeInsurance.workers === "Yes"}
                    onChange={() => {
                      setPrinciple3Leadership((prev) => ({
                        ...prev,
                        q1_lifeInsurance: { ...prev.q1_lifeInsurance, workers: "Yes" },
                      }));
                    }}
                  />
                  <label htmlFor="life-insurance-workers-yes" style={{ cursor: "pointer", margin: 0 }}>
                    Yes
                  </label>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <RadioButton
                    inputId="life-insurance-workers-no"
                    value="No"
                    checked={principle3Leadership.q1_lifeInsurance.workers === "No"}
                    onChange={() => {
                      setPrinciple3Leadership((prev) => ({
                        ...prev,
                        q1_lifeInsurance: { ...prev.q1_lifeInsurance, workers: "No" },
                      }));
                    }}
                  />
                  <label htmlFor="life-insurance-workers-no" style={{ cursor: "pointer", margin: 0 }}>
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Question 2: Statutory dues */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Provide the measures undertaken by the entity to ensure that statutory dues have been deducted and deposited by the value chain partners.", false)}
          <InputTextarea
            value={principle3Leadership.q2_statutoryDues}
            onChange={(e) => {
              setPrinciple3Leadership((prev) => ({ ...prev, q2_statutoryDues: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 3: Rehabilitation */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Provide the number of employees / workers having suffered high consequence work related injury / ill-health / fatalities (as reported in Q11 of Essential Indicators above), who have been are rehabilitated and placed in suitable employment or whose family members have been placed in suitable employment:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2"></th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    Total no of affected employees/workers
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    No if employees/workers that are rehabilitated or whose family members have been placed in suitable employment
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">
                    FY {financialYears.currentFY}
                    <br />
                  </th>
                  <th className="section-a-dynamic-table-header">
                    FY {financialYears.previousFY}
                    <br />
                  </th>
                  <th className="section-a-dynamic-table-header">
                    FY {financialYears.currentFY}
                    <br />
                  </th>
                  <th className="section-a-dynamic-table-header">
                    FY {financialYears.previousFY}
                    <br />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Employees
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Leadership.q3_rehabilitation.employees.currentAffected}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Leadership((prev) => ({
                            ...prev,
                            q3_rehabilitation: {
                              ...prev.q3_rehabilitation,
                              employees: { ...prev.q3_rehabilitation.employees, currentAffected: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Leadership.q3_rehabilitation.employees.previousAffected}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Leadership((prev) => ({
                            ...prev,
                            q3_rehabilitation: {
                              ...prev.q3_rehabilitation,
                              employees: { ...prev.q3_rehabilitation.employees, previousAffected: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Leadership.q3_rehabilitation.employees.currentRehabilitated}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Leadership((prev) => ({
                            ...prev,
                            q3_rehabilitation: {
                              ...prev.q3_rehabilitation,
                              employees: { ...prev.q3_rehabilitation.employees, currentRehabilitated: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Leadership.q3_rehabilitation.employees.previousRehabilitated}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Leadership((prev) => ({
                            ...prev,
                            q3_rehabilitation: {
                              ...prev.q3_rehabilitation,
                              employees: { ...prev.q3_rehabilitation.employees, previousRehabilitated: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Workers
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Leadership.q3_rehabilitation.workers.currentAffected}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Leadership((prev) => ({
                            ...prev,
                            q3_rehabilitation: {
                              ...prev.q3_rehabilitation,
                              workers: { ...prev.q3_rehabilitation.workers, currentAffected: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Leadership.q3_rehabilitation.workers.previousAffected}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Leadership((prev) => ({
                            ...prev,
                            q3_rehabilitation: {
                              ...prev.q3_rehabilitation,
                              workers: { ...prev.q3_rehabilitation.workers, previousAffected: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Leadership.q3_rehabilitation.workers.currentRehabilitated}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Leadership((prev) => ({
                            ...prev,
                            q3_rehabilitation: {
                              ...prev.q3_rehabilitation,
                              workers: { ...prev.q3_rehabilitation.workers, currentRehabilitated: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle3Leadership.q3_rehabilitation.workers.previousRehabilitated}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateIntegersOnly(value)) {
                          setPrinciple3Leadership((prev) => ({
                            ...prev,
                            q3_rehabilitation: {
                              ...prev.q3_rehabilitation,
                              workers: { ...prev.q3_rehabilitation.workers, previousRehabilitated: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 4: Transition assistance */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Does the entity provide transition assistance programs to facilitate continued employability and the management of career endings resulting from retirement or termination of employment? (Yes/ No)", false)}
          <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <RadioButton
                inputId="transition-assistance-yes"
                value="Yes"
                checked={principle3Leadership.q4_transitionAssistance === "Yes"}
                onChange={(e) => {
                  setPrinciple3Leadership((prev) => ({ ...prev, q4_transitionAssistance: "Yes" }));
                }}
              />
              <label htmlFor="transition-assistance-yes" style={{ cursor: "pointer", margin: 0 }}>
                Yes
              </label>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <RadioButton
                inputId="transition-assistance-no"
                value="No"
                checked={principle3Leadership.q4_transitionAssistance === "No"}
                onChange={(e) => {
                  setPrinciple3Leadership((prev) => ({ ...prev, q4_transitionAssistance: "No" }));
                }}
              />
              <label htmlFor="transition-assistance-no" style={{ cursor: "pointer", margin: 0 }}>
                No
              </label>
            </div>
          </div>
        </div>

        {/* Question 5: Value chain assessment */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Details on assessment of value chain partners:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">% of value chain partners (by value of business done with such partners) that were assessed</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Health and Safety Practices
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div>
                      <InputText
                        value={principle3Leadership.q5_valueChainAssessment.healthSafety}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            setPrinciple3Leadership((prev) => ({
                              ...prev,
                              q5_valueChainAssessment: { ...prev.q5_valueChainAssessment, healthSafety: value },
                            }));
                          }
                        }}
                        className={`section-a-input ${principle3Leadership.q5_valueChainAssessment.healthSafety && (parseFloat(principle3Leadership.q5_valueChainAssessment.healthSafety) < 0 || parseFloat(principle3Leadership.q5_valueChainAssessment.healthSafety) > 100) ? "section-a-input-error" : ""}`}
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle3Leadership.q5_valueChainAssessment.healthSafety && (parseFloat(principle3Leadership.q5_valueChainAssessment.healthSafety) < 0 || parseFloat(principle3Leadership.q5_valueChainAssessment.healthSafety) > 100) && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Value needs to be between 0 and 100
                        </div>
                      )}
                      {principle3Leadership.q5_valueChainAssessment.healthSafety && parseFloat(principle3Leadership.q5_valueChainAssessment.healthSafety) >= 0 && parseFloat(principle3Leadership.q5_valueChainAssessment.healthSafety) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                    </div>
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    Working Conditions
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div>
                      <InputText
                        value={principle3Leadership.q5_valueChainAssessment.workingConditions}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validatePercentage(value)) {
                            setPrinciple3Leadership((prev) => ({
                              ...prev,
                              q5_valueChainAssessment: { ...prev.q5_valueChainAssessment, workingConditions: value },
                            }));
                          }
                        }}
                        className={`section-a-input ${principle3Leadership.q5_valueChainAssessment.workingConditions && (parseFloat(principle3Leadership.q5_valueChainAssessment.workingConditions) < 0 || parseFloat(principle3Leadership.q5_valueChainAssessment.workingConditions) > 100) ? "section-a-input-error" : ""}`}
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle3Leadership.q5_valueChainAssessment.workingConditions && (parseFloat(principle3Leadership.q5_valueChainAssessment.workingConditions) < 0 || parseFloat(principle3Leadership.q5_valueChainAssessment.workingConditions) > 100) && (
                        <div className="section-a-field-error" style={{ marginTop: "4px", fontSize: "12px", color: "#d32f2f" }}>
                          Value needs to be between 0 and 100
                        </div>
                      )}
                      {principle3Leadership.q5_valueChainAssessment.workingConditions && parseFloat(principle3Leadership.q5_valueChainAssessment.workingConditions) >= 0 && parseFloat(principle3Leadership.q5_valueChainAssessment.workingConditions) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 6: Value chain corrective action */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("6", "Provide details of any corrective actions taken or underway to address significant risks / concerns arising from assessments of health and safety practices and working conditions of value chain partners.", false)}
          <InputTextarea
            value={principle3Leadership.q6_valueChainCorrective}
            onChange={(e) => {
              setPrinciple3Leadership((prev) => ({ ...prev, q6_valueChainCorrective: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>
      </div>
    );
  };

  // Render Principle 4 Essential Questions
  const renderPrinciple4Essential = () => {
    return (
      <div>
        {/* Question 1: Processes for identifying key stakeholder groups */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Describe the processes for identifying key stakeholder groups of the entity.", true)}
          <InputTextarea
            value={principle4Essential.q1_identifyingStakeholders}
            onChange={(e) => {
              setPrinciple4Essential((prev) => ({ ...prev, q1_identifyingStakeholders: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter processes for identifying key stakeholder groups"
          />
        </div>

        {/* Question 2: Stakeholder groups and engagement frequency */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "List stakeholder groups identified as key for your entity and the frequency of engagement with each stakeholder group.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Stakeholder group</th>
                  <th className="section-a-dynamic-table-header">Whether identified as vulnerable & marginalised group (Yes/No)</th>
                  <th className="section-a-dynamic-table-header">Channels of communication (Email, SMS, Newspaper, Pamphlets, Advertisement, Community Meetings, Notice Board, Website), other</th>
                  <th className="section-a-dynamic-table-header">Frequency (Annually/ Half yearly/ Quarterly/ other - please specify)</th>
                  <th className="section-a-dynamic-table-header">Purpose and scope of engagement including key topics and concerns raised during such engagement</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle4Essential.q2_stakeholderGroups.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.stakeholderGroup}
                        onChange={(e) => {
                          const updated = [...principle4Essential.q2_stakeholderGroups];
                          updated[index].stakeholderGroup = e.target.value;
                          setPrinciple4Essential((prev) => ({ ...prev, q2_stakeholderGroups: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter stakeholder group"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={row.vulnerableMarginalised}
                        options={yesNoOptions}
                        onChange={(e) => {
                          const updated = [...principle4Essential.q2_stakeholderGroups];
                          updated[index].vulnerableMarginalised = e.value;
                          setPrinciple4Essential((prev) => ({ ...prev, q2_stakeholderGroups: updated }));
                        }}
                        placeholder="Select Yes/No"
                        className="section-a-input"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.channels}
                        onChange={(e) => {
                          const updated = [...principle4Essential.q2_stakeholderGroups];
                          updated[index].channels = e.target.value;
                          setPrinciple4Essential((prev) => ({ ...prev, q2_stakeholderGroups: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter channels of communication"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.frequency}
                        onChange={(e) => {
                          const updated = [...principle4Essential.q2_stakeholderGroups];
                          updated[index].frequency = e.target.value;
                          setPrinciple4Essential((prev) => ({ ...prev, q2_stakeholderGroups: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter frequency"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.purposeScope}
                        onChange={(e) => {
                          const updated = [...principle4Essential.q2_stakeholderGroups];
                          updated[index].purposeScope = e.target.value;
                          setPrinciple4Essential((prev) => ({ ...prev, q2_stakeholderGroups: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter purpose and scope of engagement"
                        rows={3}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      {principle4Essential.q2_stakeholderGroups.length > 1 && (
                        <Tooltip title="Delete" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete Icon"
                            className="delete-icon actions-icon"
                            onClick={() => {
                              const updated = principle4Essential.q2_stakeholderGroups.filter((_, i) => i !== index);
                              setPrinciple4Essential((prev) => ({ ...prev, q2_stakeholderGroups: updated }));
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple4Essential((prev) => ({
                    ...prev,
                    q2_stakeholderGroups: [...prev.q2_stakeholderGroups, { stakeholderGroup: "", vulnerableMarginalised: "", channels: "", frequency: "", purposeScope: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Principle 4 Leadership Questions
  const renderPrinciple4Leadership = () => {
    return (
      <div>
        {/* Question 1: Processes for consultation between stakeholders and Board */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Provide the processes for consultation between stakeholders and the Board on economic, environmental, and social topics or if consultation is delegated, how is feedback from such consultations provided to the Board.", false)}
          <InputTextarea
            value={principle4Leadership.q1_consultationProcesses}
            onChange={(e) => {
              setPrinciple4Leadership((prev) => ({ ...prev, q1_consultationProcesses: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter processes for consultation"
          />
        </div>

        {/* Question 2: Stakeholder consultation for environmental and social topics */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Whether stakeholder consultation is used to support the identification and management of environmental, and social topics (Yes / No). If so, provide details of instances as to how the inputs received from stakeholders on these topics were incorporated into policies and activities of the entity.", false)}
          <div style={{ marginBottom: "15px" }}>
            <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "15px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="stakeholder-consultation-yes"
                  value="Yes"
                  checked={principle4Leadership.q2_stakeholderConsultation.isUsed === "Yes"}
                  onChange={(e) => {
                    setPrinciple4Leadership((prev) => ({
                      ...prev,
                      q2_stakeholderConsultation: { ...prev.q2_stakeholderConsultation, isUsed: "Yes" },
                    }));
                  }}
                />
                <label htmlFor="stakeholder-consultation-yes" style={{ cursor: "pointer", margin: 0 }}>
                  Yes
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="stakeholder-consultation-no"
                  value="No"
                  checked={principle4Leadership.q2_stakeholderConsultation.isUsed === "No"}
                  onChange={(e) => {
                    setPrinciple4Leadership((prev) => ({
                      ...prev,
                      q2_stakeholderConsultation: { ...prev.q2_stakeholderConsultation, isUsed: "No", details: "" },
                    }));
                  }}
                />
                <label htmlFor="stakeholder-consultation-no" style={{ cursor: "pointer", margin: 0 }}>
                  No
                </label>
              </div>
            </div>
            {principle4Leadership.q2_stakeholderConsultation.isUsed === "Yes" && (
              <InputTextarea
                value={principle4Leadership.q2_stakeholderConsultation.details}
                onChange={(e) => {
                  setPrinciple4Leadership((prev) => ({
                    ...prev,
                    q2_stakeholderConsultation: { ...prev.q2_stakeholderConsultation, details: e.target.value },
                  }));
                }}
                className="section-a-input"
                rows={4}
                style={{ width: "100%", resize: "vertical" }}
                placeholder="Enter details of instances"
              />
            )}
          </div>
        </div>

        {/* Question 3: Engagement with vulnerable/marginalized groups */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Provide details of instances of engagement with, and actions taken to, address the concerns of vulnerable/ marginalized stakeholder groups.", false)}
          <InputTextarea
            value={principle4Leadership.q3_vulnerableEngagement}
            onChange={(e) => {
              setPrinciple4Leadership((prev) => ({ ...prev, q3_vulnerableEngagement: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details of engagement and actions taken"
          />
        </div>
      </div>
    );
  };

  // Render Principle 5 Essential Questions
  const renderPrinciple5Essential = () => {
    // Helper function to calculate percentage
    const calculatePercentage = (numerator, denominator) => {
      if (!denominator || parseFloat(denominator) === 0) return "";
      const result = (parseFloat(numerator) / parseFloat(denominator)) * 100;
      return isNaN(result) ? "" : result.toFixed(2);
    };

    // Helper function to calculate total for employees/workers
    const calculateTotal = (permanent, otherThanPermanent) => {
      const perm = parseFloat(permanent) || 0;
      const other = parseFloat(otherThanPermanent) || 0;
      return perm + other;
    };

    return (
      <div>
        {/* Question 1: Training on human rights */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Employees and workers who have been provided training on human rights issues and policy(ies) of the entity, in the following format:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2">
                    Category
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.currentFY}
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.previousFY}
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Total (A)</th>
                  <th className="section-a-dynamic-table-header">No. of employees / workers covered (B)</th>
                  <th className="section-a-dynamic-table-header">% (B/A)</th>
                  <th className="section-a-dynamic-table-header">Total (C)</th>
                  <th className="section-a-dynamic-table-header">No. of employees / workers covered (D)</th>
                  <th className="section-a-dynamic-table-header">% (D/C)</th>
                </tr>
              </thead>
              <tbody>
                {/* Employees Section */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-header" colSpan="7" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                    Employees
                  </td>
                </tr>
                {/* Permanent Employees */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">Permanent</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              currentFY: {
                                ...prev.q1_humanRightsTraining.currentFY,
                                employees: {
                                  ...prev.q1_humanRightsTraining.currentFY.employees,
                                  permanent: { ...prev.q1_humanRightsTraining.currentFY.employees.permanent, total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.covered}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              currentFY: {
                                ...prev.q1_humanRightsTraining.currentFY,
                                employees: {
                                  ...prev.q1_humanRightsTraining.currentFY.employees,
                                  permanent: { ...prev.q1_humanRightsTraining.currentFY.employees.permanent, covered: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              previousFY: {
                                ...prev.q1_humanRightsTraining.previousFY,
                                employees: {
                                  ...prev.q1_humanRightsTraining.previousFY.employees,
                                  permanent: { ...prev.q1_humanRightsTraining.previousFY.employees.permanent, total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.covered}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              previousFY: {
                                ...prev.q1_humanRightsTraining.previousFY,
                                employees: {
                                  ...prev.q1_humanRightsTraining.previousFY.employees,
                                  permanent: { ...prev.q1_humanRightsTraining.previousFY.employees.permanent, covered: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Other than Permanent Employees */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">Other than permanent</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              currentFY: {
                                ...prev.q1_humanRightsTraining.currentFY,
                                employees: {
                                  ...prev.q1_humanRightsTraining.currentFY.employees,
                                  otherThanPermanent: { ...prev.q1_humanRightsTraining.currentFY.employees.otherThanPermanent, total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.covered}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              currentFY: {
                                ...prev.q1_humanRightsTraining.currentFY,
                                employees: {
                                  ...prev.q1_humanRightsTraining.currentFY.employees,
                                  otherThanPermanent: { ...prev.q1_humanRightsTraining.currentFY.employees.otherThanPermanent, covered: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              previousFY: {
                                ...prev.q1_humanRightsTraining.previousFY,
                                employees: {
                                  ...prev.q1_humanRightsTraining.previousFY.employees,
                                  otherThanPermanent: { ...prev.q1_humanRightsTraining.previousFY.employees.otherThanPermanent, total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.covered}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              previousFY: {
                                ...prev.q1_humanRightsTraining.previousFY,
                                employees: {
                                  ...prev.q1_humanRightsTraining.previousFY.employees,
                                  otherThanPermanent: { ...prev.q1_humanRightsTraining.previousFY.employees.otherThanPermanent, covered: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Total Employees */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                    Total Employees
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    {calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.total, principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.total) || ""}
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    {calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.covered) || ""}
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.covered), calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.total, principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.total)) && (
                        <>
                          {calculatePercentage(calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.covered), calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.employees.permanent.total, principle5Essential.q1_humanRightsTraining.currentFY.employees.otherThanPermanent.total))}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    {calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.total, principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.total) || ""}
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    {calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.covered) || ""}
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.covered), calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.total, principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.total)) && (
                        <>
                          {calculatePercentage(calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.covered), calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.employees.permanent.total, principle5Essential.q1_humanRightsTraining.previousFY.employees.otherThanPermanent.total))}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Workers Section */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-header" colSpan="7" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                    Workers
                  </td>
                </tr>
                {/* Permanent Workers */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">Permanent</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              currentFY: {
                                ...prev.q1_humanRightsTraining.currentFY,
                                workers: {
                                  ...prev.q1_humanRightsTraining.currentFY.workers,
                                  permanent: { ...prev.q1_humanRightsTraining.currentFY.workers.permanent, total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.covered}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              currentFY: {
                                ...prev.q1_humanRightsTraining.currentFY,
                                workers: {
                                  ...prev.q1_humanRightsTraining.currentFY.workers,
                                  permanent: { ...prev.q1_humanRightsTraining.currentFY.workers.permanent, covered: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              previousFY: {
                                ...prev.q1_humanRightsTraining.previousFY,
                                workers: {
                                  ...prev.q1_humanRightsTraining.previousFY.workers,
                                  permanent: { ...prev.q1_humanRightsTraining.previousFY.workers.permanent, total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.covered}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              previousFY: {
                                ...prev.q1_humanRightsTraining.previousFY,
                                workers: {
                                  ...prev.q1_humanRightsTraining.previousFY.workers,
                                  permanent: { ...prev.q1_humanRightsTraining.previousFY.workers.permanent, covered: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Other than Permanent Workers */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">Other than permanent</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              currentFY: {
                                ...prev.q1_humanRightsTraining.currentFY,
                                workers: {
                                  ...prev.q1_humanRightsTraining.currentFY.workers,
                                  otherThanPermanent: { ...prev.q1_humanRightsTraining.currentFY.workers.otherThanPermanent, total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.covered}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              currentFY: {
                                ...prev.q1_humanRightsTraining.currentFY,
                                workers: {
                                  ...prev.q1_humanRightsTraining.currentFY.workers,
                                  otherThanPermanent: { ...prev.q1_humanRightsTraining.currentFY.workers.otherThanPermanent, covered: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              previousFY: {
                                ...prev.q1_humanRightsTraining.previousFY,
                                workers: {
                                  ...prev.q1_humanRightsTraining.previousFY.workers,
                                  otherThanPermanent: { ...prev.q1_humanRightsTraining.previousFY.workers.otherThanPermanent, total: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.covered}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q1_humanRightsTraining: {
                              ...prev.q1_humanRightsTraining,
                              previousFY: {
                                ...prev.q1_humanRightsTraining.previousFY,
                                workers: {
                                  ...prev.q1_humanRightsTraining.previousFY.workers,
                                  otherThanPermanent: { ...prev.q1_humanRightsTraining.previousFY.workers.otherThanPermanent, covered: value },
                                },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Total Workers */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                    Total Workers
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    {calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.total, principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.total) || ""}
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    {calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.covered) || ""}
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.covered), calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.total, principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.total)) && (
                        <>
                          {calculatePercentage(calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.covered), calculateTotal(principle5Essential.q1_humanRightsTraining.currentFY.workers.permanent.total, principle5Essential.q1_humanRightsTraining.currentFY.workers.otherThanPermanent.total))}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    {calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.total, principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.total) || ""}
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    {calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.covered) || ""}
                  </td>
                  <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.covered), calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.total, principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.total)) && (
                        <>
                          {calculatePercentage(calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.covered, principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.covered), calculateTotal(principle5Essential.q1_humanRightsTraining.previousFY.workers.permanent.total, principle5Essential.q1_humanRightsTraining.previousFY.workers.otherThanPermanent.total))}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 2: Minimum wages */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Details of minimum wages paid to employees and workers, in the following format:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2">
                    Category
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.currentFY}
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.previousFY}
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Total (A)</th>
                  <th className="section-a-dynamic-table-header">No. (B)</th>
                  <th className="section-a-dynamic-table-header">% (B/A)</th>
                  <th className="section-a-dynamic-table-header">Total (D)</th>
                  <th className="section-a-dynamic-table-header">No. (E)</th>
                  <th className="section-a-dynamic-table-header">% (E/D)</th>
                </tr>
              </thead>
              <tbody>
                {/* Employees Section */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-header" colSpan="7" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                    Employees
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">Below minimum wages</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.employees.currentFY.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              employees: { ...prev.q2_minimumWages.employees, currentFY: { ...prev.q2_minimumWages.employees.currentFY, total: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.employees.currentFY.belowMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              employees: { ...prev.q2_minimumWages.employees, currentFY: { ...prev.q2_minimumWages.employees.currentFY, belowMin: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q2_minimumWages.employees.currentFY.belowMin, principle5Essential.q2_minimumWages.employees.currentFY.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q2_minimumWages.employees.currentFY.belowMin, principle5Essential.q2_minimumWages.employees.currentFY.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.employees.previousFY.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              employees: { ...prev.q2_minimumWages.employees, previousFY: { ...prev.q2_minimumWages.employees.previousFY, total: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.employees.previousFY.belowMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              employees: { ...prev.q2_minimumWages.employees, previousFY: { ...prev.q2_minimumWages.employees.previousFY, belowMin: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q2_minimumWages.employees.previousFY.belowMin, principle5Essential.q2_minimumWages.employees.previousFY.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q2_minimumWages.employees.previousFY.belowMin, principle5Essential.q2_minimumWages.employees.previousFY.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">At minimum wages</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.employees.currentFY.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              employees: { ...prev.q2_minimumWages.employees, currentFY: { ...prev.q2_minimumWages.employees.currentFY, total: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.employees.currentFY.atMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              employees: { ...prev.q2_minimumWages.employees, currentFY: { ...prev.q2_minimumWages.employees.currentFY, atMin: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q2_minimumWages.employees.currentFY.atMin, principle5Essential.q2_minimumWages.employees.currentFY.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q2_minimumWages.employees.currentFY.atMin, principle5Essential.q2_minimumWages.employees.currentFY.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.employees.previousFY.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              employees: { ...prev.q2_minimumWages.employees, previousFY: { ...prev.q2_minimumWages.employees.previousFY, total: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.employees.previousFY.atMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              employees: { ...prev.q2_minimumWages.employees, previousFY: { ...prev.q2_minimumWages.employees.previousFY, atMin: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q2_minimumWages.employees.previousFY.atMin, principle5Essential.q2_minimumWages.employees.previousFY.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q2_minimumWages.employees.previousFY.atMin, principle5Essential.q2_minimumWages.employees.previousFY.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Workers Section */}
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-header" colSpan="7" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                    Workers
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">Below minimum wages</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.workers.currentFY.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              workers: { ...prev.q2_minimumWages.workers, currentFY: { ...prev.q2_minimumWages.workers.currentFY, total: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.workers.currentFY.belowMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              workers: { ...prev.q2_minimumWages.workers, currentFY: { ...prev.q2_minimumWages.workers.currentFY, belowMin: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q2_minimumWages.workers.currentFY.belowMin, principle5Essential.q2_minimumWages.workers.currentFY.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q2_minimumWages.workers.currentFY.belowMin, principle5Essential.q2_minimumWages.workers.currentFY.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.workers.previousFY.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              workers: { ...prev.q2_minimumWages.workers, previousFY: { ...prev.q2_minimumWages.workers.previousFY, total: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.workers.previousFY.belowMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              workers: { ...prev.q2_minimumWages.workers, previousFY: { ...prev.q2_minimumWages.workers.previousFY, belowMin: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q2_minimumWages.workers.previousFY.belowMin, principle5Essential.q2_minimumWages.workers.previousFY.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q2_minimumWages.workers.previousFY.belowMin, principle5Essential.q2_minimumWages.workers.previousFY.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">At minimum wages</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.workers.currentFY.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              workers: { ...prev.q2_minimumWages.workers, currentFY: { ...prev.q2_minimumWages.workers.currentFY, total: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.workers.currentFY.atMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              workers: { ...prev.q2_minimumWages.workers, currentFY: { ...prev.q2_minimumWages.workers.currentFY, atMin: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q2_minimumWages.workers.currentFY.atMin, principle5Essential.q2_minimumWages.workers.currentFY.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q2_minimumWages.workers.currentFY.atMin, principle5Essential.q2_minimumWages.workers.currentFY.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.workers.previousFY.total}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              workers: { ...prev.q2_minimumWages.workers, previousFY: { ...prev.q2_minimumWages.workers.previousFY, total: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle5Essential.q2_minimumWages.workers.previousFY.atMin}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q2_minimumWages: {
                              ...prev.q2_minimumWages,
                              workers: { ...prev.q2_minimumWages.workers, previousFY: { ...prev.q2_minimumWages.workers.previousFY, atMin: value } },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {calculatePercentage(principle5Essential.q2_minimumWages.workers.previousFY.atMin, principle5Essential.q2_minimumWages.workers.previousFY.total) && (
                        <>
                          {calculatePercentage(principle5Essential.q2_minimumWages.workers.previousFY.atMin, principle5Essential.q2_minimumWages.workers.previousFY.total)}
                          <span style={{ marginLeft: "5px" }}>%</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 3a: Median remuneration */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3a", "Details of remuneration/salary/wages - a. Median remuneration / wages:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2"></th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    Male
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="2">
                    Female
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Number</th>
                  <th className="section-a-dynamic-table-header">Median remuneration/ salary/ wages of respective category</th>
                  <th className="section-a-dynamic-table-header">Number</th>
                  <th className="section-a-dynamic-table-header">Median remuneration/ salary/ wages of respective category</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "bod", label: "Board of Directors (BoD)" },
                  { key: "kmp", label: "Key Managerial Personnel" },
                  { key: "employees", label: "Employees other than BoD and KMP" },
                  { key: "workers", label: "Workers" },
                ].map((row) => (
                  <tr key={row.key} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">{row.label}</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q3a_medianRemuneration[row.key].maleNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q3a_medianRemuneration: {
                                ...prev.q3a_medianRemuneration,
                                [row.key]: { ...prev.q3a_medianRemuneration[row.key], maleNumber: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q3a_medianRemuneration[row.key].maleMedian}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q3a_medianRemuneration: {
                                ...prev.q3a_medianRemuneration,
                                [row.key]: { ...prev.q3a_medianRemuneration[row.key], maleMedian: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter amount"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q3a_medianRemuneration[row.key].femaleNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q3a_medianRemuneration: {
                                ...prev.q3a_medianRemuneration,
                                [row.key]: { ...prev.q3a_medianRemuneration[row.key], femaleNumber: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q3a_medianRemuneration[row.key].femaleMedian}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q3a_medianRemuneration: {
                                ...prev.q3a_medianRemuneration,
                                [row.key]: { ...prev.q3a_medianRemuneration[row.key], femaleMedian: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter amount"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 3b (BRSR Core): Gross wages paid to females as %} */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "3b",
              "Gross wages paid to females as % of total wages paid by the entity, in the following format:",
              true,
              'Current financial year and Previous financial year:\n\nThe term "wages" has been used for both employees and workers (Reference: Principle 5 Essential Question 2).\n\nTherefore, the total wages should include:\n1. Salaries, wages and bonus as per the disclosure made in the audited financial statements (for BFSI, this will need to be extracted from "Payments to and provisions for employees" in the audited financial statements). The same would exclude retirement benefits, ESOPs and staff welfare expenses. Bonus accrued but not paid may be apportioned between male and female staff using an appropriate basis and the basis should be disclosed.\n\n2. For other than permanent employees/workers, actual wages paid to non-permanent employees/workers to be considered.',
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header"></th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Gross wages paid to females as % of total wages</td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <InputText
                          value={principle5Essential.q3b_grossWagesFemales.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple5Essential((prev) => ({
                                ...prev,
                                q3b_grossWagesFemales: { ...prev.q3b_grossWagesFemales, current: value },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle5Essential.q3b_grossWagesFemales.current && (parseFloat(principle5Essential.q3b_grossWagesFemales.current) < 0 || parseFloat(principle5Essential.q3b_grossWagesFemales.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="Enter percentage (0-100)"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle5Essential.q3b_grossWagesFemales.current && parseFloat(principle5Essential.q3b_grossWagesFemales.current) >= 0 && parseFloat(principle5Essential.q3b_grossWagesFemales.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                      {principle5Essential.q3b_grossWagesFemales.current && (parseFloat(principle5Essential.q3b_grossWagesFemales.current) < 0 || parseFloat(principle5Essential.q3b_grossWagesFemales.current) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <InputText
                          value={principle5Essential.q3b_grossWagesFemales.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple5Essential((prev) => ({
                                ...prev,
                                q3b_grossWagesFemales: { ...prev.q3b_grossWagesFemales, previous: value },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle5Essential.q3b_grossWagesFemales.previous && (parseFloat(principle5Essential.q3b_grossWagesFemales.previous) < 0 || parseFloat(principle5Essential.q3b_grossWagesFemales.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="Enter percentage (0-100)"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle5Essential.q3b_grossWagesFemales.previous && parseFloat(principle5Essential.q3b_grossWagesFemales.previous) >= 0 && parseFloat(principle5Essential.q3b_grossWagesFemales.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                      {principle5Essential.q3b_grossWagesFemales.previous && (parseFloat(principle5Essential.q3b_grossWagesFemales.previous) < 0 || parseFloat(principle5Essential.q3b_grossWagesFemales.previous) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Question 4: Focal point */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Do you have a focal point (Individual/ Committee) responsible for addressing human rights impacts or issues caused or contributed to by the business? (Yes/No)", true)}
          <div style={{ marginBottom: "15px" }}>
            <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="focal-point-yes"
                  value="Yes"
                  checked={principle5Essential.q4_focalPoint === "Yes"}
                  onChange={(e) => {
                    setPrinciple5Essential((prev) => ({ ...prev, q4_focalPoint: "Yes" }));
                  }}
                />
                <label htmlFor="focal-point-yes" style={{ cursor: "pointer", margin: 0 }}>
                  Yes
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="focal-point-no"
                  value="No"
                  checked={principle5Essential.q4_focalPoint === "No"}
                  onChange={(e) => {
                    setPrinciple5Essential((prev) => ({ ...prev, q4_focalPoint: "No", q4_focalPointDetails: "" }));
                  }}
                />
                <label htmlFor="focal-point-no" style={{ cursor: "pointer", margin: 0 }}>
                  No
                </label>
              </div>
            </div>
          </div>
          {principle5Essential.q4_focalPoint === "Yes" && (
            <InputTextarea
              value={principle5Essential.q4_focalPointDetails}
              onChange={(e) => {
                setPrinciple5Essential((prev) => ({ ...prev, q4_focalPointDetails: e.target.value }));
              }}
              className="section-a-input"
              rows={4}
              style={{ width: "100%", resize: "vertical" }}
              placeholder="Provide additional details (e.g. individual or committee name, scope)"
            />
          )}
        </div>

        {/* Question 5: Internal mechanisms */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Describe the internal mechanisms in place to redress grievances related to human rights issues.", true)}
          <InputTextarea
            value={principle5Essential.q5_internalMechanisms}
            onChange={(e) => {
              setPrinciple5Essential((prev) => ({ ...prev, q5_internalMechanisms: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details of internal mechanisms"
          />
        </div>

        {/* Question 6: Number of complaints */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("6", "Number of Complaints on the following made by employees and workers:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" rowSpan="2"></th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.currentFY}
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3">
                    FY {financialYears.previousFY}
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header">Filed during the year</th>
                  <th className="section-a-dynamic-table-header">Pending resolution at the end of year</th>
                  <th className="section-a-dynamic-table-header">Remarks</th>
                  <th className="section-a-dynamic-table-header">Filed during the year</th>
                  <th className="section-a-dynamic-table-header">Pending resolution at the end of year</th>
                  <th className="section-a-dynamic-table-header">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "sexualHarassment", label: "Sexual Harassment" },
                  { key: "discrimination", label: "Discrimination at workplace" },
                  { key: "childLabour", label: "Child Labour" },
                  { key: "forcedLabour", label: "Forced Labour/Involuntary Labour" },
                  { key: "wages", label: "Wages" },
                  { key: "otherHumanRights", label: "Other human rights related issues" },
                ].map((row) => (
                  <tr key={row.key} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">{row.label}</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q6_complaints[row.key].currentFiled}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q6_complaints: {
                                ...prev.q6_complaints,
                                [row.key]: { ...prev.q6_complaints[row.key], currentFiled: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q6_complaints[row.key].currentPending}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q6_complaints: {
                                ...prev.q6_complaints,
                                [row.key]: { ...prev.q6_complaints[row.key], currentPending: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q6_complaints[row.key].currentRemarks}
                        onChange={(e) => {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q6_complaints: {
                              ...prev.q6_complaints,
                              [row.key]: { ...prev.q6_complaints[row.key], currentRemarks: e.target.value },
                            },
                          }));
                        }}
                        className="section-a-input"
                        placeholder="Enter remarks"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q6_complaints[row.key].previousFiled}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q6_complaints: {
                                ...prev.q6_complaints,
                                [row.key]: { ...prev.q6_complaints[row.key], previousFiled: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q6_complaints[row.key].previousPending}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q6_complaints: {
                                ...prev.q6_complaints,
                                [row.key]: { ...prev.q6_complaints[row.key], previousPending: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q6_complaints[row.key].previousRemarks}
                        onChange={(e) => {
                          setPrinciple5Essential((prev) => ({
                            ...prev,
                            q6_complaints: {
                              ...prev.q6_complaints,
                              [row.key]: { ...prev.q6_complaints[row.key], previousRemarks: e.target.value },
                            },
                          }));
                        }}
                        className="section-a-input"
                        placeholder="Enter remarks"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 7 (BRSR Core): POSH complaints */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "7",
              "Complaints filed under the Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013, in the following format:",
              true,
              "Complaints on POSH as a % of female employees / workers:\nDenominator should be considered as average of number of female employees/workers at the beginning of the year and as at end of the year\n\nComplaints on POSH upheld:\nComplaints on POSH upheld shall mean the complaints regarding which the Internal Committee in its Inquiry Report has arrived at the conclusion that the allegation has been proved, in accordance with Section 13 of The Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013.",
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header"></th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Total complaints reported under sexual harassment of women at workplace</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q7_poshComplaints.totalComplaints.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q7_poshComplaints: {
                                ...prev.q7_poshComplaints,
                                totalComplaints: { ...prev.q7_poshComplaints.totalComplaints, current: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q7_poshComplaints.totalComplaints.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q7_poshComplaints: {
                                ...prev.q7_poshComplaints,
                                totalComplaints: { ...prev.q7_poshComplaints.totalComplaints, previous: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Complaints on POSH as a % of female employees / workers</td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <InputText
                          value={principle5Essential.q7_poshComplaints.percentageOfFemales.current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple5Essential((prev) => ({
                                ...prev,
                                q7_poshComplaints: {
                                  ...prev.q7_poshComplaints,
                                  percentageOfFemales: { ...prev.q7_poshComplaints.percentageOfFemales, current: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle5Essential.q7_poshComplaints.percentageOfFemales.current && (parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.current) < 0 || parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.current) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="Enter percentage (0-100)"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle5Essential.q7_poshComplaints.percentageOfFemales.current && parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.current) >= 0 && parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.current) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                      {principle5Essential.q7_poshComplaints.percentageOfFemales.current && (parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.current) < 0 || parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.current) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <InputText
                          value={principle5Essential.q7_poshComplaints.percentageOfFemales.previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple5Essential((prev) => ({
                                ...prev,
                                q7_poshComplaints: {
                                  ...prev.q7_poshComplaints,
                                  percentageOfFemales: { ...prev.q7_poshComplaints.percentageOfFemales, previous: value },
                                },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle5Essential.q7_poshComplaints.percentageOfFemales.previous && (parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.previous) < 0 || parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.previous) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="Enter percentage (0-100)"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle5Essential.q7_poshComplaints.percentageOfFemales.previous && parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.previous) >= 0 && parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.previous) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                      {principle5Essential.q7_poshComplaints.percentageOfFemales.previous && (parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.previous) < 0 || parseFloat(principle5Essential.q7_poshComplaints.percentageOfFemales.previous) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Complaints on POSH upheld</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q7_poshComplaints.upheld.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q7_poshComplaints: {
                                ...prev.q7_poshComplaints,
                                upheld: { ...prev.q7_poshComplaints.upheld, current: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle5Essential.q7_poshComplaints.upheld.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple5Essential((prev) => ({
                              ...prev,
                              q7_poshComplaints: {
                                ...prev.q7_poshComplaints,
                                upheld: { ...prev.q7_poshComplaints.upheld, previous: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Question 8: Mechanisms to prevent adverse consequences */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("8", "Mechanisms to prevent adverse consequences to the complainant in discrimination and harassment cases.", true)}
          <InputTextarea
            value={principle5Essential.q8_preventAdverse}
            onChange={(e) => {
              setPrinciple5Essential((prev) => ({ ...prev, q8_preventAdverse: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter mechanisms"
          />
        </div>

        {/* Question 9: Human rights in business agreements */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("9", "Do human rights requirements form part of your business agreements and contracts? (Yes/No)", true)}
          <div style={{ marginBottom: "15px" }}>
            <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="business-agreements-yes"
                  value="Yes"
                  checked={principle5Essential.q9_businessAgreements === "Yes"}
                  onChange={(e) => {
                    setPrinciple5Essential((prev) => ({ ...prev, q9_businessAgreements: "Yes" }));
                  }}
                />
                <label htmlFor="business-agreements-yes" style={{ cursor: "pointer", margin: 0 }}>
                  Yes
                </label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  inputId="business-agreements-no"
                  value="No"
                  checked={principle5Essential.q9_businessAgreements === "No"}
                  onChange={(e) => {
                    setPrinciple5Essential((prev) => ({ ...prev, q9_businessAgreements: "No", q9_businessAgreementsDetails: "" }));
                  }}
                />
                <label htmlFor="business-agreements-no" style={{ cursor: "pointer", margin: 0 }}>
                  No
                </label>
              </div>
            </div>
          </div>
          {principle5Essential.q9_businessAgreements === "Yes" && (
            <InputTextarea
              value={principle5Essential.q9_businessAgreementsDetails}
              onChange={(e) => {
                setPrinciple5Essential((prev) => ({ ...prev, q9_businessAgreementsDetails: e.target.value }));
              }}
              className="section-a-input"
              rows={4}
              style={{ width: "100%", resize: "vertical" }}
              placeholder="Provide additional details on how human rights requirements are reflected in agreements"
            />
          )}
        </div>

        {/* Question 10: Assessments for the year */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("10", "Assessments for the year:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">% of your plants and offices that were assessed (by entity or statutory authorities or third parties)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "childLabour", label: "Child labour" },
                  { key: "forcedLabour", label: "Forced/involuntary labour" },
                  { key: "sexualHarassment", label: "Sexual harassment" },
                  { key: "discrimination", label: "Discrimination at workplace" },
                  { key: "wages", label: "Wages" },
                  { key: "others", label: "Others – please specify" },
                ].map((row) => (
                  <tr key={row.key} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">{row.label}</td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <InputText
                          value={principle5Essential.q10_assessments[row.key]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple5Essential((prev) => ({
                                ...prev,
                                q10_assessments: { ...prev.q10_assessments, [row.key]: value },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle5Essential.q10_assessments[row.key] && (parseFloat(principle5Essential.q10_assessments[row.key]) < 0 || parseFloat(principle5Essential.q10_assessments[row.key]) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="Enter percentage (0-100)"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle5Essential.q10_assessments[row.key] && parseFloat(principle5Essential.q10_assessments[row.key]) >= 0 && parseFloat(principle5Essential.q10_assessments[row.key]) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                      {principle5Essential.q10_assessments[row.key] && (parseFloat(principle5Essential.q10_assessments[row.key]) < 0 || parseFloat(principle5Essential.q10_assessments[row.key]) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 11: Corrective actions */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("11", "Provide details of any corrective actions taken or underway to address significant risks / concerns arising from the assessments at Question 10 above.", true)}
          <InputTextarea
            value={principle5Essential.q11_correctiveActions}
            onChange={(e) => {
              setPrinciple5Essential((prev) => ({ ...prev, q11_correctiveActions: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details of corrective actions"
          />
        </div>
      </div>
    );
  };

  // Render Principle 5 Leadership Questions
  const renderPrinciple5Leadership = () => {
    return (
      <div>
        {/* Question 1: Business process modifications */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Details of a business process being modified / introduced as a result of addressing human rights grievances/complaints.", false)}
          <InputTextarea
            value={principle5Leadership.q1_businessProcessModifications}
            onChange={(e) => {
              setPrinciple5Leadership((prev) => ({ ...prev, q1_businessProcessModifications: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details of business process modifications"
          />
        </div>

        {/* Question 2: Human rights due-diligence scope */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Details of the scope and coverage of any Human rights due-diligence conducted.", false)}
          <InputTextarea
            value={principle5Leadership.q2_dueDiligenceScope}
            onChange={(e) => {
              setPrinciple5Leadership((prev) => ({ ...prev, q2_dueDiligenceScope: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details of scope and coverage"
          />
        </div>

        {/* Question 3: Accessibility for differently abled visitors */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Is the premise/office of the entity accessible to differently abled visitors, as per the requirements of the Rights of Persons with Disabilities Act, 2016?", false)}
          <InputTextarea
            value={principle5Leadership.q3_accessibilityVisitors}
            onChange={(e) => {
              setPrinciple5Leadership((prev) => ({ ...prev, q3_accessibilityVisitors: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 4: Assessment of value chain partners */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Details on assessment of value chain partners:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">% of value chain partners (by value of business done with such partners) that were assessed</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "sexualHarassment", label: "Sexual Harassment" },
                  { key: "discrimination", label: "Discrimination at workplace" },
                  { key: "childLabour", label: "Child Labour" },
                  { key: "forcedLabour", label: "Forced Labour/Involuntary Labour" },
                  { key: "wages", label: "Wages" },
                  { key: "others", label: "Others - please specify" },
                ].map((row) => (
                  <tr key={row.key} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">{row.label}</td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <InputText
                          value={principle5Leadership.q4_valueChainAssessment[row.key]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validatePercentage(value)) {
                              setPrinciple5Leadership((prev) => ({
                                ...prev,
                                q4_valueChainAssessment: { ...prev.q4_valueChainAssessment, [row.key]: value },
                              }));
                            }
                          }}
                          className={`section-a-input ${principle5Leadership.q4_valueChainAssessment[row.key] && (parseFloat(principle5Leadership.q4_valueChainAssessment[row.key]) < 0 || parseFloat(principle5Leadership.q4_valueChainAssessment[row.key]) > 100) ? "section-a-input-error" : ""}`}
                          placeholder="Enter percentage (0-100)"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle5Leadership.q4_valueChainAssessment[row.key] && parseFloat(principle5Leadership.q4_valueChainAssessment[row.key]) >= 0 && parseFloat(principle5Leadership.q4_valueChainAssessment[row.key]) <= 100 && <span style={{ marginLeft: "5px" }}>%</span>}
                      </div>
                      {principle5Leadership.q4_valueChainAssessment[row.key] && (parseFloat(principle5Leadership.q4_valueChainAssessment[row.key]) < 0 || parseFloat(principle5Leadership.q4_valueChainAssessment[row.key]) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 5: Corrective actions for assessments */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Provide details of any corrective actions taken or underway to address significant risks / concerns arising from the assessments at Question 4 above.", false)}
          <InputTextarea
            value={principle5Leadership.q5_correctiveActions}
            onChange={(e) => {
              setPrinciple5Leadership((prev) => ({ ...prev, q5_correctiveActions: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details of corrective actions"
          />
        </div>
      </div>
    );
  };

  // Render Principle 6 Essential Questions
  const renderPrinciple6Essential = () => {
    // Helper function to calculate total
    const calculateTotal = (...values) => {
      const sum = values.reduce((acc, val) => {
        const num = parseFloat(val) || 0;
        return acc + num;
      }, 0);
      return sum >= 0 ? sum.toFixed(2) : "";
    };

    const step1Data = (() => {
      try {
        return JSON.parse(localStorage.getItem("step1") || "{}");
      } catch (e) {
        return {};
      }
    })();

    const renewableCurrentTotal = calculateTotal(principle6Essential.q1_energyConsumption.renewable.electricity.current, principle6Essential.q1_energyConsumption.renewable.fuel.current, principle6Essential.q1_energyConsumption.renewable.other.current);
    const renewablePreviousTotal = calculateTotal(principle6Essential.q1_energyConsumption.renewable.electricity.previous, principle6Essential.q1_energyConsumption.renewable.fuel.previous, principle6Essential.q1_energyConsumption.renewable.other.previous);

    const nonRenewableCurrentTotal = calculateTotal(principle6Essential.q1_energyConsumption.nonRenewable.electricity.current, principle6Essential.q1_energyConsumption.nonRenewable.fuel.current, principle6Essential.q1_energyConsumption.nonRenewable.other.current);
    const nonRenewablePreviousTotal = calculateTotal(principle6Essential.q1_energyConsumption.nonRenewable.electricity.previous, principle6Essential.q1_energyConsumption.nonRenewable.fuel.previous, principle6Essential.q1_energyConsumption.nonRenewable.other.previous);

    const totalEnergyCurrent = calculateTotal(principle6Essential.q1_energyConsumption.renewable.electricity.current, principle6Essential.q1_energyConsumption.renewable.fuel.current, principle6Essential.q1_energyConsumption.renewable.other.current, principle6Essential.q1_energyConsumption.nonRenewable.electricity.current, principle6Essential.q1_energyConsumption.nonRenewable.fuel.current, principle6Essential.q1_energyConsumption.nonRenewable.other.current);
    const totalEnergyPrevious = calculateTotal(principle6Essential.q1_energyConsumption.renewable.electricity.previous, principle6Essential.q1_energyConsumption.renewable.fuel.previous, principle6Essential.q1_energyConsumption.renewable.other.previous, principle6Essential.q1_energyConsumption.nonRenewable.electricity.previous, principle6Essential.q1_energyConsumption.nonRenewable.fuel.previous, principle6Essential.q1_energyConsumption.nonRenewable.other.previous);

    const revenueCurrent = parseFloat(step1Data?.revenueCurrentFY);
    const revenuePrevious = parseFloat(step1Data?.revenuePreviousFY);
    const totalCurrentNum = parseFloat(totalEnergyCurrent);
    const totalPreviousNum = parseFloat(totalEnergyPrevious);

    const autoIntensityPerRupeeCurrent = !isNaN(totalCurrentNum) && !isNaN(revenueCurrent) && revenueCurrent > 0 ? (totalCurrentNum / revenueCurrent).toFixed(6) : "";
    const autoIntensityPerRupeePrevious = !isNaN(totalPreviousNum) && !isNaN(revenuePrevious) && revenuePrevious > 0 ? (totalPreviousNum / revenuePrevious).toFixed(6) : "";

    // PPP factors by year (World Bank data for India)
    const PPP_FACTORS = {
      2022: 22.36,
      2023: 22.89,
      2024: 23.12,
      2025: 23.5,
      2026: 24.0,
    };
    const currentYear = financialYears.currentFY?.split("-")[0];
    const previousYear = financialYears.previousFY?.split("-")[0];
    const pppFactorCurrent = PPP_FACTORS[currentYear] || 23.5;
    const pppFactorPrevious = PPP_FACTORS[previousYear] || 23.5;

    // PPP-adjusted intensity = Total Energy / (Revenue / PPP Factor)
    const autoIntensityPPPCurrent = !isNaN(totalCurrentNum) && !isNaN(revenueCurrent) && revenueCurrent > 0 && pppFactorCurrent > 0 ? (totalCurrentNum / (revenueCurrent / pppFactorCurrent)).toFixed(6) : "";
    const autoIntensityPPPPrevious = !isNaN(totalPreviousNum) && !isNaN(revenuePrevious) && revenuePrevious > 0 && pppFactorPrevious > 0 ? (totalPreviousNum / (revenuePrevious / pppFactorPrevious)).toFixed(6) : "";

    // Check if electricity values are from Scope 2 auto-fetch
    const isElectricityAutoFetched = scope2Data !== null;

    return (
      <div>
        {/* Question 1 (BRSR Core): Energy consumption */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "1",
              "Details of total energy consumption (in Joules or multiples) and energy intensity, in the following format:",
              true,
              "Power delivered through the power connection may include many types of power. The different components of the power received should be properly accounted (this segregation will be required to calculate Scope 2 emissions).\n\nPower delivered through the local power connection may include:\na. Wheeled renewable power procured from a captive renewable power plant, a third party, or power exchange.\nb. Wheeled non-renewable power procured from a captive power plant, a third party, or power exchange.\nc. Renewable power procured under 'green tariff' program of the state.\nd. Grid power.\nThese components should be accordingly reported under renewable or non-renewable category.\n\nTotal energy consumed:\nTotal energy consumption = nonrenewable fuel consumed + renewable fuel consumed + purchased electricity, heating, cooling, steam + self-generated electricity, heating, cooling, steam (If the entity generates electricity from a non-renewable or renewable fuel source and then consumes the generated electricity, the energy consumption shall be counted only once)\n\n% of energy consumed from renewable sources:\nEnergy consumed through renewable sources / total energy consumed\n\nEnergy intensity (PPP):\n1. Total energy consumed\n2. Total Revenue from Operations (from audited P&L)\n3. PPP (USD / INR)\n\nEnergy intensity (Product or Service):\n1.Consumption as above\n2. Company & Sector Specific (i.e., No. of Vehicles Produced, MT of Material Produced, Data in Mn TB, No. of Seats / Travel Class etc)",
            )}
            <div className="section-a-dynamic-table-container section-c-energy-q1-container">
              <table className="section-a-dynamic-table section-c-energy-q1-table">
                <colgroup>
                  <col style={{ width: "40%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "30%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">Parameter</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY || "____"}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY || "____"}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* From renewable sources */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      From renewable sources
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Total electricity consumption (A)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.renewable.electricity.current}
                        onChange={(e) => {
                          if (isElectricityAutoFetched) return;
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                renewable: {
                                  ...prev.q1_energyConsumption.renewable,
                                  electricity: { ...prev.q1_energyConsumption.renewable.electricity, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className={`section-a-input ${isElectricityAutoFetched ? "auto-fetched-input" : ""}`}
                        placeholder={isElectricityAutoFetched ? "Auto-fetched" : "Enter number"}
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        disabled={isElectricityAutoFetched}
                      />
                      {scope2Loading && <div className="auto-fetch-loading">Loading from Scope 2...</div>}
                      {isElectricityAutoFetched && !scope2Loading && <div className="auto-fetch-indicator">Auto-fetched from Scope 2</div>}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.renewable.electricity.previous}
                        onChange={(e) => {
                          if (isElectricityAutoFetched) return;
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                renewable: {
                                  ...prev.q1_energyConsumption.renewable,
                                  electricity: { ...prev.q1_energyConsumption.renewable.electricity, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className={`section-a-input ${isElectricityAutoFetched ? "auto-fetched-input" : ""}`}
                        placeholder={isElectricityAutoFetched ? "Auto-fetched" : "Enter number"}
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        disabled={isElectricityAutoFetched}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Total fuel consumption (B)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.renewable.fuel.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                renewable: {
                                  ...prev.q1_energyConsumption.renewable,
                                  fuel: { ...prev.q1_energyConsumption.renewable.fuel, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.renewable.fuel.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                renewable: {
                                  ...prev.q1_energyConsumption.renewable,
                                  fuel: { ...prev.q1_energyConsumption.renewable.fuel, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Energy consumption through other sources (C)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.renewable.other.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                renewable: {
                                  ...prev.q1_energyConsumption.renewable,
                                  other: { ...prev.q1_energyConsumption.renewable.other, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.renewable.other.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                renewable: {
                                  ...prev.q1_energyConsumption.renewable,
                                  other: { ...prev.q1_energyConsumption.renewable.other, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total energy consumed from renewable sources (A+B+C)
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Unit: In % terms)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {renewableCurrentTotal}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {renewablePreviousTotal}
                    </td>
                  </tr>
                  {/* From non-renewable sources */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      From non-renewable sources
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Total electricity consumption (D)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.nonRenewable.electricity.current}
                        onChange={(e) => {
                          if (isElectricityAutoFetched) return;
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                nonRenewable: {
                                  ...prev.q1_energyConsumption.nonRenewable,
                                  electricity: { ...prev.q1_energyConsumption.nonRenewable.electricity, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className={`section-a-input ${isElectricityAutoFetched ? "auto-fetched-input" : ""}`}
                        placeholder={isElectricityAutoFetched ? "Auto-fetched" : "Enter number"}
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        disabled={isElectricityAutoFetched}
                      />
                      {scope2Loading && <div className="auto-fetch-loading">Loading from Scope 2...</div>}
                      {isElectricityAutoFetched && !scope2Loading && <div className="auto-fetch-indicator">Auto-fetched from Scope 2</div>}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.nonRenewable.electricity.previous}
                        onChange={(e) => {
                          if (isElectricityAutoFetched) return;
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                nonRenewable: {
                                  ...prev.q1_energyConsumption.nonRenewable,
                                  electricity: { ...prev.q1_energyConsumption.nonRenewable.electricity, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className={`section-a-input ${isElectricityAutoFetched ? "auto-fetched-input" : ""}`}
                        placeholder={isElectricityAutoFetched ? "Auto-fetched" : "Enter number"}
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        disabled={isElectricityAutoFetched}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Total fuel consumption (E)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.nonRenewable.fuel.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                nonRenewable: {
                                  ...prev.q1_energyConsumption.nonRenewable,
                                  fuel: { ...prev.q1_energyConsumption.nonRenewable.fuel, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.nonRenewable.fuel.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                nonRenewable: {
                                  ...prev.q1_energyConsumption.nonRenewable,
                                  fuel: { ...prev.q1_energyConsumption.nonRenewable.fuel, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Energy consumption through other sources (F)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.nonRenewable.other.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                nonRenewable: {
                                  ...prev.q1_energyConsumption.nonRenewable,
                                  other: { ...prev.q1_energyConsumption.nonRenewable.other, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.nonRenewable.other.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                nonRenewable: {
                                  ...prev.q1_energyConsumption.nonRenewable,
                                  other: { ...prev.q1_energyConsumption.nonRenewable.other, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total energy consumed from non-renewable sources (D+E+F)
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {nonRenewableCurrentTotal}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {nonRenewablePreviousTotal}
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total energy consumed (A+B+C+D+E+F)
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Unit: In Joules or multiples)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {totalEnergyCurrent}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {totalEnergyPrevious}
                    </td>
                  </tr>
                  {/* Energy intensity */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Energy intensity per rupee of turnover
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Total energy consumed / Revenue from operations)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={autoIntensityPerRupeeCurrent || principle6Essential.q1_energyConsumption.intensity.perRupeeTurnover.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                intensity: {
                                  ...prev.q1_energyConsumption.intensity,
                                  perRupeeTurnover: { ...prev.q1_energyConsumption.intensity.perRupeeTurnover, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder={autoIntensityPerRupeeCurrent ? "Auto-calculated" : "Enter number"}
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        disabled={!!autoIntensityPerRupeeCurrent}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={autoIntensityPerRupeePrevious || principle6Essential.q1_energyConsumption.intensity.perRupeeTurnover.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                intensity: {
                                  ...prev.q1_energyConsumption.intensity,
                                  perRupeeTurnover: { ...prev.q1_energyConsumption.intensity.perRupeeTurnover, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder={autoIntensityPerRupeePrevious ? "Auto-calculated" : "Enter number"}
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        disabled={!!autoIntensityPerRupeePrevious}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Energy intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Unit: Joules or multiples / Rupee adjusted for PPP)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={autoIntensityPPPCurrent || principle6Essential.q1_energyConsumption.intensity.perRupeeTurnoverPPP.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                intensity: {
                                  ...prev.q1_energyConsumption.intensity,
                                  perRupeeTurnoverPPP: { ...prev.q1_energyConsumption.intensity.perRupeeTurnoverPPP, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder={autoIntensityPPPCurrent ? "Auto-calculated" : "Enter number"}
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        disabled={!!autoIntensityPPPCurrent}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={autoIntensityPPPPrevious || principle6Essential.q1_energyConsumption.intensity.perRupeeTurnoverPPP.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                intensity: {
                                  ...prev.q1_energyConsumption.intensity,
                                  perRupeeTurnoverPPP: { ...prev.q1_energyConsumption.intensity.perRupeeTurnoverPPP, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder={autoIntensityPPPPrevious ? "Auto-calculated" : "Enter number"}
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                        disabled={!!autoIntensityPPPPrevious}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Energy intensity in terms of physical output
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Unit: Joules or multiples / Product or Service)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.intensity.physicalOutput.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                intensity: {
                                  ...prev.q1_energyConsumption.intensity,
                                  physicalOutput: { ...prev.q1_energyConsumption.intensity.physicalOutput, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q1_energyConsumption.intensity.physicalOutput.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q1_energyConsumption: {
                                ...prev.q1_energyConsumption,
                                intensity: {
                                  ...prev.q1_energyConsumption.intensity,
                                  physicalOutput: { ...prev.q1_energyConsumption.intensity.physicalOutput, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
                <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontWeight: 600 }}>Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)</span>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <RadioButton
                      inputId="energy-assessment-yes"
                      value="Y"
                      checked={principle6Essential.q1_energyConsumption.externalAssessment.hasAssessment === "Y"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q1_energyConsumption: {
                            ...prev.q1_energyConsumption,
                            externalAssessment: { ...prev.q1_energyConsumption.externalAssessment, hasAssessment: "Y" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="energy-assessment-yes" style={{ cursor: "pointer", margin: 0 }}>
                      Y
                    </label>
                    <RadioButton
                      inputId="energy-assessment-no"
                      value="N"
                      checked={principle6Essential.q1_energyConsumption.externalAssessment.hasAssessment === "N"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q1_energyConsumption: {
                            ...prev.q1_energyConsumption,
                            externalAssessment: { ...prev.q1_energyConsumption.externalAssessment, hasAssessment: "N", agencyName: "" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="energy-assessment-no" style={{ cursor: "pointer", margin: 0 }}>
                      N
                    </label>
                  </div>
                </div>
                {principle6Essential.q1_energyConsumption.externalAssessment.hasAssessment === "Y" && (
                  <div>
                    <span style={{ fontWeight: 600 }}>If yes, name of the external agency:</span>
                    <InputText
                      value={principle6Essential.q1_energyConsumption.externalAssessment.agencyName}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q1_energyConsumption: {
                            ...prev.q1_energyConsumption,
                            externalAssessment: { ...prev.q1_energyConsumption.externalAssessment, agencyName: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      placeholder="Enter agency name"
                      style={{ width: "100%", marginTop: "8px" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Question 2: PAT Scheme */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Does the entity have any sites / facilities identified as designated consumers (DCs) under the Performance, Achieve and Trade (PAT) Scheme of the Government of India? (Y/N) If yes, disclose whether targets set under the PAT scheme have been achieved. In case targets have not been achieved, provide the remedial action taken, if any.", true)}
          <InputTextarea
            value={principle6Essential.q2_patScheme}
            onChange={(e) => {
              setPrinciple6Essential((prev) => ({ ...prev, q2_patScheme: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 3 (BRSR Core): Water details - Due to size, implementing key structure */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "3",
              "Provide details of the following disclosures related to water, in the following format:",
              true,
              "Reporting entities which have offices, outlets, branches, and other facilities where direct measurement of water is not available or practicable, should use guidelines established by the Central Ground Water Authority (CGWA)2 to estimate water consumption.\n\nAs per CGWA guideline, the estimated consumption is 45 litres per head per working day for offices. Thus, the quantification of water usage can be done by multiplying the number of employees and workers working within the office space by the stipulated 45 litres per head per working day.\n\nFor other countries, use consumption rates representative of the country or region to the extent practicable.\n\nOffices, outlets, branches, or other similar situations where directly measurement of water withdrawal, discharge, and consumption data, are available at a larger facility-level, then the sub-unit level water withdrawal, discharge, and consumption should be estimated from facility-level data as follows:\n\nSub-unit level data = (Facility level data including common area consumption) * (sub-unit area in sq. ft. (÷ total facility area in sq. ft)",
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">Parameter</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Water withdrawal by source (in kilolitres)
                    </td>
                  </tr>
                  {[
                    { key: "surfaceWater", label: "(i) Surface water" },
                    { key: "groundwater", label: "(ii) Groundwater" },
                    { key: "thirdParty", label: "(iii) Third party water" },
                    { key: "seawater", label: "(iv) Seawater / desalinated water" },
                    { key: "others", label: "(v) Others" },
                  ].map((row) => (
                    <tr key={row.key} className="section-a-dynamic-table-row">
                      <td className="section-a-dynamic-table-cell">{row.label}</td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={principle6Essential.q3_waterDetails.withdrawal[row.key].current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumbersOnly(value)) {
                              setPrinciple6Essential((prev) => ({
                                ...prev,
                                q3_waterDetails: {
                                  ...prev.q3_waterDetails,
                                  withdrawal: {
                                    ...prev.q3_waterDetails.withdrawal,
                                    [row.key]: { ...prev.q3_waterDetails.withdrawal[row.key], current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="Enter number"
                          type="text"
                          inputMode="numeric"
                          style={{ width: "100%" }}
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={principle6Essential.q3_waterDetails.withdrawal[row.key].previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumbersOnly(value)) {
                              setPrinciple6Essential((prev) => ({
                                ...prev,
                                q3_waterDetails: {
                                  ...prev.q3_waterDetails,
                                  withdrawal: {
                                    ...prev.q3_waterDetails.withdrawal,
                                    [row.key]: { ...prev.q3_waterDetails.withdrawal[row.key], previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="Enter number"
                          type="text"
                          inputMode="numeric"
                          style={{ width: "100%" }}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total volume of water withdrawal (in kilolitres) (i + ii + iii + iv + v)
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(principle6Essential.q3_waterDetails.withdrawal.surfaceWater.current, principle6Essential.q3_waterDetails.withdrawal.groundwater.current, principle6Essential.q3_waterDetails.withdrawal.thirdParty.current, principle6Essential.q3_waterDetails.withdrawal.seawater.current, principle6Essential.q3_waterDetails.withdrawal.others.current)}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(principle6Essential.q3_waterDetails.withdrawal.surfaceWater.previous, principle6Essential.q3_waterDetails.withdrawal.groundwater.previous, principle6Essential.q3_waterDetails.withdrawal.thirdParty.previous, principle6Essential.q3_waterDetails.withdrawal.seawater.previous, principle6Essential.q3_waterDetails.withdrawal.others.previous)}
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total volume of water consumption (in kilolitres)
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q3_waterDetails.consumption.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q3_waterDetails: {
                                ...prev.q3_waterDetails,
                                consumption: { ...prev.q3_waterDetails.consumption, current: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q3_waterDetails.consumption.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q3_waterDetails: {
                                ...prev.q3_waterDetails,
                                consumption: { ...prev.q3_waterDetails.consumption, previous: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Water intensity
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Water intensity per rupee of turnover
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Total water consumption / Revenue from operations)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q3_waterDetails.intensity.perRupeeTurnover.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q3_waterDetails: {
                                ...prev.q3_waterDetails,
                                intensity: {
                                  ...prev.q3_waterDetails.intensity,
                                  perRupeeTurnover: { ...prev.q3_waterDetails.intensity.perRupeeTurnover, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q3_waterDetails.intensity.perRupeeTurnover.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q3_waterDetails: {
                                ...prev.q3_waterDetails,
                                intensity: {
                                  ...prev.q3_waterDetails.intensity,
                                  perRupeeTurnover: { ...prev.q3_waterDetails.intensity.perRupeeTurnover, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Water intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Total water consumption / Revenue from operations adjusted for PPP)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q3_waterDetails.intensity.perRupeeTurnoverPPP.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q3_waterDetails: {
                                ...prev.q3_waterDetails,
                                intensity: {
                                  ...prev.q3_waterDetails.intensity,
                                  perRupeeTurnoverPPP: { ...prev.q3_waterDetails.intensity.perRupeeTurnoverPPP, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q3_waterDetails.intensity.perRupeeTurnoverPPP.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q3_waterDetails: {
                                ...prev.q3_waterDetails,
                                intensity: {
                                  ...prev.q3_waterDetails.intensity,
                                  perRupeeTurnoverPPP: { ...prev.q3_waterDetails.intensity.perRupeeTurnoverPPP, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Water intensity in terms of physical output
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>Water intensity (optional) – the relevant metric may be selected by the entity</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q3_waterDetails.intensity.physicalOutput.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q3_waterDetails: {
                                ...prev.q3_waterDetails,
                                intensity: {
                                  ...prev.q3_waterDetails.intensity,
                                  physicalOutput: { ...prev.q3_waterDetails.intensity.physicalOutput, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q3_waterDetails.intensity.physicalOutput.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q3_waterDetails: {
                                ...prev.q3_waterDetails,
                                intensity: {
                                  ...prev.q3_waterDetails.intensity,
                                  physicalOutput: { ...prev.q3_waterDetails.intensity.physicalOutput, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
                <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontWeight: 600 }}>Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)</span>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <RadioButton
                      inputId="water-assessment-yes"
                      value="Y"
                      checked={principle6Essential.q3_waterDetails.externalAssessment.hasAssessment === "Y"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q3_waterDetails: {
                            ...prev.q3_waterDetails,
                            externalAssessment: { ...prev.q3_waterDetails.externalAssessment, hasAssessment: "Y" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="water-assessment-yes" style={{ cursor: "pointer", margin: 0 }}>
                      Y
                    </label>
                    <RadioButton
                      inputId="water-assessment-no"
                      value="N"
                      checked={principle6Essential.q3_waterDetails.externalAssessment.hasAssessment === "N"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q3_waterDetails: {
                            ...prev.q3_waterDetails,
                            externalAssessment: { ...prev.q3_waterDetails.externalAssessment, hasAssessment: "N", agencyName: "" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="water-assessment-no" style={{ cursor: "pointer", margin: 0 }}>
                      N
                    </label>
                  </div>
                </div>
                {principle6Essential.q3_waterDetails.externalAssessment.hasAssessment === "Y" && (
                  <div>
                    <span style={{ fontWeight: 600 }}>If yes, name of the external agency:</span>
                    <InputText
                      value={principle6Essential.q3_waterDetails.externalAssessment.agencyName}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q3_waterDetails: {
                            ...prev.q3_waterDetails,
                            externalAssessment: { ...prev.q3_waterDetails.externalAssessment, agencyName: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      placeholder="Enter agency name"
                      style={{ width: "100%", marginTop: "8px" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Question 4 (BRSR Core): Water discharge */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader("4", "Provide the following details related to water discharged:", true, "Water Discharge by destination and levels of Treatment:\n1. Untreated Water\n2. Primary Treatment (Removal of material that floats or settle out i.e Filtration, Screening, Sedimentation etc.)\n3. Secondary Treatment (Removal of Dissolved organic Matter i.e. Oxidation, Digestion etc.)\n4. Tertiary Treatment (Disinfecting Water i.e. removal of pathogens, Phosphorous, Nitrogen etc.)")}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">Parameter</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Water discharge by destination and level of treatment (in kilolitres)
                    </td>
                  </tr>
                  {[
                    { key: "surfaceWater", label: "(i) To Surface water" },
                    { key: "groundwater", label: "(ii) To Groundwater" },
                    { key: "seawater", label: "(iii) To Seawater" },
                    { key: "thirdParties", label: "(iv) Sent to third-parties" },
                    { key: "others", label: "(v) Others" },
                  ].map((destination) => (
                    <React.Fragment key={destination.key}>
                      <tr className="section-a-dynamic-table-row">
                        <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                          {destination.label}
                        </td>
                        <td className="section-a-dynamic-table-cell"></td>
                        <td className="section-a-dynamic-table-cell"></td>
                      </tr>
                      <tr className="section-a-dynamic-table-row">
                        <td className="section-a-dynamic-table-cell" style={{ paddingLeft: "30px" }}>
                          - No treatment
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle6Essential.q4_waterDischarge[destination.key].noTreatment.current}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple6Essential((prev) => ({
                                  ...prev,
                                  q4_waterDischarge: {
                                    ...prev.q4_waterDischarge,
                                    [destination.key]: {
                                      ...prev.q4_waterDischarge[destination.key],
                                      noTreatment: { ...prev.q4_waterDischarge[destination.key].noTreatment, current: value },
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle6Essential.q4_waterDischarge[destination.key].noTreatment.previous}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple6Essential((prev) => ({
                                  ...prev,
                                  q4_waterDischarge: {
                                    ...prev.q4_waterDischarge,
                                    [destination.key]: {
                                      ...prev.q4_waterDischarge[destination.key],
                                      noTreatment: { ...prev.q4_waterDischarge[destination.key].noTreatment, previous: value },
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                      </tr>
                      <tr className="section-a-dynamic-table-row">
                        <td className="section-a-dynamic-table-cell" style={{ paddingLeft: "30px" }}>
                          - With treatment – please specify level of treatment
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle6Essential.q4_waterDischarge[destination.key].withTreatment.current}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple6Essential((prev) => ({
                                  ...prev,
                                  q4_waterDischarge: {
                                    ...prev.q4_waterDischarge,
                                    [destination.key]: {
                                      ...prev.q4_waterDischarge[destination.key],
                                      withTreatment: { ...prev.q4_waterDischarge[destination.key].withTreatment, current: value },
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                          <InputText
                            value={principle6Essential.q4_waterDischarge[destination.key].withTreatment.treatmentLevel}
                            onChange={(e) => {
                              setPrinciple6Essential((prev) => ({
                                ...prev,
                                q4_waterDischarge: {
                                  ...prev.q4_waterDischarge,
                                  [destination.key]: {
                                    ...prev.q4_waterDischarge[destination.key],
                                    withTreatment: { ...prev.q4_waterDischarge[destination.key].withTreatment, treatmentLevel: e.target.value },
                                  },
                                },
                              }));
                            }}
                            className="section-a-input"
                            placeholder="Specify treatment level"
                            style={{ width: "100%", marginTop: "5px" }}
                          />
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle6Essential.q4_waterDischarge[destination.key].withTreatment.previous}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple6Essential((prev) => ({
                                  ...prev,
                                  q4_waterDischarge: {
                                    ...prev.q4_waterDischarge,
                                    [destination.key]: {
                                      ...prev.q4_waterDischarge[destination.key],
                                      withTreatment: { ...prev.q4_waterDischarge[destination.key].withTreatment, previous: value },
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total water discharged (in kilolitres)
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(
                        principle6Essential.q4_waterDischarge.surfaceWater.noTreatment.current,
                        principle6Essential.q4_waterDischarge.surfaceWater.withTreatment.current,
                        principle6Essential.q4_waterDischarge.groundwater.noTreatment.current,
                        principle6Essential.q4_waterDischarge.groundwater.withTreatment.current,
                        principle6Essential.q4_waterDischarge.seawater.noTreatment.current,
                        principle6Essential.q4_waterDischarge.seawater.withTreatment.current,
                        principle6Essential.q4_waterDischarge.thirdParties.noTreatment.current,
                        principle6Essential.q4_waterDischarge.thirdParties.withTreatment.current,
                        principle6Essential.q4_waterDischarge.others.noTreatment.current,
                        principle6Essential.q4_waterDischarge.others.withTreatment.current,
                      )}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(
                        principle6Essential.q4_waterDischarge.surfaceWater.noTreatment.previous,
                        principle6Essential.q4_waterDischarge.surfaceWater.withTreatment.previous,
                        principle6Essential.q4_waterDischarge.groundwater.noTreatment.previous,
                        principle6Essential.q4_waterDischarge.groundwater.withTreatment.previous,
                        principle6Essential.q4_waterDischarge.seawater.noTreatment.previous,
                        principle6Essential.q4_waterDischarge.seawater.withTreatment.previous,
                        principle6Essential.q4_waterDischarge.thirdParties.noTreatment.previous,
                        principle6Essential.q4_waterDischarge.thirdParties.withTreatment.previous,
                        principle6Essential.q4_waterDischarge.others.noTreatment.previous,
                        principle6Essential.q4_waterDischarge.others.withTreatment.previous,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
                <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontWeight: 600 }}>Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)</span>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <RadioButton
                      inputId="water-discharge-assessment-yes"
                      value="Y"
                      checked={principle6Essential.q4_waterDischarge.externalAssessment.hasAssessment === "Y"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q4_waterDischarge: {
                            ...prev.q4_waterDischarge,
                            externalAssessment: { ...prev.q4_waterDischarge.externalAssessment, hasAssessment: "Y" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="water-discharge-assessment-yes" style={{ cursor: "pointer", margin: 0 }}>
                      Y
                    </label>
                    <RadioButton
                      inputId="water-discharge-assessment-no"
                      value="N"
                      checked={principle6Essential.q4_waterDischarge.externalAssessment.hasAssessment === "N"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q4_waterDischarge: {
                            ...prev.q4_waterDischarge,
                            externalAssessment: { ...prev.q4_waterDischarge.externalAssessment, hasAssessment: "N", agencyName: "" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="water-discharge-assessment-no" style={{ cursor: "pointer", margin: 0 }}>
                      N
                    </label>
                  </div>
                </div>
                {principle6Essential.q4_waterDischarge.externalAssessment.hasAssessment === "Y" && (
                  <div>
                    <span style={{ fontWeight: 600 }}>If yes, name of the external agency:</span>
                    <InputText
                      value={principle6Essential.q4_waterDischarge.externalAssessment.agencyName}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q4_waterDischarge: {
                            ...prev.q4_waterDischarge,
                            externalAssessment: { ...prev.q4_waterDischarge.externalAssessment, agencyName: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      placeholder="Enter agency name"
                      style={{ width: "100%", marginTop: "8px" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Question 5: Zero Liquid Discharge */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Has the entity implemented a mechanism for Zero Liquid Discharge? If yes, provide details of its coverage and implementation.", true)}
          <InputTextarea
            value={principle6Essential.q5_zeroLiquidDischarge}
            onChange={(e) => {
              setPrinciple6Essential((prev) => ({ ...prev, q5_zeroLiquidDischarge: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 6: Air emissions */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("6", "Please provide details of air emissions (other than GHG emissions) by the entity, in the following format:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Parameter</th>
                  <th className="section-a-dynamic-table-header">Please specify unit</th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                </tr>
              </thead>
              <tbody>
                {principle6Essential.q6_airEmissions.map((emission, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      {emission.parameter === "Others - please specify" ? (
                        <div>
                          <div style={{ marginBottom: "5px" }}>{emission.parameter}</div>
                          <InputText
                            value={emission.specify || ""}
                            onChange={(e) => {
                              const updatedEmissions = [...principle6Essential.q6_airEmissions];
                              updatedEmissions[index].specify = e.target.value;
                              setPrinciple6Essential((prev) => ({ ...prev, q6_airEmissions: updatedEmissions }));
                            }}
                            className="section-a-input"
                            placeholder="Specify other parameter"
                            style={{ width: "100%" }}
                          />
                        </div>
                      ) : (
                        emission.parameter
                      )}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={emission.unit}
                        onChange={(e) => {
                          const updatedEmissions = [...principle6Essential.q6_airEmissions];
                          updatedEmissions[index].unit = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q6_airEmissions: updatedEmissions }));
                        }}
                        className="section-a-input"
                        placeholder="Enter unit"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={emission.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            const updatedEmissions = [...principle6Essential.q6_airEmissions];
                            updatedEmissions[index].current = value;
                            setPrinciple6Essential((prev) => ({ ...prev, q6_airEmissions: updatedEmissions }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={emission.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            const updatedEmissions = [...principle6Essential.q6_airEmissions];
                            updatedEmissions[index].previous = value;
                            setPrinciple6Essential((prev) => ({ ...prev, q6_airEmissions: updatedEmissions }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
              <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "10px" }}>
                <span style={{ fontWeight: 600 }}>Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)</span>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <RadioButton
                    inputId="air-emissions-assessment-yes"
                    value="Y"
                    checked={principle6Essential.q6_airEmissionsExternalAssessment.hasAssessment === "Y"}
                    onChange={(e) => {
                      setPrinciple6Essential((prev) => ({
                        ...prev,
                        q6_airEmissionsExternalAssessment: { ...prev.q6_airEmissionsExternalAssessment, hasAssessment: "Y" },
                      }));
                    }}
                  />
                  <label htmlFor="air-emissions-assessment-yes" style={{ cursor: "pointer", margin: 0 }}>
                    Y
                  </label>
                  <RadioButton
                    inputId="air-emissions-assessment-no"
                    value="N"
                    checked={principle6Essential.q6_airEmissionsExternalAssessment.hasAssessment === "N"}
                    onChange={(e) => {
                      setPrinciple6Essential((prev) => ({
                        ...prev,
                        q6_airEmissionsExternalAssessment: { ...prev.q6_airEmissionsExternalAssessment, hasAssessment: "N", agencyName: "" },
                      }));
                    }}
                  />
                  <label htmlFor="air-emissions-assessment-no" style={{ cursor: "pointer", margin: 0 }}>
                    N
                  </label>
                </div>
              </div>
              {principle6Essential.q6_airEmissionsExternalAssessment.hasAssessment === "Y" && (
                <div>
                  <span style={{ fontWeight: 600 }}>If yes, name of the external agency:</span>
                  <InputText
                    value={principle6Essential.q6_airEmissionsExternalAssessment.agencyName}
                    onChange={(e) => {
                      setPrinciple6Essential((prev) => ({
                        ...prev,
                        q6_airEmissionsExternalAssessment: { ...prev.q6_airEmissionsExternalAssessment, agencyName: e.target.value },
                      }));
                    }}
                    className="section-a-input"
                    placeholder="Enter agency name"
                    style={{ width: "100%", marginTop: "8px" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Question 7 (BRSR Core): GHG emissions Scope 1 & 2 */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "7",
              "Provide details of greenhouse gas emissions (Scope 1 and Scope 2 emissions) & its intensity, in the following format:",
              true,
              "Total Scope 1 Emissions:\n1. Absolute Fossil Fuel (Coal, Natural Gas, Diesel, FO etc.) Consumption (Mn MT / KT / MT / MM BTU etc.)\n2. To calculate Scope 1 and Scope 2 emissions (GHG in CO2e / Unit of Measure), reporting entities should refer to following recognized sources for emission factors for their latest available guidance:\n• NABL Accredited Lab\n• Intergovernmental Panel on Climate Change (IPCC)\n• International Energy Agency (IEA)\n• Department of Environment, Food & Rural Affairs (UK DEFRA)\n• US Environment Protection Agency (EPA)\n• Country specific emission factors\nIt is necessary that reporting entities disclose the source of emissions factor used.\n3. Quantity of Carbon Capture (Mn MT / KT / MT)\n4. GHG emissions in CO2 equivalent by process (Non-Fuel Source) (Mn MT / KT / MT / MM BTU)\n5. Fugitive emissions\n6. Total Scope 1 GHG Emissions: Point 2 x Point 1 - Point 3 + Point 4 + Point 5\n\nTotal Scope 2 emissions:\n1. Total Consumption of Purchased Energy (MW), Steam (MT), Refrigeration (MMBTU)\n2. GHG (CO2e) Emission Factor across all purchased energy sources - IPCC or actual from the supplier (audited certificates)\n3. Total Scope 2 GHG Emissions: Total Consumption x Emission Factor\nHowever, spend-based methodology should be used in a restricted manner and only initially when a data measurement is not in place. It is suggested that every entity must eventually start measuring quantitative data and spend-based methodology can be used in initial years of reporting. Where used, the reporting entity must specify the source of the spend-based consumption factor and explain its suitability for the purpose.\nCompanies should not go back to spend-based method after having and reporting measurable data.\n\nGHG Emission Intensity (Scope 1 +2) (PPP):\n1.Total Emission (Scope 1 & 2)\n2. Total Revenue from Operations - From Audited P&L Statement\n3. PPP (USD / INR)\n\nGHG Emission Intensity (Scope 1 +2) (Products & Services):\n1. Total Emission (Scope 1 & 2) as above\n2. Company & Sector Specific (i.e., No. of Vehicles Produced, MT of Material Produced, Data in Mn TB, No. of Seats / Travel Class, Room-nights etc.)",
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">Parameter</th>
                    <th className="section-a-dynamic-table-header">Unit</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Total Scope 1 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.scope1.unit}
                        onChange={(e) => {
                          setPrinciple6Essential((prev) => ({
                            ...prev,
                            q7_ghgEmissions: {
                              ...prev.q7_ghgEmissions,
                              scope1: { ...prev.q7_ghgEmissions.scope1, unit: e.target.value },
                            },
                          }));
                        }}
                        className="section-a-input"
                        placeholder="Enter unit"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.scope1.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                scope1: { ...prev.q7_ghgEmissions.scope1, current: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.scope1.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                scope1: { ...prev.q7_ghgEmissions.scope1, previous: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Total Scope 2 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.scope2.unit}
                        onChange={(e) => {
                          setPrinciple6Essential((prev) => ({
                            ...prev,
                            q7_ghgEmissions: {
                              ...prev.q7_ghgEmissions,
                              scope2: { ...prev.q7_ghgEmissions.scope2, unit: e.target.value },
                            },
                          }));
                        }}
                        className="section-a-input"
                        placeholder="Enter unit"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.scope2.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                scope2: { ...prev.q7_ghgEmissions.scope2, current: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.scope2.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                scope2: { ...prev.q7_ghgEmissions.scope2, previous: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" colSpan="4" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Total Scope 1 and Scope 2 emission intensity
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" colSpan="2">
                      Total Scope 1 and Scope 2 emission intensity per rupee of turnover
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Total Scope 1 and Scope 2 GHG emissions / Revenue from operations)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.intensity.perRupeeTurnover.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                intensity: {
                                  ...prev.q7_ghgEmissions.intensity,
                                  perRupeeTurnover: { ...prev.q7_ghgEmissions.intensity.perRupeeTurnover, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.intensity.perRupeeTurnover.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                intensity: {
                                  ...prev.q7_ghgEmissions.intensity,
                                  perRupeeTurnover: { ...prev.q7_ghgEmissions.intensity.perRupeeTurnover, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" colSpan="2">
                      Total Scope 1 and Scope 2 emission intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Total Scope 1 and Scope 2 GHG emissions / Revenue from operations adjusted for PPP)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.intensity.perRupeeTurnoverPPP.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                intensity: {
                                  ...prev.q7_ghgEmissions.intensity,
                                  perRupeeTurnoverPPP: { ...prev.q7_ghgEmissions.intensity.perRupeeTurnoverPPP, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.intensity.perRupeeTurnoverPPP.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                intensity: {
                                  ...prev.q7_ghgEmissions.intensity,
                                  perRupeeTurnoverPPP: { ...prev.q7_ghgEmissions.intensity.perRupeeTurnoverPPP, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" colSpan="2">
                      Total Scope 1 and Scope 2 emission intensity (optional) – the relevant metric may be selected by the entity
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.intensity.physicalOutput.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                intensity: {
                                  ...prev.q7_ghgEmissions.intensity,
                                  physicalOutput: { ...prev.q7_ghgEmissions.intensity.physicalOutput, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q7_ghgEmissions.intensity.physicalOutput.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q7_ghgEmissions: {
                                ...prev.q7_ghgEmissions,
                                intensity: {
                                  ...prev.q7_ghgEmissions.intensity,
                                  physicalOutput: { ...prev.q7_ghgEmissions.intensity.physicalOutput, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
                <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontWeight: 600 }}>Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)</span>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <RadioButton
                      inputId="ghg-assessment-yes"
                      value="Y"
                      checked={principle6Essential.q7_ghgEmissions.externalAssessment.hasAssessment === "Y"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q7_ghgEmissions: {
                            ...prev.q7_ghgEmissions,
                            externalAssessment: { ...prev.q7_ghgEmissions.externalAssessment, hasAssessment: "Y" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="ghg-assessment-yes" style={{ cursor: "pointer", margin: 0 }}>
                      Y
                    </label>
                    <RadioButton
                      inputId="ghg-assessment-no"
                      value="N"
                      checked={principle6Essential.q7_ghgEmissions.externalAssessment.hasAssessment === "N"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q7_ghgEmissions: {
                            ...prev.q7_ghgEmissions,
                            externalAssessment: { ...prev.q7_ghgEmissions.externalAssessment, hasAssessment: "N", agencyName: "" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="ghg-assessment-no" style={{ cursor: "pointer", margin: 0 }}>
                      N
                    </label>
                  </div>
                </div>
                {principle6Essential.q7_ghgEmissions.externalAssessment.hasAssessment === "Y" && (
                  <div>
                    <span style={{ fontWeight: 600 }}>If yes, name of the external agency:</span>
                    <InputText
                      value={principle6Essential.q7_ghgEmissions.externalAssessment.agencyName}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q7_ghgEmissions: {
                            ...prev.q7_ghgEmissions,
                            externalAssessment: { ...prev.q7_ghgEmissions.externalAssessment, agencyName: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      placeholder="Enter agency name"
                      style={{ width: "100%", marginTop: "8px" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Question 8: GHG reduction projects */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("8", "Does the entity have any project related to reducing Green House Gas emission? If Yes, then provide details.", true)}
          <InputTextarea
            value={principle6Essential.q8_ghgReductionProjects}
            onChange={(e) => {
              setPrinciple6Essential((prev) => ({ ...prev, q8_ghgReductionProjects: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 9 (BRSR Core): Waste management - Large complex table */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "9",
              "Provide details related to waste management by the entity, in the following format:",
              true,
              "Plastic Waste (A): Absolute weight of the packaging material (Bags, Bottles, Pallets etc.) discarded as defined under the plastic waste management rules 2016 and amendments thereof\n\nE Waste (B): Discarded Computers, televisions, cell phones, VCRs, stereos, DVD players, copiers, and fax machines etc. as listed under e-waste management rules 2016 and amendments thereof\n\nBio-medical waste (C): Solids and liquid waste including its container and any intermediate product, which is generated during the diagnosis, treatment or immunization of human beings or animals or research activities as listed under Bio-medical waste management rules 2016 and amendments thereof\n\nConstruction and demolition waste (D): Construction waste as per C&D waste management Rules 2016 and amendments thereof like concrete, plaster, metal rods / wires, wood, plastics etc.\n\nBattery waste (E): Discarded batteries i.e., Li-ion, Alkaline, Lead Acid etc used in vehicles, computers & laptops, mobiles other electronics, UPS, Power Back up etc. as per Battery Waste management Rules 2016 and amendments thereof\n\nRadioactive waste (F): Discarded material such as paper, plastic, clothes, equipment, machine parts etc having exposure to radiation across Nuclear Power Plants, Hospitals, Research Laboratories, Industrial Applications etc.)\n\nOther Hazardous waste. Please specify, if any. (G): As per hazardous waste management rules of CPCB\n\nOther Non-hazardous waste generated (H): Waste not identified as Hazardous as per CPCB\n\nWaste intensity (PPP):\n1. Total waste generated\n2. Total Revenue from Operations (from audited P&L)\n3. PPP (USD / INR)\n\nWaste intensity (Products & Services):\n1. Total waste generated\n2. Company & Sector Specific (i.e., No. of Vehicles Produced, MT of Material Produced, Data in Mn TB, No. of Seats / Travel Class etc)\n\nEach category of waste generated, total waste recovered through recycling, re-using or other recovery operations:\nAbsolute Quantity\nKg of Waste Recycled Recovered /Total Waste generated\nDisclosure may be provided if certificates from vendors have been relied upon for assurance of KPIs on waste management\n\nFor each category of waste generated, total waste disposed by nature of disposal method:\n1. Amount of material in MT disposed through Incineration\n2. Amount of Material to Landfill\n3. Any other method Kg of Waste Recycled Recovered /Total Waste generated",
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">Parameter</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Total Waste generated (in metric tonnes)
                    </td>
                  </tr>
                  {[
                    { key: "plastic", label: "Plastic waste (A)" },
                    { key: "ewaste", label: "E-waste (B)" },
                    { key: "biomedical", label: "Bio-medical waste (C)" },
                    { key: "construction", label: "Construction and demolition waste (D)" },
                    { key: "battery", label: "Battery waste (E)" },
                    { key: "radioactive", label: "Radioactive waste (F)" },
                  ].map((waste) => (
                    <tr key={waste.key} className="section-a-dynamic-table-row">
                      <td className="section-a-dynamic-table-cell">{waste.label}</td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={principle6Essential.q9_wasteManagement.totalWaste[waste.key].current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumbersOnly(value)) {
                              setPrinciple6Essential((prev) => ({
                                ...prev,
                                q9_wasteManagement: {
                                  ...prev.q9_wasteManagement,
                                  totalWaste: {
                                    ...prev.q9_wasteManagement.totalWaste,
                                    [waste.key]: { ...prev.q9_wasteManagement.totalWaste[waste.key], current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="Enter number"
                          type="text"
                          inputMode="numeric"
                          style={{ width: "100%" }}
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={principle6Essential.q9_wasteManagement.totalWaste[waste.key].previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumbersOnly(value)) {
                              setPrinciple6Essential((prev) => ({
                                ...prev,
                                q9_wasteManagement: {
                                  ...prev.q9_wasteManagement,
                                  totalWaste: {
                                    ...prev.q9_wasteManagement.totalWaste,
                                    [waste.key]: { ...prev.q9_wasteManagement.totalWaste[waste.key], previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="Enter number"
                          type="text"
                          inputMode="numeric"
                          style={{ width: "100%" }}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Other Hazardous waste. Please specify, if any. (G)
                      <InputText
                        value={principle6Essential.q9_wasteManagement.totalWaste.otherHazardous.specify || ""}
                        onChange={(e) => {
                          setPrinciple6Essential((prev) => ({
                            ...prev,
                            q9_wasteManagement: {
                              ...prev.q9_wasteManagement,
                              totalWaste: {
                                ...prev.q9_wasteManagement.totalWaste,
                                otherHazardous: { ...prev.q9_wasteManagement.totalWaste.otherHazardous, specify: e.target.value },
                              },
                            },
                          }));
                        }}
                        className="section-a-input"
                        placeholder="Specify other hazardous waste"
                        style={{ width: "100%", marginTop: "5px" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.totalWaste.otherHazardous.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                totalWaste: {
                                  ...prev.q9_wasteManagement.totalWaste,
                                  otherHazardous: { ...prev.q9_wasteManagement.totalWaste.otherHazardous, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.totalWaste.otherHazardous.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                totalWaste: {
                                  ...prev.q9_wasteManagement.totalWaste,
                                  otherHazardous: { ...prev.q9_wasteManagement.totalWaste.otherHazardous, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Other Non-hazardous waste generated (H). Please specify, if any.
                      <InputText
                        value={principle6Essential.q9_wasteManagement.totalWaste.otherNonHazardous.specify || ""}
                        onChange={(e) => {
                          setPrinciple6Essential((prev) => ({
                            ...prev,
                            q9_wasteManagement: {
                              ...prev.q9_wasteManagement,
                              totalWaste: {
                                ...prev.q9_wasteManagement.totalWaste,
                                otherNonHazardous: { ...prev.q9_wasteManagement.totalWaste.otherNonHazardous, specify: e.target.value },
                              },
                            },
                          }));
                        }}
                        className="section-a-input"
                        placeholder="Specify other non-hazardous waste"
                        style={{ width: "100%", marginTop: "5px" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.totalWaste.otherNonHazardous.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                totalWaste: {
                                  ...prev.q9_wasteManagement.totalWaste,
                                  otherNonHazardous: { ...prev.q9_wasteManagement.totalWaste.otherNonHazardous, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.totalWaste.otherNonHazardous.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                totalWaste: {
                                  ...prev.q9_wasteManagement.totalWaste,
                                  otherNonHazardous: { ...prev.q9_wasteManagement.totalWaste.otherNonHazardous, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total (A+B + C + D + E + F + G + H)
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(
                        principle6Essential.q9_wasteManagement.totalWaste.plastic.current,
                        principle6Essential.q9_wasteManagement.totalWaste.ewaste.current,
                        principle6Essential.q9_wasteManagement.totalWaste.biomedical.current,
                        principle6Essential.q9_wasteManagement.totalWaste.construction.current,
                        principle6Essential.q9_wasteManagement.totalWaste.battery.current,
                        principle6Essential.q9_wasteManagement.totalWaste.radioactive.current,
                        principle6Essential.q9_wasteManagement.totalWaste.otherHazardous.current,
                        principle6Essential.q9_wasteManagement.totalWaste.otherNonHazardous.current,
                      )}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(
                        principle6Essential.q9_wasteManagement.totalWaste.plastic.previous,
                        principle6Essential.q9_wasteManagement.totalWaste.ewaste.previous,
                        principle6Essential.q9_wasteManagement.totalWaste.biomedical.previous,
                        principle6Essential.q9_wasteManagement.totalWaste.construction.previous,
                        principle6Essential.q9_wasteManagement.totalWaste.battery.previous,
                        principle6Essential.q9_wasteManagement.totalWaste.radioactive.previous,
                        principle6Essential.q9_wasteManagement.totalWaste.otherHazardous.previous,
                        principle6Essential.q9_wasteManagement.totalWaste.otherNonHazardous.previous,
                      )}
                    </td>
                  </tr>
                  {/* Waste intensity section */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Waste intensity per rupee of turnover
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Total waste generated / Revenue from operations)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.intensity.perRupeeTurnover.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                intensity: {
                                  ...prev.q9_wasteManagement.intensity,
                                  perRupeeTurnover: { ...prev.q9_wasteManagement.intensity.perRupeeTurnover, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.intensity.perRupeeTurnover.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                intensity: {
                                  ...prev.q9_wasteManagement.intensity,
                                  perRupeeTurnover: { ...prev.q9_wasteManagement.intensity.perRupeeTurnover, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Waste intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>(Total waste generated / Revenue from operations adjusted for PPP)</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.intensity.perRupeeTurnoverPPP.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                intensity: {
                                  ...prev.q9_wasteManagement.intensity,
                                  perRupeeTurnoverPPP: { ...prev.q9_wasteManagement.intensity.perRupeeTurnoverPPP, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.intensity.perRupeeTurnoverPPP.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                intensity: {
                                  ...prev.q9_wasteManagement.intensity,
                                  perRupeeTurnoverPPP: { ...prev.q9_wasteManagement.intensity.perRupeeTurnoverPPP, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      Waste intensity in terms of physical output
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>Waste intensity (optional) – the relevant metric may be selected by the entity</div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.intensity.physicalOutput.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                intensity: {
                                  ...prev.q9_wasteManagement.intensity,
                                  physicalOutput: { ...prev.q9_wasteManagement.intensity.physicalOutput, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.intensity.physicalOutput.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                intensity: {
                                  ...prev.q9_wasteManagement.intensity,
                                  physicalOutput: { ...prev.q9_wasteManagement.intensity.physicalOutput, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  {/* Recovery section */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      For each category of waste generated, total waste recovered through recycling, re-using or other recovery operations (in metric tonnes)
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Recycled</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.recovery.recycled.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                recovery: {
                                  ...prev.q9_wasteManagement.recovery,
                                  recycled: { ...prev.q9_wasteManagement.recovery.recycled, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.recovery.recycled.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                recovery: {
                                  ...prev.q9_wasteManagement.recovery,
                                  recycled: { ...prev.q9_wasteManagement.recovery.recycled, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Re-used</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.recovery.reused.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                recovery: {
                                  ...prev.q9_wasteManagement.recovery,
                                  reused: { ...prev.q9_wasteManagement.recovery.reused, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.recovery.reused.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                recovery: {
                                  ...prev.q9_wasteManagement.recovery,
                                  reused: { ...prev.q9_wasteManagement.recovery.reused, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Other recovery operations</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.recovery.otherRecovery.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                recovery: {
                                  ...prev.q9_wasteManagement.recovery,
                                  otherRecovery: { ...prev.q9_wasteManagement.recovery.otherRecovery, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.recovery.otherRecovery.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                recovery: {
                                  ...prev.q9_wasteManagement.recovery,
                                  otherRecovery: { ...prev.q9_wasteManagement.recovery.otherRecovery, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(principle6Essential.q9_wasteManagement.recovery.recycled.current, principle6Essential.q9_wasteManagement.recovery.reused.current, principle6Essential.q9_wasteManagement.recovery.otherRecovery.current)}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(principle6Essential.q9_wasteManagement.recovery.recycled.previous, principle6Essential.q9_wasteManagement.recovery.reused.previous, principle6Essential.q9_wasteManagement.recovery.otherRecovery.previous)}
                    </td>
                  </tr>
                  {/* Disposal section */}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      For each category of waste generated, total waste disposed by nature of disposal method (in metric tonnes)
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Incineration</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.disposal.incineration.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                disposal: {
                                  ...prev.q9_wasteManagement.disposal,
                                  incineration: { ...prev.q9_wasteManagement.disposal.incineration, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.disposal.incineration.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                disposal: {
                                  ...prev.q9_wasteManagement.disposal,
                                  incineration: { ...prev.q9_wasteManagement.disposal.incineration, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Landfilling</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.disposal.landfilling.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                disposal: {
                                  ...prev.q9_wasteManagement.disposal,
                                  landfilling: { ...prev.q9_wasteManagement.disposal.landfilling, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.disposal.landfilling.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                disposal: {
                                  ...prev.q9_wasteManagement.disposal,
                                  landfilling: { ...prev.q9_wasteManagement.disposal.landfilling, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Other disposal operations</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.disposal.otherDisposal.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                disposal: {
                                  ...prev.q9_wasteManagement.disposal,
                                  otherDisposal: { ...prev.q9_wasteManagement.disposal.otherDisposal, current: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Essential.q9_wasteManagement.disposal.otherDisposal.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Essential((prev) => ({
                              ...prev,
                              q9_wasteManagement: {
                                ...prev.q9_wasteManagement,
                                disposal: {
                                  ...prev.q9_wasteManagement.disposal,
                                  otherDisposal: { ...prev.q9_wasteManagement.disposal.otherDisposal, previous: value },
                                },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(principle6Essential.q9_wasteManagement.disposal.incineration.current, principle6Essential.q9_wasteManagement.disposal.landfilling.current, principle6Essential.q9_wasteManagement.disposal.otherDisposal.current)}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(principle6Essential.q9_wasteManagement.disposal.incineration.previous, principle6Essential.q9_wasteManagement.disposal.landfilling.previous, principle6Essential.q9_wasteManagement.disposal.otherDisposal.previous)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
                <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ fontWeight: 600 }}>Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)</span>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <RadioButton
                      inputId="waste-assessment-yes"
                      value="Y"
                      checked={principle6Essential.q9_wasteManagement.externalAssessment.hasAssessment === "Y"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q9_wasteManagement: {
                            ...prev.q9_wasteManagement,
                            externalAssessment: { ...prev.q9_wasteManagement.externalAssessment, hasAssessment: "Y" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="waste-assessment-yes" style={{ cursor: "pointer", margin: 0 }}>
                      Y
                    </label>
                    <RadioButton
                      inputId="waste-assessment-no"
                      value="N"
                      checked={principle6Essential.q9_wasteManagement.externalAssessment.hasAssessment === "N"}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q9_wasteManagement: {
                            ...prev.q9_wasteManagement,
                            externalAssessment: { ...prev.q9_wasteManagement.externalAssessment, hasAssessment: "N", agencyName: "" },
                          },
                        }));
                      }}
                    />
                    <label htmlFor="waste-assessment-no" style={{ cursor: "pointer", margin: 0 }}>
                      N
                    </label>
                  </div>
                </div>
                {principle6Essential.q9_wasteManagement.externalAssessment.hasAssessment === "Y" && (
                  <div>
                    <span style={{ fontWeight: 600 }}>If yes, name of the external agency:</span>
                    <InputText
                      value={principle6Essential.q9_wasteManagement.externalAssessment.agencyName}
                      onChange={(e) => {
                        setPrinciple6Essential((prev) => ({
                          ...prev,
                          q9_wasteManagement: {
                            ...prev.q9_wasteManagement,
                            externalAssessment: { ...prev.q9_wasteManagement.externalAssessment, agencyName: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      placeholder="Enter agency name"
                      style={{ width: "100%", marginTop: "8px" }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Question 10: Waste management practices */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("10", "Briefly describe the waste management practices adopted in your establishments. Describe the strategy adopted by your company to reduce usage of hazardous and toxic chemicals in your products and processes and the practices adopted to manage such wastes.", true)}
          <InputTextarea
            value={principle6Essential.q10_wasteManagementPractices}
            onChange={(e) => {
              setPrinciple6Essential((prev) => ({ ...prev, q10_wasteManagementPractices: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 11: Ecologically sensitive areas */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("11", "If the entity has operations/offices in/around ecologically sensitive areas (such as national parks, wildlife sanctuaries, biosphere reserves, wetlands, biodiversity hotspots, forests, coastal regulation zones etc.) where environmental approvals / clearances are required, please specify details in the following format:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    S. No.
                  </th>
                  <th className="section-a-dynamic-table-header">Location of operations/offices</th>
                  <th className="section-a-dynamic-table-header">Type of operations</th>
                  <th className="section-a-dynamic-table-header">Whether the conditions of environmental approval / clearance are being complied with? (Y/N) If no, the reasons thereof and corrective action taken, if any.</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle6Essential.q11_ecologicallySensitive.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.location}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q11_ecologicallySensitive];
                          updated[index].location = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q11_ecologicallySensitive: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter location"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.typeOfOperations}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q11_ecologicallySensitive];
                          updated[index].typeOfOperations = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q11_ecologicallySensitive: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter type of operations"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.compliance}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q11_ecologicallySensitive];
                          updated[index].compliance = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q11_ecologicallySensitive: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter compliance details"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle6Essential.q11_ecologicallySensitive.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle6Essential.q11_ecologicallySensitive.filter((_, i) => i !== index);
                              setPrinciple6Essential((prev) => ({ ...prev, q11_ecologicallySensitive: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple6Essential((prev) => ({
                    ...prev,
                    q11_ecologicallySensitive: [...prev.q11_ecologicallySensitive, { location: "", typeOfOperations: "", compliance: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 12: Environmental impact assessments */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("12", "Details of environmental impact assessments of projects undertaken by the entity based on applicable laws, in the current financial year:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Name and brief details of project</th>
                  <th className="section-a-dynamic-table-header">EIA Notification No.</th>
                  <th className="section-a-dynamic-table-header">Date</th>
                  <th className="section-a-dynamic-table-header">Whether conducted by independent external agency (Yes / No)</th>
                  <th className="section-a-dynamic-table-header">Results communicated in public domain (Yes / No)</th>
                  <th className="section-a-dynamic-table-header">Relevant Web link</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle6Essential.q12_eia.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.projectName}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q12_eia];
                          updated[index].projectName = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q12_eia: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter project name and brief"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.eiaNotificationNo}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            const updated = [...principle6Essential.q12_eia];
                            updated[index].eiaNotificationNo = value;
                            setPrinciple6Essential((prev) => ({ ...prev, q12_eia: updated }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.date}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q12_eia];
                          updated[index].date = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q12_eia: updated }));
                        }}
                        className="section-a-input"
                        placeholder="DD/MM/YYYY"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.independentAgency}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q12_eia];
                          updated[index].independentAgency = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q12_eia: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Yes or No"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.publicDomain}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q12_eia];
                          updated[index].publicDomain = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q12_eia: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Yes or No"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.webLink}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q12_eia];
                          updated[index].webLink = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q12_eia: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter web link"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle6Essential.q12_eia.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle6Essential.q12_eia.filter((_, i) => i !== index);
                              setPrinciple6Essential((prev) => ({ ...prev, q12_eia: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple6Essential((prev) => ({
                    ...prev,
                    q12_eia: [...prev.q12_eia, { projectName: "", eiaNotificationNo: "", date: "", independentAgency: "", publicDomain: "", webLink: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 13: Environmental compliance */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("13", "Is the entity compliant with the applicable environmental law/ regulations/ guidelines in India; such as the Water (Prevention and Control of Pollution) Act, Air (Prevention and Control of Pollution) Act, Environment protection act and rules thereunder (Y/N). If not, provide details of all such non-compliances, in the following format:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    S. No.
                  </th>
                  <th className="section-a-dynamic-table-header">Specify the law / regulation / guidelines which was not complied with</th>
                  <th className="section-a-dynamic-table-header">Provide details of the non-compliance</th>
                  <th className="section-a-dynamic-table-header">Any fines / penalties / action taken by regulatory agencies such as pollution control boards or by courts</th>
                  <th className="section-a-dynamic-table-header">Corrective action taken, if any</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle6Essential.q13_environmentalCompliance.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.law}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q13_environmentalCompliance];
                          updated[index].law = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q13_environmentalCompliance: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter law/regulation"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.nonComplianceDetails}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q13_environmentalCompliance];
                          updated[index].nonComplianceDetails = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q13_environmentalCompliance: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter details"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.finesPenalties}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q13_environmentalCompliance];
                          updated[index].finesPenalties = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q13_environmentalCompliance: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter fines/penalties"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.correctiveAction}
                        onChange={(e) => {
                          const updated = [...principle6Essential.q13_environmentalCompliance];
                          updated[index].correctiveAction = e.target.value;
                          setPrinciple6Essential((prev) => ({ ...prev, q13_environmentalCompliance: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter corrective action"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle6Essential.q13_environmentalCompliance.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle6Essential.q13_environmentalCompliance.filter((_, i) => i !== index);
                              setPrinciple6Essential((prev) => ({ ...prev, q13_environmentalCompliance: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple6Essential((prev) => ({
                    ...prev,
                    q13_environmentalCompliance: [...prev.q13_environmentalCompliance, { law: "", nonComplianceDetails: "", finesPenalties: "", correctiveAction: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Principle 6 Leadership Questions
  const renderPrinciple6Leadership = () => {
    return (
      <div>
        {/* Question 1: Water stress areas */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Water withdrawal, consumption and discharge in areas of water stress (in kilolitres): For each facility / plant located in areas of water stress, provide the following information: (i) Name of the area (ii) Nature of operations (iii) Water withdrawal, consumption and discharge in the following format:", false)}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: "8px" }}>(i) Name of the area:</label>
            <InputText
              value={principle6Leadership.q1_waterStress.areaName}
              onChange={(e) => {
                setPrinciple6Leadership((prev) => ({
                  ...prev,
                  q1_waterStress: { ...prev.q1_waterStress, areaName: e.target.value },
                }));
              }}
              className="section-a-input"
              placeholder="Enter area name"
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: "8px" }}>(ii) Nature of operations:</label>
            <InputText
              value={principle6Leadership.q1_waterStress.natureOfOperations}
              onChange={(e) => {
                setPrinciple6Leadership((prev) => ({
                  ...prev,
                  q1_waterStress: { ...prev.q1_waterStress, natureOfOperations: e.target.value },
                }));
              }}
              className="section-a-input"
              placeholder="Enter nature of operations"
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ marginTop: "20px" }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: "12px" }}>(iii) Water withdrawal, consumption and discharge:</label>
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header">Parameter</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Water withdrawal by source (in kilolitres)
                    </td>
                  </tr>
                  {[
                    { key: "surfaceWater", label: "(i) Surface water" },
                    { key: "groundwater", label: "(ii) Groundwater" },
                    { key: "thirdParty", label: "(iii) Third party water" },
                    { key: "seawater", label: "(iv) Seawater / desalinated water" },
                    { key: "others", label: "(v) Others" },
                  ].map((row) => (
                    <tr key={row.key} className="section-a-dynamic-table-row">
                      <td className="section-a-dynamic-table-cell">{row.label}</td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={principle6Leadership.q1_waterStress.withdrawal[row.key].current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumbersOnly(value)) {
                              setPrinciple6Leadership((prev) => ({
                                ...prev,
                                q1_waterStress: {
                                  ...prev.q1_waterStress,
                                  withdrawal: {
                                    ...prev.q1_waterStress.withdrawal,
                                    [row.key]: { ...prev.q1_waterStress.withdrawal[row.key], current: value },
                                  },
                                },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="Enter number"
                          type="text"
                          inputMode="numeric"
                          style={{ width: "100%" }}
                        />
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={principle6Leadership.q1_waterStress.withdrawal[row.key].previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumbersOnly(value)) {
                              setPrinciple6Leadership((prev) => ({
                                ...prev,
                                q1_waterStress: {
                                  ...prev.q1_waterStress,
                                  withdrawal: {
                                    ...prev.q1_waterStress.withdrawal,
                                    [row.key]: { ...prev.q1_waterStress.withdrawal[row.key], previous: value },
                                  },
                                },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="Enter number"
                          type="text"
                          inputMode="numeric"
                          style={{ width: "100%" }}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total volume of water withdrawal (in kilolitres)
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(principle6Leadership.q1_waterStress.withdrawal.surfaceWater.current, principle6Leadership.q1_waterStress.withdrawal.groundwater.current, principle6Leadership.q1_waterStress.withdrawal.thirdParty.current, principle6Leadership.q1_waterStress.withdrawal.seawater.current, principle6Leadership.q1_waterStress.withdrawal.others.current)}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(principle6Leadership.q1_waterStress.withdrawal.surfaceWater.previous, principle6Leadership.q1_waterStress.withdrawal.groundwater.previous, principle6Leadership.q1_waterStress.withdrawal.thirdParty.previous, principle6Leadership.q1_waterStress.withdrawal.seawater.previous, principle6Leadership.q1_waterStress.withdrawal.others.previous)}
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total volume of water consumption (in kilolitres)
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Leadership.q1_waterStress.consumption.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Leadership((prev) => ({
                              ...prev,
                              q1_waterStress: {
                                ...prev.q1_waterStress,
                                consumption: { ...prev.q1_waterStress.consumption, current: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle6Leadership.q1_waterStress.consumption.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple6Leadership((prev) => ({
                              ...prev,
                              q1_waterStress: {
                                ...prev.q1_waterStress,
                                consumption: { ...prev.q1_waterStress.consumption, previous: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-header" colSpan="3" style={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}>
                      Water discharge by destination and level of treatment (in kilolitres)
                    </td>
                  </tr>
                  {[
                    { key: "surfaceWater", label: "(i) Into Surface water" },
                    { key: "groundwater", label: "(ii) Into Groundwater" },
                    { key: "seawater", label: "(iii) Into Seawater" },
                    { key: "thirdParties", label: "(iv) Sent to third-parties" },
                    { key: "others", label: "(v) Others" },
                  ].map((destination) => (
                    <React.Fragment key={destination.key}>
                      <tr className="section-a-dynamic-table-row">
                        <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                          {destination.label}
                        </td>
                        <td className="section-a-dynamic-table-cell"></td>
                        <td className="section-a-dynamic-table-cell"></td>
                      </tr>
                      <tr className="section-a-dynamic-table-row">
                        <td className="section-a-dynamic-table-cell" style={{ paddingLeft: "30px" }}>
                          - No treatment
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle6Leadership.q1_waterStress.discharge[destination.key].noTreatment.current}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple6Leadership((prev) => ({
                                  ...prev,
                                  q1_waterStress: {
                                    ...prev.q1_waterStress,
                                    discharge: {
                                      ...prev.q1_waterStress.discharge,
                                      [destination.key]: {
                                        ...prev.q1_waterStress.discharge[destination.key],
                                        noTreatment: { ...prev.q1_waterStress.discharge[destination.key].noTreatment, current: value },
                                      },
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle6Leadership.q1_waterStress.discharge[destination.key].noTreatment.previous}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple6Leadership((prev) => ({
                                  ...prev,
                                  q1_waterStress: {
                                    ...prev.q1_waterStress,
                                    discharge: {
                                      ...prev.q1_waterStress.discharge,
                                      [destination.key]: {
                                        ...prev.q1_waterStress.discharge[destination.key],
                                        noTreatment: { ...prev.q1_waterStress.discharge[destination.key].noTreatment, previous: value },
                                      },
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                      </tr>
                      <tr className="section-a-dynamic-table-row">
                        <td className="section-a-dynamic-table-cell" style={{ paddingLeft: "30px" }}>
                          - With treatment – please specify level of treatment
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle6Leadership.q1_waterStress.discharge[destination.key].withTreatment.current}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple6Leadership((prev) => ({
                                  ...prev,
                                  q1_waterStress: {
                                    ...prev.q1_waterStress,
                                    discharge: {
                                      ...prev.q1_waterStress.discharge,
                                      [destination.key]: {
                                        ...prev.q1_waterStress.discharge[destination.key],
                                        withTreatment: { ...prev.q1_waterStress.discharge[destination.key].withTreatment, current: value },
                                      },
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td className="section-a-dynamic-table-cell">
                          <InputText
                            value={principle6Leadership.q1_waterStress.discharge[destination.key].withTreatment.previous}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (validateNumbersOnly(value)) {
                                setPrinciple6Leadership((prev) => ({
                                  ...prev,
                                  q1_waterStress: {
                                    ...prev.q1_waterStress,
                                    discharge: {
                                      ...prev.q1_waterStress.discharge,
                                      [destination.key]: {
                                        ...prev.q1_waterStress.discharge[destination.key],
                                        withTreatment: { ...prev.q1_waterStress.discharge[destination.key].withTreatment, previous: value },
                                      },
                                    },
                                  },
                                }));
                              }
                            }}
                            className="section-a-input"
                            placeholder="Enter number"
                            type="text"
                            inputMode="numeric"
                            style={{ width: "100%" }}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 600 }}>
                      Total water discharged (in kilolitres)
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(
                        principle6Leadership.q1_waterStress.discharge.surfaceWater.noTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.surfaceWater.withTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.groundwater.noTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.groundwater.withTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.seawater.noTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.seawater.withTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.thirdParties.noTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.thirdParties.withTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.others.noTreatment.current,
                        principle6Leadership.q1_waterStress.discharge.others.withTreatment.current,
                      )}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {calculateTotal(
                        principle6Leadership.q1_waterStress.discharge.surfaceWater.noTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.surfaceWater.withTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.groundwater.noTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.groundwater.withTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.seawater.noTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.seawater.withTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.thirdParties.noTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.thirdParties.withTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.others.noTreatment.previous,
                        principle6Leadership.q1_waterStress.discharge.others.withTreatment.previous,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Question 2: Scope 3 emissions */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Please provide details of total Scope 3 emissions & its intensity, in the following format:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Parameter</th>
                  <th className="section-a-dynamic-table-header">Unit</th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                  <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell">Total Scope 3 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)</td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle6Leadership.q2_scope3Emissions.totalScope3.unit}
                      onChange={(e) => {
                        setPrinciple6Leadership((prev) => ({
                          ...prev,
                          q2_scope3Emissions: {
                            ...prev.q2_scope3Emissions,
                            totalScope3: { ...prev.q2_scope3Emissions.totalScope3, unit: e.target.value },
                          },
                        }));
                      }}
                      className="section-a-input"
                      placeholder="Enter unit"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle6Leadership.q2_scope3Emissions.totalScope3.current}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple6Leadership((prev) => ({
                            ...prev,
                            q2_scope3Emissions: {
                              ...prev.q2_scope3Emissions,
                              totalScope3: { ...prev.q2_scope3Emissions.totalScope3, current: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle6Leadership.q2_scope3Emissions.totalScope3.previous}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple6Leadership((prev) => ({
                            ...prev,
                            q2_scope3Emissions: {
                              ...prev.q2_scope3Emissions,
                              totalScope3: { ...prev.q2_scope3Emissions.totalScope3, previous: value },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" colSpan="2">
                    Total Scope 3 emissions per rupee of turnover
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle6Leadership.q2_scope3Emissions.intensity.perRupeeTurnover.current}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple6Leadership((prev) => ({
                            ...prev,
                            q2_scope3Emissions: {
                              ...prev.q2_scope3Emissions,
                              intensity: {
                                ...prev.q2_scope3Emissions.intensity,
                                perRupeeTurnover: { ...prev.q2_scope3Emissions.intensity.perRupeeTurnover, current: value },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle6Leadership.q2_scope3Emissions.intensity.perRupeeTurnover.previous}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple6Leadership((prev) => ({
                            ...prev,
                            q2_scope3Emissions: {
                              ...prev.q2_scope3Emissions,
                              intensity: {
                                ...prev.q2_scope3Emissions.intensity,
                                perRupeeTurnover: { ...prev.q2_scope3Emissions.intensity.perRupeeTurnover, previous: value },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
                <tr className="section-a-dynamic-table-row">
                  <td className="section-a-dynamic-table-cell" colSpan="2">
                    Total Scope 3 emission intensity (optional) – the relevant metric may be selected by the entity
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle6Leadership.q2_scope3Emissions.intensity.optional.current}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple6Leadership((prev) => ({
                            ...prev,
                            q2_scope3Emissions: {
                              ...prev.q2_scope3Emissions,
                              intensity: {
                                ...prev.q2_scope3Emissions.intensity,
                                optional: { ...prev.q2_scope3Emissions.intensity.optional, current: value },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td className="section-a-dynamic-table-cell">
                    <InputText
                      value={principle6Leadership.q2_scope3Emissions.intensity.optional.previous}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (validateNumbersOnly(value)) {
                          setPrinciple6Leadership((prev) => ({
                            ...prev,
                            q2_scope3Emissions: {
                              ...prev.q2_scope3Emissions,
                              intensity: {
                                ...prev.q2_scope3Emissions.intensity,
                                optional: { ...prev.q2_scope3Emissions.intensity.optional, previous: value },
                              },
                            },
                          }));
                        }
                      }}
                      className="section-a-input"
                      placeholder="Enter number"
                      type="text"
                      inputMode="numeric"
                      style={{ width: "100%" }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#f9f9f9", borderRadius: "4px" }}>
              <div style={{ display: "flex", gap: "20px", alignItems: "center", marginBottom: "10px" }}>
                <span style={{ fontWeight: 600 }}>Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)</span>
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <RadioButton
                    inputId="scope3-assessment-yes"
                    value="Y"
                    checked={principle6Leadership.q2_scope3Emissions.externalAssessment.hasAssessment === "Y"}
                    onChange={(e) => {
                      setPrinciple6Leadership((prev) => ({
                        ...prev,
                        q2_scope3Emissions: {
                          ...prev.q2_scope3Emissions,
                          externalAssessment: { ...prev.q2_scope3Emissions.externalAssessment, hasAssessment: "Y" },
                        },
                      }));
                    }}
                  />
                  <label htmlFor="scope3-assessment-yes" style={{ cursor: "pointer", margin: 0 }}>
                    Y
                  </label>
                  <RadioButton
                    inputId="scope3-assessment-no"
                    value="N"
                    checked={principle6Leadership.q2_scope3Emissions.externalAssessment.hasAssessment === "N"}
                    onChange={(e) => {
                      setPrinciple6Leadership((prev) => ({
                        ...prev,
                        q2_scope3Emissions: {
                          ...prev.q2_scope3Emissions,
                          externalAssessment: { ...prev.q2_scope3Emissions.externalAssessment, hasAssessment: "N", agencyName: "" },
                        },
                      }));
                    }}
                  />
                  <label htmlFor="scope3-assessment-no" style={{ cursor: "pointer", margin: 0 }}>
                    N
                  </label>
                </div>
              </div>
              {principle6Leadership.q2_scope3Emissions.externalAssessment.hasAssessment === "Y" && (
                <div>
                  <span style={{ fontWeight: 600 }}>If yes, name of the external agency:</span>
                  <InputText
                    value={principle6Leadership.q2_scope3Emissions.externalAssessment.agencyName}
                    onChange={(e) => {
                      setPrinciple6Leadership((prev) => ({
                        ...prev,
                        q2_scope3Emissions: {
                          ...prev.q2_scope3Emissions,
                          externalAssessment: { ...prev.q2_scope3Emissions.externalAssessment, agencyName: e.target.value },
                        },
                      }));
                    }}
                    className="section-a-input"
                    placeholder="Enter agency name"
                    style={{ width: "100%", marginTop: "8px" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Question 3: Biodiversity impact */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "With respect to the ecologically sensitive areas reported at Question 11 of Essential Indicators above, provide details of significant direct & indirect impact of the entity on biodiversity in such areas along-with prevention and remediation activities.", false)}
          <InputTextarea
            value={principle6Leadership.q3_biodiversityImpact}
            onChange={(e) => {
              setPrinciple6Leadership((prev) => ({ ...prev, q3_biodiversityImpact: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 4: Resource efficiency initiatives */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "If the entity has undertaken any specific initiatives or used innovative technology or solutions to improve resource efficiency, or reduce impact due to emissions / effluent discharge / waste generated, please provide details of the same as well as outcome of such initiatives, as per the following format:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    Sr. No
                  </th>
                  <th className="section-a-dynamic-table-header">Initiative undertaken</th>
                  <th className="section-a-dynamic-table-header">Details of the initiative (Web-link, if any, may be provided along-with summary)</th>
                  <th className="section-a-dynamic-table-header">Outcome of the initiative</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle6Leadership.q4_resourceEfficiency.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.initiative}
                        onChange={(e) => {
                          const updated = [...principle6Leadership.q4_resourceEfficiency];
                          updated[index].initiative = e.target.value;
                          setPrinciple6Leadership((prev) => ({ ...prev, q4_resourceEfficiency: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter initiative"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.details}
                        onChange={(e) => {
                          const updated = [...principle6Leadership.q4_resourceEfficiency];
                          updated[index].details = e.target.value;
                          setPrinciple6Leadership((prev) => ({ ...prev, q4_resourceEfficiency: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter details"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.outcome}
                        onChange={(e) => {
                          const updated = [...principle6Leadership.q4_resourceEfficiency];
                          updated[index].outcome = e.target.value;
                          setPrinciple6Leadership((prev) => ({ ...prev, q4_resourceEfficiency: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter outcome"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle6Leadership.q4_resourceEfficiency.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle6Leadership.q4_resourceEfficiency.filter((_, i) => i !== index);
                              setPrinciple6Leadership((prev) => ({ ...prev, q4_resourceEfficiency: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple6Leadership((prev) => ({
                    ...prev,
                    q4_resourceEfficiency: [...prev.q4_resourceEfficiency, { initiative: "", details: "", outcome: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 5: Business continuity plan */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Does the entity have a business continuity and disaster management plan? Give details in 100 words/ web link.", false)}
          <InputTextarea
            value={principle6Leadership.q5_businessContinuity}
            onChange={(e) => {
              setPrinciple6Leadership((prev) => ({ ...prev, q5_businessContinuity: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details or web link"
          />
        </div>

        {/* Question 6: Value chain environmental impact */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("6", "Disclose any significant adverse impact to the environment, arising from the value chain of the entity. What mitigation or adaptation measures have been taken by the entity in this regard.", false)}
          <InputTextarea
            value={principle6Leadership.q6_valueChainImpact}
            onChange={(e) => {
              setPrinciple6Leadership((prev) => ({ ...prev, q6_valueChainImpact: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 7: Value chain partner assessment */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("7", "Percentage of value chain partners (by value of business done with such partners) that were assessed for environmental impacts.", false)}
          <InputTextarea
            value={principle6Leadership.q7_valueChainAssessment}
            onChange={(e) => {
              setPrinciple6Leadership((prev) => ({ ...prev, q7_valueChainAssessment: e.target.value }));
            }}
            className="section-a-input"
            rows={3}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter percentage and details"
          />
        </div>
      </div>
    );
  };

  // Render Principle 7 Essential Questions
  const renderPrinciple7Essential = () => {
    return (
      <div>
        {/* Question 1a: Number of affiliations */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1a", "Number of affiliations with trade and industry chambers/ associations.", true)}
          <InputText
            value={principle7Essential.q1a_numberOfAffiliations}
            onChange={(e) => {
              const value = e.target.value;
              if (validateIntegersOnly(value)) {
                setPrinciple7Essential((prev) => ({ ...prev, q1a_numberOfAffiliations: value }));
              }
            }}
            className="section-a-input"
            placeholder="Enter number"
            type="text"
            inputMode="numeric"
            style={{ width: "100%", maxWidth: "400px" }}
          />
        </div>

        {/* Question 1b: Top 10 trade and industry chambers/associations */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1b", "List the top 10 trade and industry chambers/ associations (determined based on the total members of such body) the entity is a member of/ affiliated to.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    S. No.
                  </th>
                  <th className="section-a-dynamic-table-header">Name of the trade and industry chambers/associations</th>
                  <th className="section-a-dynamic-table-header">Reach of trade and industry chambers/ associations (State/National)</th>
                </tr>
              </thead>
              <tbody>
                {principle7Essential.q1b_tradeChambers.map((chamber, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={chamber.name}
                        onChange={(e) => {
                          const updated = [...principle7Essential.q1b_tradeChambers];
                          updated[index].name = e.target.value;
                          setPrinciple7Essential((prev) => ({ ...prev, q1b_tradeChambers: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter name"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", gap: "20px", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <RadioButton
                            inputId={`state-${index}`}
                            value="State"
                            checked={chamber.reach === "State"}
                            onChange={(e) => {
                              const updated = [...principle7Essential.q1b_tradeChambers];
                              updated[index].reach = e.value;
                              setPrinciple7Essential((prev) => ({ ...prev, q1b_tradeChambers: updated }));
                            }}
                          />
                          <label htmlFor={`state-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            State
                          </label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <RadioButton
                            inputId={`national-${index}`}
                            value="National"
                            checked={chamber.reach === "National"}
                            onChange={(e) => {
                              const updated = [...principle7Essential.q1b_tradeChambers];
                              updated[index].reach = e.value;
                              setPrinciple7Essential((prev) => ({ ...prev, q1b_tradeChambers: updated }));
                            }}
                          />
                          <label htmlFor={`national-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            National
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 2: Corrective action on anti-competitive conduct */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Provide details of corrective action taken or underway on any issues related to anti competitive conduct by the entity, based on adverse orders from regulatory authorities.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Name of authority</th>
                  <th className="section-a-dynamic-table-header">Brief of the case</th>
                  <th className="section-a-dynamic-table-header">Corrective action taken</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle7Essential.q2_correctiveAction.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.authority}
                        onChange={(e) => {
                          const updated = [...principle7Essential.q2_correctiveAction];
                          updated[index].authority = e.target.value;
                          setPrinciple7Essential((prev) => ({ ...prev, q2_correctiveAction: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter authority name"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.brief}
                        onChange={(e) => {
                          const updated = [...principle7Essential.q2_correctiveAction];
                          updated[index].brief = e.target.value;
                          setPrinciple7Essential((prev) => ({ ...prev, q2_correctiveAction: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter brief of the case"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.correctiveAction}
                        onChange={(e) => {
                          const updated = [...principle7Essential.q2_correctiveAction];
                          updated[index].correctiveAction = e.target.value;
                          setPrinciple7Essential((prev) => ({ ...prev, q2_correctiveAction: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter corrective action taken"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle7Essential.q2_correctiveAction.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle7Essential.q2_correctiveAction.filter((_, i) => i !== index);
                              setPrinciple7Essential((prev) => ({ ...prev, q2_correctiveAction: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple7Essential((prev) => ({
                    ...prev,
                    q2_correctiveAction: [...prev.q2_correctiveAction, { authority: "", brief: "", correctiveAction: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Principle 7 Leadership Questions
  const renderPrinciple7Leadership = () => {
    const frequencyOptions = [
      { label: "Annually", value: "Annually" },
      { label: "Half yearly", value: "Half yearly" },
      { label: "Quarterly", value: "Quarterly" },
      { label: "Others - please specify", value: "Others" },
    ];

    return (
      <div>
        {/* Question 1: Public policy positions advocated */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Details of public policy positions advocated by the entity:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    S. No.
                  </th>
                  <th className="section-a-dynamic-table-header">Public policy advocated</th>
                  <th className="section-a-dynamic-table-header">Method resorted for such advocacy</th>
                  <th className="section-a-dynamic-table-header">Whether information available in public domain? (Yes/No)</th>
                  <th className="section-a-dynamic-table-header">Frequency of Review by Board (Annually/ Half yearly/ Quarterly / Others – please specify)</th>
                  <th className="section-a-dynamic-table-header">Web Link, if available</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle7Leadership.q1_publicPolicy.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.policy}
                        onChange={(e) => {
                          const updated = [...principle7Leadership.q1_publicPolicy];
                          updated[index].policy = e.target.value;
                          setPrinciple7Leadership((prev) => ({ ...prev, q1_publicPolicy: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter public policy"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.method}
                        onChange={(e) => {
                          const updated = [...principle7Leadership.q1_publicPolicy];
                          updated[index].method = e.target.value;
                          setPrinciple7Leadership((prev) => ({ ...prev, q1_publicPolicy: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter method"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <div style={{ display: "flex", gap: "20px", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <RadioButton
                            inputId={`public-yes-${index}`}
                            value="Yes"
                            checked={row.publicDomain === "Yes"}
                            onChange={(e) => {
                              const updated = [...principle7Leadership.q1_publicPolicy];
                              updated[index].publicDomain = e.value;
                              setPrinciple7Leadership((prev) => ({ ...prev, q1_publicPolicy: updated }));
                            }}
                          />
                          <label htmlFor={`public-yes-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            Yes
                          </label>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <RadioButton
                            inputId={`public-no-${index}`}
                            value="No"
                            checked={row.publicDomain === "No"}
                            onChange={(e) => {
                              const updated = [...principle7Leadership.q1_publicPolicy];
                              updated[index].publicDomain = e.value;
                              setPrinciple7Leadership((prev) => ({ ...prev, q1_publicPolicy: updated }));
                            }}
                          />
                          <label htmlFor={`public-no-${index}`} style={{ cursor: "pointer", margin: 0 }}>
                            No
                          </label>
                        </div>
                      </div>
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={row.frequency}
                        options={frequencyOptions}
                        onChange={(e) => {
                          const updated = [...principle7Leadership.q1_publicPolicy];
                          updated[index].frequency = e.value;
                          setPrinciple7Leadership((prev) => ({ ...prev, q1_publicPolicy: updated }));
                        }}
                        placeholder="Select frequency"
                        className="section-a-dropdown"
                        style={{ width: "100%" }}
                      />
                      {row.frequency === "Others" && (
                        <InputText
                          value={row.frequencyOther || ""}
                          onChange={(e) => {
                            const updated = [...principle7Leadership.q1_publicPolicy];
                            updated[index].frequencyOther = e.target.value;
                            setPrinciple7Leadership((prev) => ({ ...prev, q1_publicPolicy: updated }));
                          }}
                          className="section-a-input"
                          placeholder="Please specify"
                          style={{ width: "100%", marginTop: "5px" }}
                        />
                      )}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.webLink}
                        onChange={(e) => {
                          const updated = [...principle7Leadership.q1_publicPolicy];
                          updated[index].webLink = e.target.value;
                          setPrinciple7Leadership((prev) => ({ ...prev, q1_publicPolicy: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter web link (optional)"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle7Leadership.q1_publicPolicy.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle7Leadership.q1_publicPolicy.filter((_, i) => i !== index);
                              setPrinciple7Leadership((prev) => ({ ...prev, q1_publicPolicy: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple7Leadership((prev) => ({
                    ...prev,
                    q1_publicPolicy: [...prev.q1_publicPolicy, { policy: "", method: "", publicDomain: "", frequency: "", frequencyOther: "", webLink: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Principle 8 Essential Questions
  const renderPrinciple8Essential = () => {
    return (
      <div>
        {/* Question 1: Social Impact Assessments */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Details of Social Impact Assessments (SIA) of projects undertaken by the entity based on applicable laws, in the current financial year.", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Name and brief details of project</th>
                  <th className="section-a-dynamic-table-header">SIA Notification Number</th>
                  <th className="section-a-dynamic-table-header">Date of notification</th>
                  <th className="section-a-dynamic-table-header">Whether conducted by independent external agency (Yes / No)</th>
                  <th className="section-a-dynamic-table-header">Results communicated in public domain (Yes / No)</th>
                  <th className="section-a-dynamic-table-header">Relevant Web link</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle8Essential.q1_sia.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.projectName}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q1_sia];
                          updated[index].projectName = e.target.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q1_sia: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter project details"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.notificationNo}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q1_sia];
                          updated[index].notificationNo = e.target.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q1_sia: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter notification number"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.date}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q1_sia];
                          updated[index].date = e.target.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q1_sia: updated }));
                        }}
                        className="section-a-input"
                        placeholder="DD/MM/YYYY"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={row.independentAgency}
                        options={[
                          { label: "Yes", value: "Yes" },
                          { label: "No", value: "No" },
                        ]}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q1_sia];
                          updated[index].independentAgency = e.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q1_sia: updated }));
                        }}
                        placeholder="Select"
                        className="section-a-dropdown"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={row.publicDomain}
                        options={[
                          { label: "Yes", value: "Yes" },
                          { label: "No", value: "No" },
                        ]}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q1_sia];
                          updated[index].publicDomain = e.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q1_sia: updated }));
                        }}
                        placeholder="Select"
                        className="section-a-dropdown"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.webLink}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q1_sia];
                          updated[index].webLink = e.target.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q1_sia: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter web link"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle8Essential.q1_sia.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle8Essential.q1_sia.filter((_, i) => i !== index);
                              setPrinciple8Essential((prev) => ({ ...prev, q1_sia: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple8Essential((prev) => ({
                    ...prev,
                    q1_sia: [...prev.q1_sia, { projectName: "", notificationNo: "", date: "", independentAgency: "", publicDomain: "", webLink: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 2: Rehabilitation and Resettlement */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Provide information on project(s) for which ongoing Rehabilitation and Resettlement (R&R) is being undertaken by your entity, in the following format:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    S. No.
                  </th>
                  <th className="section-a-dynamic-table-header">Name of Project for which R&R is ongoing</th>
                  <th className="section-a-dynamic-table-header">State</th>
                  <th className="section-a-dynamic-table-header">District</th>
                  <th className="section-a-dynamic-table-header">No. of Project Affected Families (PAFs)</th>
                  <th className="section-a-dynamic-table-header">% of PAFs covered by R&R</th>
                  <th className="section-a-dynamic-table-header">Amounts paid to PAFs in the FY (In INR)</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle8Essential.q2_rrProjects.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.projectName}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q2_rrProjects];
                          updated[index].projectName = e.target.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q2_rrProjects: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter project name"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.state}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q2_rrProjects];
                          updated[index].state = e.target.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q2_rrProjects: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter state"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.district}
                        onChange={(e) => {
                          const updated = [...principle8Essential.q2_rrProjects];
                          updated[index].district = e.target.value;
                          setPrinciple8Essential((prev) => ({ ...prev, q2_rrProjects: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter district"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.pafNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            const updated = [...principle8Essential.q2_rrProjects];
                            updated[index].pafNumber = value;
                            setPrinciple8Essential((prev) => ({ ...prev, q2_rrProjects: updated }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.pafPercentage}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            if (value === "" || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                              const updated = [...principle8Essential.q2_rrProjects];
                              updated[index].pafPercentage = value;
                              setPrinciple8Essential((prev) => ({ ...prev, q2_rrProjects: updated }));
                            }
                          }
                        }}
                        className="section-a-input"
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {row.pafPercentage && (parseFloat(row.pafPercentage) < 0 || parseFloat(row.pafPercentage) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                      {row.pafPercentage && parseFloat(row.pafPercentage) >= 0 && parseFloat(row.pafPercentage) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(row.pafPercentage).toFixed(2)}%</div>}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.amountPaid}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            const updated = [...principle8Essential.q2_rrProjects];
                            updated[index].amountPaid = value;
                            setPrinciple8Essential((prev) => ({ ...prev, q2_rrProjects: updated }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter amount"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle8Essential.q2_rrProjects.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle8Essential.q2_rrProjects.filter((_, i) => i !== index);
                              setPrinciple8Essential((prev) => ({ ...prev, q2_rrProjects: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple8Essential((prev) => ({
                    ...prev,
                    q2_rrProjects: [...prev.q2_rrProjects, { projectName: "", state: "", district: "", pafNumber: "", pafPercentage: "", amountPaid: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 3: Grievance mechanisms */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Describe the mechanisms to receive and redress grievances of the community.", true)}
          <InputTextarea
            value={principle8Essential.q3_grievanceMechanisms}
            onChange={(e) => {
              setPrinciple8Essential((prev) => ({ ...prev, q3_grievanceMechanisms: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 4 (BRSR Core): Input material sourced */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "4",
              "Percentage of input material (inputs to total inputs by value) sourced from suppliers:",
              true,
              "Directly sourced from MSMEs/ small producers: Reporting Entities shall refer to the definitions provided in the Guidance Note for BRSR. 'Input material' and 'total purchases' shall include all types of procurement such as raw material, spares, services, capex procurement items etc. Reporting under this KPI shall only be applicable to Indian entities within the reporting boundary.\n\nDirectly from within India: 'Input material' and 'total purchases' shall include all types of procurement such as raw material, spares, services, capex procurement items etc. Reporting under this KPI shall only be applicable to Indian entities within the reporting boundary.",
            )}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header" style={{ minWidth: "300px" }}></th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Directly sourced from MSMEs/ small producers</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle8Essential.q4_inputMaterial.msme.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple8Essential((prev) => ({
                              ...prev,
                              q4_inputMaterial: {
                                ...prev.q4_inputMaterial,
                                msme: { ...prev.q4_inputMaterial.msme, current: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle8Essential.q4_inputMaterial.msme.current && parseFloat(principle8Essential.q4_inputMaterial.msme.current) >= 0 && parseFloat(principle8Essential.q4_inputMaterial.msme.current) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(principle8Essential.q4_inputMaterial.msme.current).toFixed(2)}%</div>}
                      {principle8Essential.q4_inputMaterial.msme.current && (parseFloat(principle8Essential.q4_inputMaterial.msme.current) < 0 || parseFloat(principle8Essential.q4_inputMaterial.msme.current) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle8Essential.q4_inputMaterial.msme.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple8Essential((prev) => ({
                              ...prev,
                              q4_inputMaterial: {
                                ...prev.q4_inputMaterial,
                                msme: { ...prev.q4_inputMaterial.msme, previous: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle8Essential.q4_inputMaterial.msme.previous && parseFloat(principle8Essential.q4_inputMaterial.msme.previous) >= 0 && parseFloat(principle8Essential.q4_inputMaterial.msme.previous) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(principle8Essential.q4_inputMaterial.msme.previous).toFixed(2)}%</div>}
                      {principle8Essential.q4_inputMaterial.msme.previous && (parseFloat(principle8Essential.q4_inputMaterial.msme.previous) < 0 || parseFloat(principle8Essential.q4_inputMaterial.msme.previous) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                  </tr>
                  <tr className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">Directly from within India</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle8Essential.q4_inputMaterial.withinIndia.current}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple8Essential((prev) => ({
                              ...prev,
                              q4_inputMaterial: {
                                ...prev.q4_inputMaterial,
                                withinIndia: { ...prev.q4_inputMaterial.withinIndia, current: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle8Essential.q4_inputMaterial.withinIndia.current && parseFloat(principle8Essential.q4_inputMaterial.withinIndia.current) >= 0 && parseFloat(principle8Essential.q4_inputMaterial.withinIndia.current) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(principle8Essential.q4_inputMaterial.withinIndia.current).toFixed(2)}%</div>}
                      {principle8Essential.q4_inputMaterial.withinIndia.current && (parseFloat(principle8Essential.q4_inputMaterial.withinIndia.current) < 0 || parseFloat(principle8Essential.q4_inputMaterial.withinIndia.current) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle8Essential.q4_inputMaterial.withinIndia.previous}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            setPrinciple8Essential((prev) => ({
                              ...prev,
                              q4_inputMaterial: {
                                ...prev.q4_inputMaterial,
                                withinIndia: { ...prev.q4_inputMaterial.withinIndia, previous: value },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle8Essential.q4_inputMaterial.withinIndia.previous && parseFloat(principle8Essential.q4_inputMaterial.withinIndia.previous) >= 0 && parseFloat(principle8Essential.q4_inputMaterial.withinIndia.previous) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(principle8Essential.q4_inputMaterial.withinIndia.previous).toFixed(2)}%</div>}
                      {principle8Essential.q4_inputMaterial.withinIndia.previous && (parseFloat(principle8Essential.q4_inputMaterial.withinIndia.previous) < 0 || parseFloat(principle8Essential.q4_inputMaterial.withinIndia.previous) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Question 5 (BRSR Core): Job creation in smaller towns */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader("5", "Job creation in smaller towns – Disclose wages paid to persons employed (including employees or workers employed on a permanent or non-permanent / on contract basis) in the following locations, as % of total wage cost (Place to be categorized as per RBI Classification System - rural / semi-urban / urban / metropolitan)", true)}
            <div className="section-a-dynamic-table-container">
              <table className="section-a-dynamic-table">
                <thead>
                  <tr>
                    <th className="section-a-dynamic-table-header" style={{ minWidth: "300px" }}>
                      Location
                    </th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.currentFY}</th>
                    <th className="section-a-dynamic-table-header">FY {financialYears.previousFY}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: "rural", label: "Rural" },
                    { key: "semiUrban", label: "Semi-urban" },
                    { key: "urban", label: "Urban" },
                    { key: "metropolitan", label: "Metropolitan" },
                  ].map((location) => (
                    <tr key={location.key} className="section-a-dynamic-table-row">
                      <td className="section-a-dynamic-table-cell">{location.label}</td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={principle8Essential.q5_jobCreation[location.key].current}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumbersOnly(value)) {
                              setPrinciple8Essential((prev) => ({
                                ...prev,
                                q5_jobCreation: {
                                  ...prev.q5_jobCreation,
                                  [location.key]: { ...prev.q5_jobCreation[location.key], current: value },
                                },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle8Essential.q5_jobCreation[location.key].current && parseFloat(principle8Essential.q5_jobCreation[location.key].current) >= 0 && parseFloat(principle8Essential.q5_jobCreation[location.key].current) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(principle8Essential.q5_jobCreation[location.key].current).toFixed(2)}%</div>}
                        {principle8Essential.q5_jobCreation[location.key].current && (parseFloat(principle8Essential.q5_jobCreation[location.key].current) < 0 || parseFloat(principle8Essential.q5_jobCreation[location.key].current) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                      </td>
                      <td className="section-a-dynamic-table-cell">
                        <InputText
                          value={principle8Essential.q5_jobCreation[location.key].previous}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (validateNumbersOnly(value)) {
                              setPrinciple8Essential((prev) => ({
                                ...prev,
                                q5_jobCreation: {
                                  ...prev.q5_jobCreation,
                                  [location.key]: { ...prev.q5_jobCreation[location.key], previous: value },
                                },
                              }));
                            }
                          }}
                          className="section-a-input"
                          placeholder="0-100"
                          type="text"
                          inputMode="decimal"
                          style={{ width: "100%" }}
                        />
                        {principle8Essential.q5_jobCreation[location.key].previous && parseFloat(principle8Essential.q5_jobCreation[location.key].previous) >= 0 && parseFloat(principle8Essential.q5_jobCreation[location.key].previous) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(principle8Essential.q5_jobCreation[location.key].previous).toFixed(2)}%</div>}
                        {principle8Essential.q5_jobCreation[location.key].previous && (parseFloat(principle8Essential.q5_jobCreation[location.key].previous) < 0 || parseFloat(principle8Essential.q5_jobCreation[location.key].previous) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Principle 8 Leadership Questions
  const renderPrinciple8Leadership = () => {
    return (
      <div>
        {/* Question 1: Actions to mitigate negative social impacts */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Provide details of actions taken to mitigate any negative social impacts identified in the Social Impact Assessments (Reference: Question 1 of Essential Indicators above):", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Details of negative social impact identified</th>
                  <th className="section-a-dynamic-table-header">Corrective action taken</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle8Leadership.q1_mitigateImpacts.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.impact}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q1_mitigateImpacts];
                          updated[index].impact = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q1_mitigateImpacts: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter details"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.correctiveAction}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q1_mitigateImpacts];
                          updated[index].correctiveAction = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q1_mitigateImpacts: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter corrective action"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle8Leadership.q1_mitigateImpacts.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle8Leadership.q1_mitigateImpacts.filter((_, i) => i !== index);
                              setPrinciple8Leadership((prev) => ({ ...prev, q1_mitigateImpacts: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple8Leadership((prev) => ({
                    ...prev,
                    q1_mitigateImpacts: [...prev.q1_mitigateImpacts, { impact: "", correctiveAction: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 2: CSR projects in aspirational districts */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Provide the following information on CSR projects undertaken by your entity in designated aspirational districts as identified by government bodies:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    S. No.
                  </th>
                  <th className="section-a-dynamic-table-header">State</th>
                  <th className="section-a-dynamic-table-header">Aspirational district</th>
                  <th className="section-a-dynamic-table-header">Amount Spent (In INR)</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle8Leadership.q2_csrAspirations.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.state}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q2_csrAspirations];
                          updated[index].state = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q2_csrAspirations: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter state"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.district}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q2_csrAspirations];
                          updated[index].district = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q2_csrAspirations: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter district"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.amountSpent}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            const updated = [...principle8Leadership.q2_csrAspirations];
                            updated[index].amountSpent = value;
                            setPrinciple8Leadership((prev) => ({ ...prev, q2_csrAspirations: updated }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter amount"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle8Leadership.q2_csrAspirations.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle8Leadership.q2_csrAspirations.filter((_, i) => i !== index);
                              setPrinciple8Leadership((prev) => ({ ...prev, q2_csrAspirations: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple8Leadership((prev) => ({
                    ...prev,
                    q2_csrAspirations: [...prev.q2_csrAspirations, { state: "", district: "", amountSpent: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Questions 3a, 3b, 3c: Preferential procurement */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3a", "Do you have a preferential procurement policy where you give preference to purchase from suppliers comprising marginalized /vulnerable groups? (Yes/No)", false)}
          <Dropdown
            value={principle8Leadership.q3a_preferentialPolicy}
            options={[
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ]}
            onChange={(e) => {
              setPrinciple8Leadership((prev) => ({ ...prev, q3a_preferentialPolicy: e.value }));
            }}
            placeholder="Select"
            className="section-a-dropdown"
            style={{ width: "100%", maxWidth: "400px" }}
          />
        </div>

        {principle8Leadership.q3a_preferentialPolicy === "Yes" && (
          <>
            <div style={{ marginBottom: "30px" }}>
              {renderQuestionHeader("3b", "From which marginalized /vulnerable groups do you procure?", false)}
              <InputTextarea
                value={principle8Leadership.q3b_marginalized}
                onChange={(e) => {
                  setPrinciple8Leadership((prev) => ({ ...prev, q3b_marginalized: e.target.value }));
                }}
                className="section-a-input"
                rows={4}
                style={{ width: "100%", resize: "vertical" }}
                placeholder="Enter details"
              />
            </div>

            <div style={{ marginBottom: "30px" }}>
              {renderQuestionHeader("3c", "What percentage of total procurement (by value) does it constitute?", false)}
              <InputTextarea
                value={principle8Leadership.q3c_procurementPercentage}
                onChange={(e) => {
                  setPrinciple8Leadership((prev) => ({ ...prev, q3c_procurementPercentage: e.target.value }));
                }}
                className="section-a-input"
                rows={3}
                style={{ width: "100%", resize: "vertical" }}
                placeholder="Enter percentage details"
              />
            </div>
          </>
        )}

        {/* Question 4: IP benefits from traditional knowledge */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Details of the benefits derived and shared from the intellectual properties owned or acquired by your entity (in the current financial year), based on traditional knowledge:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    S. No.
                  </th>
                  <th className="section-a-dynamic-table-header">Intellectual Property based on traditional knowledge</th>
                  <th className="section-a-dynamic-table-header">Owned / Acquired (Yes/No)</th>
                  <th className="section-a-dynamic-table-header">Benefit Shared (Yes/No)</th>
                  <th className="section-a-dynamic-table-header">Basis of calculating benefit share</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle8Leadership.q4_ipBenefits.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.ipName}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q4_ipBenefits];
                          updated[index].ipName = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q4_ipBenefits: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter IP details"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={row.owned}
                        options={[
                          { label: "Yes", value: "Yes" },
                          { label: "No", value: "No" },
                        ]}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q4_ipBenefits];
                          updated[index].owned = e.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q4_ipBenefits: updated }));
                        }}
                        placeholder="Select"
                        className="section-a-dropdown"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <Dropdown
                        value={row.benefitShared}
                        options={[
                          { label: "Yes", value: "Yes" },
                          { label: "No", value: "No" },
                        ]}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q4_ipBenefits];
                          updated[index].benefitShared = e.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q4_ipBenefits: updated }));
                        }}
                        placeholder="Select"
                        className="section-a-dropdown"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.basisCalculation}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q4_ipBenefits];
                          updated[index].basisCalculation = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q4_ipBenefits: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter basis"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle8Leadership.q4_ipBenefits.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle8Leadership.q4_ipBenefits.filter((_, i) => i !== index);
                              setPrinciple8Leadership((prev) => ({ ...prev, q4_ipBenefits: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple8Leadership((prev) => ({
                    ...prev,
                    q4_ipBenefits: [...prev.q4_ipBenefits, { ipName: "", owned: "", benefitShared: "", basisCalculation: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 5: IP disputes corrective actions */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Details of corrective actions taken or underway, based on any adverse order in intellectual property related disputes wherein usage of traditional knowledge is involved.", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Name of authority</th>
                  <th className="section-a-dynamic-table-header">Brief of the case</th>
                  <th className="section-a-dynamic-table-header">Corrective action taken</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle8Leadership.q5_ipDisputes.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.authority}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q5_ipDisputes];
                          updated[index].authority = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q5_ipDisputes: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter authority name"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.caseDetails}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q5_ipDisputes];
                          updated[index].caseDetails = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q5_ipDisputes: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter case details"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.correctiveAction}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q5_ipDisputes];
                          updated[index].correctiveAction = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q5_ipDisputes: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter corrective action"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle8Leadership.q5_ipDisputes.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle8Leadership.q5_ipDisputes.filter((_, i) => i !== index);
                              setPrinciple8Leadership((prev) => ({ ...prev, q5_ipDisputes: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple8Leadership((prev) => ({
                    ...prev,
                    q5_ipDisputes: [...prev.q5_ipDisputes, { authority: "", caseDetails: "", correctiveAction: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>

        {/* Question 6: CSR beneficiaries */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("6", "Details of beneficiaries of CSR Projects:", false)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ width: "80px" }}>
                    S. No.
                  </th>
                  <th className="section-a-dynamic-table-header">CSR Project</th>
                  <th className="section-a-dynamic-table-header">No of persons benefitted from CSR projects</th>
                  <th className="section-a-dynamic-table-header">% of beneficiaries from vulnerable and marginalized groups</th>
                  <th className="section-a-dynamic-table-header" style={{ width: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {principle8Leadership.q6_csrBeneficiaries.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center", fontWeight: 500 }}>
                      {index + 1}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.csrProject}
                        onChange={(e) => {
                          const updated = [...principle8Leadership.q6_csrBeneficiaries];
                          updated[index].csrProject = e.target.value;
                          setPrinciple8Leadership((prev) => ({ ...prev, q6_csrBeneficiaries: updated }));
                        }}
                        className="section-a-input"
                        placeholder="Enter project name"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.personsCount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            const updated = [...principle8Leadership.q6_csrBeneficiaries];
                            updated[index].personsCount = value;
                            setPrinciple8Leadership((prev) => ({ ...prev, q6_csrBeneficiaries: updated }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="Enter number"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.vulnerablePercentage}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            if (value === "" || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                              const updated = [...principle8Leadership.q6_csrBeneficiaries];
                              updated[index].vulnerablePercentage = value;
                              setPrinciple8Leadership((prev) => ({ ...prev, q6_csrBeneficiaries: updated }));
                            }
                          }
                        }}
                        className="section-a-input"
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {row.vulnerablePercentage && parseFloat(row.vulnerablePercentage) >= 0 && parseFloat(row.vulnerablePercentage) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(row.vulnerablePercentage).toFixed(2)}%</div>}
                      {row.vulnerablePercentage && (parseFloat(row.vulnerablePercentage) < 0 || parseFloat(row.vulnerablePercentage) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                    <td className="section-a-dynamic-table-cell" style={{ textAlign: "center" }}>
                      {principle8Leadership.q6_csrBeneficiaries.length > 1 && (
                        <Tooltip title="Delete row" arrow>
                          <img
                            src="/assets/img/reports/delete-repo.svg"
                            alt="Delete"
                            style={{ cursor: "pointer", width: "20px", height: "20px" }}
                            onClick={() => {
                              const updated = principle8Leadership.q6_csrBeneficiaries.filter((_, i) => i !== index);
                              setPrinciple8Leadership((prev) => ({ ...prev, q6_csrBeneficiaries: updated }));
                            }}
                          />
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="section-a-table-actions">
              <span
                className="section-a-add-row-text"
                onClick={() => {
                  setPrinciple8Leadership((prev) => ({
                    ...prev,
                    q6_csrBeneficiaries: [...prev.q6_csrBeneficiaries, { csrProject: "", personsCount: "", vulnerablePercentage: "" }],
                  }));
                }}
              >
                <span className="section-a-add-icon">+</span> Add Row
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Principle 9 Essential Questions
  const renderPrinciple9Essential = () => {
    const complaintCategories = [
      { key: "dataPrivacy", label: "Data privacy" },
      { key: "advertising", label: "Advertising" },
      { key: "cyberSecurity", label: "Cyber-security" },
      { key: "deliveryServices", label: "Delivery of essential services" },
      { key: "restrictiveTraces", label: "Restrictive Trade Practices" },
      { key: "unfairTrade", label: "Unfair Trade Practices" },
      { key: "other", label: "Other" },
    ];

    return (
      <div>
        {/* Question 1: Consumer complaints mechanisms */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Describe the mechanisms in place to receive and respond to consumer complaints and feedback.", true)}
          <InputTextarea
            value={principle9Essential.q1_consumerComplaints}
            onChange={(e) => {
              setPrinciple9Essential((prev) => ({ ...prev, q1_consumerComplaints: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 2: Turnover of products with information */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Turnover of products and/ services as a percentage of turnover from all products/service that carry information about:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">As a percentage to total turnover</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "environmentalSocialParameters", label: "Environmental and social parameters relevant to the product" },
                  { key: "safeResponsibleUsage", label: "Safe and responsible usage" },
                  { key: "recyclingSafeDisposal", label: "Recycling and/ or safe disposal" },
                ].map((item) => (
                  <tr key={item.key} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">{item.label}</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle9Essential.q2_productInfoTurnover[item.key]}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateNumbersOnly(value)) {
                            if (value === "" || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                              setPrinciple9Essential((prev) => ({
                                ...prev,
                                q2_productInfoTurnover: { ...prev.q2_productInfoTurnover, [item.key]: value },
                              }));
                            }
                          }
                        }}
                        className="section-a-input"
                        placeholder="0-100"
                        type="text"
                        inputMode="decimal"
                        style={{ width: "100%" }}
                      />
                      {principle9Essential.q2_productInfoTurnover[item.key] && parseFloat(principle9Essential.q2_productInfoTurnover[item.key]) >= 0 && parseFloat(principle9Essential.q2_productInfoTurnover[item.key]) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(principle9Essential.q2_productInfoTurnover[item.key]).toFixed(2)}%</div>}
                      {principle9Essential.q2_productInfoTurnover[item.key] && (parseFloat(principle9Essential.q2_productInfoTurnover[item.key]) < 0 || parseFloat(principle9Essential.q2_productInfoTurnover[item.key]) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 3: Consumer complaints by category */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Number of consumer complaints in respect of the following:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header" style={{ minWidth: "250px" }}></th>
                  <th className="section-a-dynamic-table-header" colSpan="3" style={{ textAlign: "center" }}>
                    FY {financialYears.currentFY}
                  </th>
                  <th className="section-a-dynamic-table-header" colSpan="3" style={{ textAlign: "center" }}>
                    FY {financialYears.previousFY}
                  </th>
                </tr>
                <tr>
                  <th className="section-a-dynamic-table-header"></th>
                  <th className="section-a-dynamic-table-header">Received during the year</th>
                  <th className="section-a-dynamic-table-header">Pending resolution at end of year</th>
                  <th className="section-a-dynamic-table-header">Remarks</th>
                  <th className="section-a-dynamic-table-header">Received during the year</th>
                  <th className="section-a-dynamic-table-header">Pending resolution at end of year</th>
                  <th className="section-a-dynamic-table-header">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {complaintCategories.map((category) => (
                  <tr key={category.key} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell">{category.label}</td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle9Essential.q3_complaintNumbers[category.key].current.received}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple9Essential((prev) => ({
                              ...prev,
                              q3_complaintNumbers: {
                                ...prev.q3_complaintNumbers,
                                [category.key]: { ...prev.q3_complaintNumbers[category.key], current: { ...prev.q3_complaintNumbers[category.key].current, received: value } },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle9Essential.q3_complaintNumbers[category.key].current.pending}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple9Essential((prev) => ({
                              ...prev,
                              q3_complaintNumbers: {
                                ...prev.q3_complaintNumbers,
                                [category.key]: { ...prev.q3_complaintNumbers[category.key], current: { ...prev.q3_complaintNumbers[category.key].current, pending: value } },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle9Essential.q3_complaintNumbers[category.key].current.remarks}
                        onChange={(e) => {
                          setPrinciple9Essential((prev) => ({
                            ...prev,
                            q3_complaintNumbers: {
                              ...prev.q3_complaintNumbers,
                              [category.key]: { ...prev.q3_complaintNumbers[category.key], current: { ...prev.q3_complaintNumbers[category.key].current, remarks: e.target.value } },
                            },
                          }));
                        }}
                        className="section-a-input"
                        placeholder="Enter remarks"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle9Essential.q3_complaintNumbers[category.key].previous.received}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple9Essential((prev) => ({
                              ...prev,
                              q3_complaintNumbers: {
                                ...prev.q3_complaintNumbers,
                                [category.key]: { ...prev.q3_complaintNumbers[category.key], previous: { ...prev.q3_complaintNumbers[category.key].previous, received: value } },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle9Essential.q3_complaintNumbers[category.key].previous.pending}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            setPrinciple9Essential((prev) => ({
                              ...prev,
                              q3_complaintNumbers: {
                                ...prev.q3_complaintNumbers,
                                [category.key]: { ...prev.q3_complaintNumbers[category.key], previous: { ...prev.q3_complaintNumbers[category.key].previous, pending: value } },
                              },
                            }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={principle9Essential.q3_complaintNumbers[category.key].previous.remarks}
                        onChange={(e) => {
                          setPrinciple9Essential((prev) => ({
                            ...prev,
                            q3_complaintNumbers: {
                              ...prev.q3_complaintNumbers,
                              [category.key]: { ...prev.q3_complaintNumbers[category.key], previous: { ...prev.q3_complaintNumbers[category.key].previous, remarks: e.target.value } },
                            },
                          }));
                        }}
                        className="section-a-input"
                        placeholder="Enter remarks"
                        style={{ width: "100%" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 4: Product recalls */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Details of instances of product recalls on account of safety issues:", true)}
          <div className="section-a-dynamic-table-container">
            <table className="section-a-dynamic-table">
              <thead>
                <tr>
                  <th className="section-a-dynamic-table-header">Type</th>
                  <th className="section-a-dynamic-table-header">Number</th>
                  <th className="section-a-dynamic-table-header">Reasons for recall</th>
                </tr>
              </thead>
              <tbody>
                {principle9Essential.q4_productRecalls.map((row, index) => (
                  <tr key={index} className="section-a-dynamic-table-row">
                    <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                      {row.type}
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputText
                        value={row.number}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateIntegersOnly(value)) {
                            const updated = [...principle9Essential.q4_productRecalls];
                            updated[index].number = value;
                            setPrinciple9Essential((prev) => ({ ...prev, q4_productRecalls: updated }));
                          }
                        }}
                        className="section-a-input"
                        placeholder="0"
                        type="text"
                        inputMode="numeric"
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td className="section-a-dynamic-table-cell">
                      <InputTextarea
                        value={row.reasons}
                        onChange={(e) => {
                          const updated = [...principle9Essential.q4_productRecalls];
                          updated[index].reasons = e.target.value;
                          setPrinciple9Essential((prev) => ({ ...prev, q4_productRecalls: updated }));
                        }}
                        className="section-a-input"
                        rows={2}
                        placeholder="Enter reasons"
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Question 5: Cyber security framework */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("5", "Does the entity have a framework/ policy on cyber security and risks related to data privacy? (Yes/No) If available, provide a web-link of the policy.", true)}
          <InputTextarea
            value={principle9Essential.q5_cyberSecurityFramework}
            onChange={(e) => {
              setPrinciple9Essential((prev) => ({ ...prev, q5_cyberSecurityFramework: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details and/or web link"
          />
        </div>

        {/* Question 6: Corrective actions */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("6", "Provide details of any corrective actions taken or underway on issues relating to advertising, and delivery of essential services; cyber security and data privacy of customers; re-occurrence of instances of product recalls; penalty / action taken by regulatory authorities on safety of products / services.", true)}
          <InputTextarea
            value={principle9Essential.q6_correctiveActions}
            onChange={(e) => {
              setPrinciple9Essential((prev) => ({ ...prev, q6_correctiveActions: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 7a: Data breaches instances */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("7a", "Provide the following information relating to data breaches: a. Number of instances of data breaches", true)}
          <InputText
            value={principle9Essential.q7a_dataBreachesInstances}
            onChange={(e) => {
              const value = e.target.value;
              if (validateIntegersOnly(value)) {
                setPrinciple9Essential((prev) => ({ ...prev, q7a_dataBreachesInstances: value }));
              }
            }}
            className="section-a-input"
            placeholder="Enter number"
            type="text"
            inputMode="numeric"
            style={{ width: "100%", maxWidth: "400px" }}
          />
        </div>

        {/* Question 7b (BRSR Core): Data breaches with PII */}
        {isBRSRCore && (
          <div style={{ marginBottom: "30px" }}>
            {renderQuestionHeader(
              "7b",
              "b. Percentage of data breaches involving personally identifiable information of customers",
              true,
              "Reporting entities shall report the number of cyber security incidents in accordance with their reporting during the year to CERT-In as per the direction dated 28 April 2022 issued by the Indian Computer Emergency Response Team, under the aegis of the Ministry of Electronics and Information Technology, under Section 70-B (6) of the Information Technology Act 2000.\n\nReporting companies shall provide percentage of the cyber security incidents as reported to CERT In that involved personally identifiable information of customers against the total cyber security incidents reported to CERT-In.\n\nFor cyber security incidents in jurisdictions outside India, reporting may be done basis regulatory requirement/reporting, if any, in the respective jurisdiction. In the absence of the same, the guidance in CERT-In should be used.",
            )}
            <InputText
              value={principle9Essential.q7b_dataBreachesPercentage}
              onChange={(e) => {
                const value = e.target.value;
                if (validateNumbersOnly(value)) {
                  if (value === "" || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                    setPrinciple9Essential((prev) => ({ ...prev, q7b_dataBreachesPercentage: value }));
                  }
                }
              }}
              className="section-a-input"
              placeholder="0-100"
              type="text"
              inputMode="decimal"
              style={{ width: "100%", maxWidth: "400px" }}
            />
            {principle9Essential.q7b_dataBreachesPercentage && parseFloat(principle9Essential.q7b_dataBreachesPercentage) >= 0 && parseFloat(principle9Essential.q7b_dataBreachesPercentage) <= 100 && <div style={{ color: "#407775", fontSize: "12px", marginTop: "2px", fontWeight: 500 }}>{parseFloat(principle9Essential.q7b_dataBreachesPercentage).toFixed(2)}%</div>}
            {principle9Essential.q7b_dataBreachesPercentage && (parseFloat(principle9Essential.q7b_dataBreachesPercentage) < 0 || parseFloat(principle9Essential.q7b_dataBreachesPercentage) > 100) && <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>Value needs to be between 0 and 100</div>}
          </div>
        )}

        {/* Question 7c: Impact of data breaches */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("7c", "c. Impact, if any, of the data breaches", true)}
          <InputTextarea
            value={principle9Essential.q7c_dataBreachesImpact}
            onChange={(e) => {
              setPrinciple9Essential((prev) => ({ ...prev, q7c_dataBreachesImpact: e.target.value }));
            }}
            className="section-a-input"
            rows={4}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>
      </div>
    );
  };

  // Render Principle 9 Leadership Questions
  const renderPrinciple9Leadership = () => {
    return (
      <div>
        {/* Question 1: Information channels/platforms */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("1", "Channels / platforms where information on products and services of the entity can be accessed (provide web link, if available).", false)}
          <InputTextarea
            value={principle9Leadership.q1_informationChannels}
            onChange={(e) => {
              setPrinciple9Leadership((prev) => ({ ...prev, q1_informationChannels: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter channels and/or web links"
          />
        </div>

        {/* Question 2: Consumer education steps */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("2", "Steps taken to inform and educate consumers about safe and responsible usage of products and/or services.", false)}
          <InputTextarea
            value={principle9Leadership.q2_consumerEducation}
            onChange={(e) => {
              setPrinciple9Leadership((prev) => ({ ...prev, q2_consumerEducation: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 3: Risk communication mechanisms */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("3", "Mechanisms in place to inform consumers of any risk of disruption/discontinuation of essential services.", false)}
          <InputTextarea
            value={principle9Leadership.q3_riskCommunication}
            onChange={(e) => {
              setPrinciple9Leadership((prev) => ({ ...prev, q3_riskCommunication: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>

        {/* Question 4: Product information and consumer surveys */}
        <div style={{ marginBottom: "30px" }}>
          {renderQuestionHeader("4", "Does the entity display product information on the product over and above what is mandated as per local laws? (Yes/No/Not Applicable) If yes, provide details in brief. Did your entity carry out any survey with regard to consumer satisfaction relating to the major products / services of the entity, significant locations of operation of the entity or the entity as a whole? (Yes/No)", false)}
          <InputTextarea
            value={principle9Leadership.q4_productInfoSurveys}
            onChange={(e) => {
              setPrinciple9Leadership((prev) => ({ ...prev, q4_productInfoSurveys: e.target.value }));
            }}
            className="section-a-input"
            rows={5}
            style={{ width: "100%", resize: "vertical" }}
            placeholder="Enter details"
          />
        </div>
      </div>
    );
  };

  const renderPrincipleContent = (principle) => {
    const isEssentialEnabled = essentialEnabled[principle.id] !== false; // Default to true
    const isLeadershipEnabled = leadershipEnabled[principle.id] !== false; // Default to true

    return (
      <div className="section-c-principle-content">
        {/* Checkboxes for Essential and Leadership */}
        <div style={{ display: "flex", gap: "30px", marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox
              inputId={`essential-checkbox-${principle.id}`}
              checked={isEssentialEnabled}
              onChange={(e) => {
                // Prevent deselection - only allow if trying to check (which shouldn't happen)
                // But prevent unchecking by not updating state if current value is true
                if (isEssentialEnabled && !e.checked) {
                  // Prevent unchecking - don't update state
                  return;
                }
                handleEssentialToggle(principle.id, e);
              }}
            />
            <label htmlFor={`essential-checkbox-${principle.id}`} style={{ cursor: "pointer", margin: 0, display: "flex", alignItems: "center", gap: "8px", fontSize: "16px", fontWeight: 500, color: "#407775" }}>
              <img src="/assets/img/reports/mandatory.svg" alt="Essential" className="section-c-indicator-icon" />
              Essential
            </label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Checkbox inputId={`leadership-checkbox-${principle.id}`} checked={isLeadershipEnabled} onChange={(e) => handleLeadershipToggle(principle.id, e)} />
            <label htmlFor={`leadership-checkbox-${principle.id}`} style={{ cursor: "pointer", margin: 0, display: "flex", alignItems: "center", gap: "8px", fontSize: "16px", fontWeight: 500, color: "#407775" }}>
              <img src="/assets/img/reports/leadership.svg" alt="Leadership" className="section-c-indicator-icon" />
              Leadership
            </label>
          </div>
        </div>

        {/* Show all questions based on checkbox state */}
        <div className="section-c-questions-content">
          {isEssentialEnabled && (
            <div className="section-c-essential-content">
              {principle.id === "principle1" ? (
                renderPrinciple1Essential()
              ) : principle.id === "principle2" ? (
                renderPrinciple2Essential()
              ) : principle.id === "principle3" ? (
                renderPrinciple3Essential()
              ) : principle.id === "principle4" ? (
                renderPrinciple4Essential()
              ) : principle.id === "principle5" ? (
                renderPrinciple5Essential()
              ) : principle.id === "principle6" ? (
                renderPrinciple6Essential()
              ) : principle.id === "principle7" ? (
                renderPrinciple7Essential()
              ) : principle.id === "principle8" ? (
                renderPrinciple8Essential()
              ) : principle.id === "principle9" ? (
                renderPrinciple9Essential()
              ) : (
                <p>Essential content for {principle.label}</p>
              )}
            </div>
          )}
          {isLeadershipEnabled && (
            <div className="section-c-leadership-content" style={{ marginTop: "0" }}>
              {principle.id === "principle1" ? (
                renderPrinciple1Leadership()
              ) : principle.id === "principle2" ? (
                renderPrinciple2Leadership()
              ) : principle.id === "principle3" ? (
                renderPrinciple3Leadership()
              ) : principle.id === "principle4" ? (
                renderPrinciple4Leadership()
              ) : principle.id === "principle5" ? (
                renderPrinciple5Leadership()
              ) : principle.id === "principle6" ? (
                renderPrinciple6Leadership()
              ) : principle.id === "principle7" ? (
                renderPrinciple7Leadership()
              ) : principle.id === "principle8" ? (
                renderPrinciple8Leadership()
              ) : principle.id === "principle9" ? (
                renderPrinciple9Leadership()
              ) : (
                <p>Leadership content for {principle.label}</p>
              )}
            </div>
          )}
          {!isEssentialEnabled && !isLeadershipEnabled && (
            <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
              <p>Please enable Essential or Leadership checkboxes to view questions.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <BrsrSectionCPopup isModalOpen={showSectionCPopup} onClose={() => setShowSectionCPopup(false)} />
      <Accordion className="brsr-principle-accordion">
        {principles.map((principle, index) => (
          <AccordionTab
            key={principle.id}
            header={
              <div>
                <span className="principle-label">
                  Principle {index + 1}: {principle.description}
                </span>
              </div>
            }
          >
            {renderPrincipleContent(principle)}
          </AccordionTab>
        ))}
      </Accordion>
    </div>
  );
};

export default SectionC;
